// Web Audio 合成エンジン (Phase 3 強化版)。
// 設計のキモ:
//   - パリィ音は本作の心臓。リミッタ手前まで攻める音圧で「脳汁」を出す。
//   - 13レイヤ構成: クリック / FMベル×2 / リング×2 / サブ×2 / ノイズ×2 / ベル余韻×2 / シマー / shimmer
//   - マスタ前にコンプレッサを噛ませて全レイヤを1つの塊に圧縮。
//   - リバーブはコンプ後にミックスして余韻だけ呼吸させる。
//
// 参考リサーチ:
//   - Ring Modulation excels at metallic sounds (Sweetwater)
//   - FM inharmonic ratios produce metallic textures (MusicRadar)
//   - Transient + Body + Tail layering, sub-bass + noise crash (David Dumais Audio)

import { EXEC_UNIQUE_SOUND_ROLES, execUniqueSfxId, execUniqueSoundFiles } from './exec_unique_sound'
import { ADV_SFX_IDS, advSoundFiles, advSoundSamples, type AdvSoundTake } from './adv_sound'
import { KNIGHT_SWEEP_CANDIDATES, KNIGHT_SWEEP_GAIN_DB, KNIGHT_SWEEP_PV, KNIGHT_SWEEP_PV_MATCH_DB, type KnightSweepRole } from './knight_sweep_sound'
import { KNIGHT_TELL_SFX_IDS } from './knight_telegraph_sound'
import { HOUND_VOICE_SFX_IDS, houndVoiceFiles } from './hound_voice_sound'
import { EXEC_SOUND_SFX_IDS, execSoundFiles, type ExecSoundTake } from './exec_sound'
import { FLOOR_TELL_SFX_IDS, floorTellFiles } from './floor_tell_sound'
import { BOSS_SFX_V1_IDS, bossSfxShippedFiles, bossSfxV1Files } from './boss_sfx_v1'
import { BLADE_SFX_V2_IDS, bladeSfxV2Files } from './blade_sfx_v2'
import { FINISHER_SFX_IDS, finisherSfxFiles } from './finisher_sound'
import { AWOKEN_VOICE_SFX_IDS, awokenVoiceFiles, type AwokenVoiceTake } from './awoken_voice_sound'
import type { SfxId } from './types'
import { BOSS_DUCK, getBossDuckDepthDb, isBossMoveSfx, isPlayerSfx, keepsBossHitUp, shouldDuckFor } from './sound_priority'
import { TOKI_LONG_CUT_MS, TOKI_SHORT_CLUCK_AT_MS, TOKI_SHORT_CLUCK_MS } from './rooster_toki'
import { ADOPTED_PARRY_SOUND_FILES, PARRY_SOUND_CANDIDATE_FILES, planParrySound } from './parry_sound'
import { PARRY_PITCH_FILES, PARRY_PITCH_REVERB, planParryPitch, type ParryPitchHit } from './parry_pitch'
import { PARRY_MATERIAL_VOICE, parryMaterialVoiceWeight, type ParryMaterialVoice } from './parry_material_voice'
import { parryMaterialOf, type ParryMaterial } from './parry_material'
import { PARRY_BASE_PLAN, PARRY_GAKIN_LEVEL, PARRY_HUM_CHAIN, PARRY_HUM_PLAN, parryHumCoreLevel, parryHumCoreT60, parryHumSwayMakeup, parryHumSwayOf, PARRY_TAIL_PLAN, isParryHumTail, parryHumFor, parryHumChainIndex, parryHumLevel, parryHumLineLevel, parryHumT60, parryTail, type ParryHumTail, type ParryTailPlan, PARRY_TONE_GB3, type ParryTone, PARRY_REMAKE_REVERB, isParryBaseVersion, isParryFixVersion, isParryGakinVersion, isParryOldVersion, parryFixWeight, parryRemakeFiles, parryRemakeVariant, parryVoiceVersion, planParryFixWeight, planParryRemake } from './parry_remake'
import { LUNA_SFX, finisherV2On, lunaSfxAllSamples } from './luna_finisher'
import { SFX_ROOTS_FILES, parseSfxRoots, type SfxRootsMode } from './sfx_roots'

export type Engine = {
  ctx: AudioContext
  master: GainNode
  comp: DynamicsCompressorNode
  reverb: ConvolverNode
  reverbSend: GainNode
  cathedral: ConvolverNode
  cathedralReturn: GainNode
  /** マスタ最終段のブリックウォール・リミッタ (ctx.destination の直前)。dev録音のタップ点。 */
  limiter: DynamicsCompressorNode
  /** ボスの技の音だけが通るつまみ (乾いた音 → comp / 残響の送り → reverbSend・cathedral)。
   *  プレイヤーの音が鳴った瞬間に一時的に下げる (2026-09-29・分け方の正本 = src/sound_priority.ts)。 */
  bossDuck: GainNode
  bossDuckVerb: GainNode
  bossDuckCathedral: GainNode
}

let engine: Engine | null = null

// AudioContext が生成済み・running (=解錠済み) か。タイトル画面のパッド開始ヒント (P5) が読む。
export function audioUnlocked(): boolean {
  return engine !== null && engine.ctx.state === 'running'
}

// =====================================================================
// SFX カタログ型 (Phase 4: 段階移行の器)
// =====================================================================
export type SfxBus = 'impact' | 'telegraph' | 'movement' | 'ui'
export type SfxPlayback = 'sample' | 'hybrid' | 'procedural'

export interface SfxSpec {
  bus: SfxBus
  playback: SfxPlayback
  /** sfxSamples に登録されたキー名。hybrid では先にサンプルを試み、未ロード時は proceduralFn へ落ちる。 */
  samples?: string[]
  volume: number
  /** ランダムピッチ幅 (0=固定, 0.1=±10%) — 将来 AudioBufferSource.playbackRate 揺らしに使う */
  pitchVariance: number
  reverbSend: number
  /** 同一ID の連打を防ぐ最小間隔 (ms)。0=制限なし */
  cooldownMs: number
  /** 同時発音上限 */
  polyphony: number
  proceduralFn?: (engine: Engine, t: number, intensity: number, isJust: boolean) => void
  /** サンプル再生成功時だけ重ねる短い補助層。proceduralFn はサンプル未ロード時の代替音を担う。 */
  sampleLayerFn?: (engine: Engine, t: number, intensity: number, isJust: boolean) => void
  /** 音の方向性・将来の差し替え条件・TODO */
  notes: string
}

// カタログ発音状態 (cooldown / polyphony 管理)
const _sfxLastFireMs: Partial<Record<SfxId, number>> = {}
const _sfxActiveCount: Partial<Record<SfxId, number>> = {}

// パリィ芯素材: A(細い銀)=軽い弾き主役 / C(芯あり)=重い弾き主役。
let parrySteelA: AudioBuffer | null = null
let parrySteelC: AudioBuffer | null = null
let parrySteelAArr: ArrayBuffer | null = null
let parrySteelCArr: ArrayBuffer | null = null
export function preloadParrySteel(): void {
  if (typeof fetch === 'undefined') return
  // 作者が作ってきた元の音。11-01 に一度シンセで作り直したが、作者「元に戻してほしい」で戻した (?v=3 はブラウザに残った作り直しを読み直させるため)。
  fetch('/sfx/parry_steel_1.wav?v=3').then(r => r.arrayBuffer()).then(a => { parrySteelAArr = a; decodeSteel() }).catch(() => {})
  fetch('/sfx/parry_steel_4.wav?v=3').then(r => r.arrayBuffer()).then(a => { parrySteelCArr = a; decodeSteel() }).catch(() => {})
}
function decodeSteel(): void {
  if (!engine) return
  if (parrySteelAArr && !parrySteelA) engine.ctx.decodeAudioData(parrySteelAArr.slice(0), b => { parrySteelA = b })
  if (parrySteelCArr && !parrySteelC) engine.ctx.decodeAudioData(parrySteelCArr.slice(0), b => { parrySteelC = b })
}

// 採用SE のプリレンダーWAV主役 (2026-06-09 ピボット)。
//   見切り/回避は合成でなく ElevenLabs 生成WAV を AudioBuffer で低遅延再生。
//   Web Audio は「再生・音量・残響send」だけ担当(音色は焼かない)。
const sfxSamples: Record<string, { buf: AudioBuffer | null; arr: ArrayBuffer | null }> = {}

function fetchSamples(list: [string, string][]): void {
  if (typeof fetch === 'undefined') return
  for (const [name, url] of list) {
    if (!sfxSamples[name]) sfxSamples[name] = { buf: null, arr: null }
    fetch(url).then(r => r.arrayBuffer()).then(a => { sfxSamples[name].arr = a; decodeSamples() }).catch(() => {})
  }
}

// 楽器の層を抜いて聞き比べる (開発版の ?sfxRoots= だけ・10-17・src/sfx_roots.ts)。既定 off = 配布版のまま。
let sfxRoots: SfxRootsMode = 'off'
export function setSfxRootsDev(raw: string | null | undefined): SfxRootsMode {
  sfxRoots = parseSfxRoots(raw)
  if (sfxRoots !== 'off') fetchSamples(SFX_ROOTS_FILES)
  return sfxRoots
}
export function sfxRootsMode(): SfxRootsMode { return sfxRoots }

export function preloadSfxSamples(): void {
  fetchSamples([
    ['mikiri', '/sfx/mikiri.mp3'],  // 見切り(暫定=b-tame: perfect dodge cue 高域-9dB / park継続)
    ['dodge', '/sfx/dodge.mp3'],    // 回避(✅確定=生 f を26msに短縮+末尾クリーン化: dodge_f_s6e / 2026-06-10 park解除)
    // 花嫁 裾乱れ ため予告=教会の鐘4連(三全音不協和・1.5s)。11-01 に Surge XT で作り直した (元の「教会の鐘1.mp3」は出どころ不明のため)。
    ['bride_flurry_bell', '/sfx/bride_flurry_bell.wav?v=2'],
    // 王系への通常命中。ElevenLabs生成候補Bを低音化・高域抑制した190ms版 (11-01 に一度作り直したが元へ戻した・?v=3 はキャッシュ避け)。
    ['hit_boss_contact', '/sfx/hit_boss_contact.wav?v=3'],
    // 致命3連撃 hit1/2/3 = ElevenLabs 生楽器テイク (2026-06-23 #12 確定・A=大聖堂+冷EQ で世界へ染める。本番置き場 /sfx/)
    ['exec_hit1', '/sfx/execute_hit1.mp3'],
    ['exec_hit2', '/sfx/execute_hit2.mp3'],
    ['exec_hit3', '/sfx/execute_hit3.mp3'],
    // 騎士の薙ぎ (2026-09-29 ユーザー「Lにする」)。書き出し = scripts/render_knight_sweep_sound_v3.py の l
    ['knight_sweep', '/sfx/knight_sweep.wav?v=1'],
    // 騎士の薙ぎ P の振り分け (09-29 夕方「今日の鳴らし分けに差し替えて」＝今の採用版)。役ごとの音 = src/knight_sweep_sound.ts KNIGHT_SWEEP_PV
    ...(Object.keys(KNIGHT_SWEEP_PV) as KnightSweepRole[]).map(role => [`knight_sweep_pv_${role}`, `/sfx/knight_sweep_pv_${role}.wav?v=1`] as [string, string]),
    // パリィの音 (2026-10-09 ユーザー採用): 普段の弾き = 柳の型 / ボスの致命を弾いた時 = 押し返しの二段 (src/parry_sound.ts)
    ...ADOPTED_PARRY_SOUND_FILES,
    // 騎士の固有のとどめ (突進) と弾かれた花嫁 (10-12・src/exec_unique_sound.ts・書き出し = scripts/render_exec_unique_sfx.py)
    ...execUniqueSoundFiles('a'),
    // 戴冠獣の予備動作の声 b 獅子寄り (10-13 ユーザー採用・src/hound_voice_sound.ts・書き出し = scripts/render_hound_voice_sfx.py)
    ...houndVoiceFiles('b'),
    ...awokenVoiceFiles('haunt'),
    // 剣舞・軍配者たちの技の音 (10-31 作者 blade=ok・twins=ok・src/boss_sfx_v1.ts・書き出し = scripts/render_boss_sfx_v1.py)。鶏は ?bossSfx=v1 の時だけ
    ...bossSfxShippedFiles(),
    ...floorTellFiles(),
    // ボスの致命固有の音 (配布版で鳴る) と花嫁の抱擁の音 (作り直しの案を聞いている間は開発版だけ)。11-01・src/finisher_sound.ts
    ...finisherSfxFiles(),
    // 請われぬ介錯の技の音 a (10-29・src/exec_sound.ts・書き出し = scripts/render_exec_sfx.py)
    ...execSoundFiles('a'),
    // d (10-30・作者が選んだ手本に寄せたシンセ・風切りと叩きつけの2役) と、既定 e (11-03・残りの役も同じシンセ。振り下ろし・叩きつけは d を鳴らす)
    ...execSoundFiles('d'),
    ...execSoundFiles('e'),
    // 会話場面の物音 (10-27・src/adv_sound.ts・書き出し = scripts/render_adv_sfx.py)
    ...advSoundFiles('a'),
  ])
}
/** 会話場面の物音の別案 (開発版 ?advSound=b|c だけ・git 外)。 */
export function preloadAdvSoundTake(take: AdvSoundTake): void {
  fetchSamples(advSoundFiles(take))
}
/** 固有のとどめの音の別テイク (開発版 ?execUniqueSound=b だけ・git 外)。キー = <名前>_b (読めていればカタログが先に鳴らす)。 */
export function preloadExecUniqueSoundB(): void {
  fetchSamples(execUniqueSoundFiles('b').map(([k, url]) => [`${k}_b`, url] as [string, string]))
}

// 騎士の薙ぎの音の候補 a〜t — 聞き比べ専用 (本番では読まない)。素材は git 外 = scripts/render_knight_sweep_sound.py で書き出す。
export function preloadKnightSweepCandidates(): void {
  fetchSamples([...KNIGHT_SWEEP_CANDIDATES.map(k => `knight_sweep_${k}`)].map(id => [id, `/sfx/candidates/${id}.wav?v=4`]))
}

// 騎士の予告音の候補 — 聞き比べ専用 (本番では読まない)。素材は git 外 = scripts/render_knight_telegraph_sound.py で書き出す。
// 戴冠獣の予備動作の声の別テイク a (10-13・開発版 ?houndVoice=a の時だけ読む・src/hound_voice_sound.ts)。既定 b は preloadSfxSamples が読む。
export function preloadHoundVoice(take: 'a' | 'b'): void {
  fetchSamples(houndVoiceFiles(take))
}
// 請われぬ介錯の技の音の別テイク b・c (開発版 ?execSound=b|c の時だけ読む)。a・d・既定 e は preloadSfxSamples が読む。
export function preloadExecSound(take: ExecSoundTake): void {
  fetchSamples(execSoundFiles(take))
}
// 鶏・剣舞・軍配者たちの技の音 v1 (10-31・開発版 ?bossSfx=v1 の時に三体とも読む・剣舞と軍配者たちは preloadSfxSamples も読む・src/boss_sfx_v1.ts)。
export function preloadBossSfxV1(): void {
  fetchSamples(bossSfxV1Files())
}
// 剣舞の技の音と拍手 v2 (11-04・開発版 ?bladeSfx=v2 の時だけ読む・src/blade_sfx_v2.ts)。
export function preloadBladeSfxV2(): void {
  fetchSamples(bladeSfxV2Files())
}
// 覚醒王の予備動作の声 (10-13・開発版 ?awokenVoice=deep|haunt の時だけ読む・src/awoken_voice_sound.ts)。
export function preloadAwokenVoice(take: AwokenVoiceTake): void {
  fetchSamples(awokenVoiceFiles(take))
}

export function preloadKnightTellCandidates(): void {
  fetchSamples(KNIGHT_TELL_SFX_IDS.map(id => [id, `/sfx/candidates/${id}.wav?v=2`]))
}

// パリィの音の比較用の候補 (b・c・A・C) — 開発版の ?parrySound= / ?execParrySound= の時だけ読む (本番では読まない・2026-10-09・src/parry_sound.ts)。
//   採用の2本 (柳の型・押し返しの二段) は本番の preloadSfxSamples が読む。
//   素材は git 外 = scripts/render_parry_sound_candidates.py で書き出す。置いていなければ今の音が鳴る。
export function preloadParrySoundCandidates(): void {
  fetchSamples([...PARRY_SOUND_CANDIDATE_FILES])
}
// 連続の段と技の重さで変わるパリィの音の候補 (?parryPitch=p|q|r の時だけ・10-13・src/parry_pitch.ts)。柳の型は本番の先読みが読む。
export function preloadParryPitchSamples(): void {
  fetchSamples([...PARRY_PITCH_FILES])
}
// パリィの音の作り直し (?parrySfx=a|b|c の時だけ・10-14・src/parry_remake.ts)。選んだ案の材質8本＋重さ1本を読む。
export function preloadParryRemakeSamples(): void {
  fetchSamples(parryRemakeFiles(parryRemakeVariant()))
}

// 系統③ 魂 — fxlab の聞き比べ専用 (本番では読まない・2026-07-26 に本番リストから分離)。
//   台帳 `docs/departments/sound/se-design-tracker.md` P0-2 (2026-06-24 #12 完全クローズ) =
//   魂は無音(off)で確定。 下の soulTake 既定も 'off' なので、 これらは本番で一度も鳴らない。
//   ⚠️ 実体は そざい/_inbox/2026-06-24-魂_偶発系/processed/ にあり public/ へは未配置
//      (git 方針 2026-06-22 裁定=ラボ素材はローカルのみ / .gitignore public/sfx/candidates/)。
//      聞き比べる時は 5 本を public/sfx/candidates/ へコピーする。 未配置のまま呼んでも
//      fetch が失敗して黙って捨てられるだけ (fxlab の魂ボタンが無音になる)。
//      旧テイク exec_soul_g4_3s.mp3 (弦+声 #4 = old_strings) はディスク上に現存しない。
export function preloadSoulTakeCandidates(): void {
  fetchSamples([
    ['exec_soul', '/sfx/candidates/exec_soul_g4_3s.mp3'],  // old_strings (旧テイク・現物なし)
    ['exec_soul_e1', '/sfx/candidates/exec_soul_e1.mp3'],  // E=溶ける吐息
    ['exec_soul_e2', '/sfx/candidates/exec_soul_e2.mp3'],
    ['exec_soul_e3', '/sfx/candidates/exec_soul_e3.mp3'],
    ['exec_soul_f1', '/sfx/candidates/exec_soul_f1.mp3'],  // F=空気が通った
    ['exec_soul_f2', '/sfx/candidates/exec_soul_f2.mp3'],
  ])
}
function decodeSamples(): void {
  if (!engine) return
  for (const name in sfxSamples) {
    const s = sfxSamples[name]
    if (s.arr && !s.buf) engine.ctx.decodeAudioData(s.arr.slice(0), b => { s.buf = b })
  }
}
function playSample(name: string, vol: number, reverb: number, out: Engine | null = engine): boolean {
  if (!out) return false
  const s = sfxSamples[name]
  if (!s || !s.buf) return false
  const src = out.ctx.createBufferSource()
  src.buffer = s.buf
  const g = out.ctx.createGain()
  g.gain.value = vol
  src.connect(g).connect(out.comp)
  if (reverb > 0) routeReverb(out, g, reverb)
  src.start(out.ctx.currentTime)
  return true
}

// 会話場面の環境音 (鳴らし続ける・src/adv.ts の advAmbience)。 main が毎フレーム「いま鳴っているべき id」を渡す。
//   同じ id は何もしない。 素材がまだ読めていなければ、 読めた後のフレームで鳴らし始める。
//   大きさは会話場面の物音 (0.2) の 0.8 倍 = 戴冠獣の試作 (広間の空気 0.8) と同じ釣り合い。
const ADV_AMBIENCE_VOLUME = 0.16
let advAmb: { id: string; src: AudioBufferSourceNode; g: GainNode } | null = null
export function setAdvAmbience(id: string | null, fadeMs: number): void {
  if ((advAmb?.id ?? null) === id) return
  const e = engine
  if (!e) return
  if (advAmb) {
    const { src, g } = advAmb, t = e.ctx.currentTime
    g.gain.cancelScheduledValues(t); g.gain.setValueAtTime(g.gain.value, t); g.gain.linearRampToValueAtTime(0, t + fadeMs / 1000)
    src.stop(t + fadeMs / 1000 + .05)
    advAmb = null
  }
  const buf = id ? sfxSamples[id]?.buf : null
  if (!id || !buf) return
  const src = e.ctx.createBufferSource(), g = e.ctx.createGain(), t = e.ctx.currentTime
  src.buffer = buf; src.loop = true
  g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(ADV_AMBIENCE_VOLUME, t + fadeMs / 1000)
  src.connect(g).connect(e.comp)
  src.start(t)
  advAmb = { id, src, g }
}

const ADV_IMPACT_SFX = new Set<string>(['adv_wrist_grip', 'adv_hound_chain_taut',
  'adv_exec_stomp', 'adv_jester_wing_beat', 'adv_blade_whoosh', 'adv_princess_lunge', 'adv_awoken_stones_fall', 'adv_knight_butt_strike'])

function sampleReady(name: string): boolean {
  return !!sfxSamples[name]?.buf
}
// 再生速度つきの素材再生 (パリィの音の候補が使う・普段のジャストでない弾きを少し低くする)。
function playSampleRated(name: string, vol: number, rate: number, reverb: number, out: Engine): boolean {
  const s = sfxSamples[name]
  if (!s || !s.buf) return false
  const src = out.ctx.createBufferSource()
  src.buffer = s.buf
  src.playbackRate.value = rate
  const g = out.ctx.createGain()
  g.gain.value = vol
  src.connect(g).connect(out.comp)
  if (reverb > 0) routeReverb(out, g, reverb)
  src.start(out.ctx.currentTime)
  return true
}

// いま鳴らす弾きが「ボスの致命を弾いた」ものか (playPending が束ごとに立てる・main.ts が parry_spark.exec から渡す)。
let parryHitIsExec = false
// いま鳴らす弾きの連続の段と技の重さ (playPending が束ごとに立てる・音の候補 ?parryPitch= だけが使う)。
let parryHitPitch: ParryPitchHit | undefined
// 連続の段と技の重さで変わる候補 (src/parry_pitch.ts)。層を全部同時に鳴らす。素材が1つでも読めていなければ鳴らさず false (= 今の音)。
function playParryPitch(hit: ParryPitchHit, e: Engine): boolean {
  const layers = planParryPitch(hit)
  if (!layers || !layers.every((l) => sampleReady(l.sample))) return false
  for (const l of layers) playSampleRated(l.sample, l.gain, l.rate, PARRY_PITCH_REVERB, e)
  return true
}
// 作り直しの案 (src/parry_remake.ts)。芯と重さの2層。素材が読めていなければ鳴らさず false (= 今の音)。連続の段は使わない。
function playParryRemake(hit: ParryPitchHit, e: Engine): boolean {
  const layers = planParryRemake({ attackId: hit.attackId, weight: hit.weight, broke: hit.broke, material: hit.material })
  if (!layers || !layers.every((l) => sampleReady(l.sample))) return false
  for (const l of layers) playSampleRated(l.sample, l.gain, l.rate, PARRY_REMAKE_REVERB, e)
  return true
}
// 弾きの音: 候補が選ばれていれば候補の WAV、そうでなければ (または未ロードなら) 今の音。
function playParryVoice(e: Engine, t: number, intensity: number, isJust: boolean): void {
  // 前の音の比較 (開発版 ?parrySfx=old-0526|old-just|old-normal だけ・10-14)。致命を弾いた時は今のまま。
  // 得物での使い分け (10-18・配布版) = 弾いた技の得物の材質で版を選ぶ (金の爪と牙は gs3・ほかは gs1)。開発版 ?parryByWeapon=0 で切る。
  const rv = parryVoiceVersion(parryRemakeVariant(), parryHitPitch?.attackId, parryHitPitch?.material)
  if (!parryHitIsExec && isParryOldVersion(rv)) {
    if (rv === 'old-0526') playParry(e, t)
    else playParryCold(e, t, intensity, rv === 'old-just')
    return
  }
  // 直した版 d / e (開発版だけ・10-14): 前の冷たい銀 (ジャスト) を技の重さで鳴らす・e は重さの層を重ねる。
  if (!parryHitIsExec && isParryFixVersion(rv)) {
    const hit = { attackId: parryHitPitch?.attackId, weight: parryHitPitch?.weight ?? intensity, broke: parryHitPitch?.broke }
    if (rv === 'f') {
      // f (10-15): 材質で鳴らし分ける・重さの幅は狭める (src/parry_material_voice.ts)
      const mat = parryMaterialOf(hit.attackId, hit.weight).material
      const voice = PARRY_MATERIAL_VOICE[mat]
      const wf = parryMaterialVoiceWeight(parryFixWeight(hit))
      playParryCold(e, t, wf, true, voice)
      playParryMaterialLayer(e, t, voice, wf)
      const wl = planParryFixWeight(hit, rv)
      if (wl && sampleReady(wl.sample)) playSampleRated(wl.sample, wl.gain * voice.weightMul, wl.rate, PARRY_REMAKE_REVERB, e)
      return
    }
    if (isParryGakinVersion(rv)) {
      // ga1〜ga3 (10-16): e の上にガとキンを重ね、全体を e の大きさへそろえる
      const lv = PARRY_GAKIN_LEVEL[rv]
      const trim = e.ctx.createGain()
      trim.gain.value = 10 ** (lv.trimDb / 20)
      trim.connect(e.comp)
      const te: Engine = { ...e, comp: trim as unknown as DynamicsCompressorNode }
      const w = Math.max(0.25, parryFixWeight(hit))
      playParryCold(te, t, w, true)
      playParryGakin(te, t, w, 10 ** (lv.layerDb / 20))
      const wl = planParryFixWeight(hit, rv)
      if (wl && sampleReady(wl.sample)) playSampleRated(wl.sample, wl.gain, wl.rate, PARRY_REMAKE_REVERB, te)
      return
    }
    if (isParryBaseVersion(rv)) {
      // gb1〜gb3 (10-16): 土台の「ぽよん」(音程が滑る層・低い純音) を段ごとに外し、ガとキンを重ねる。
      //   冷たい銀 (鋼・鎖の鈴) は残し、ガとキンは 6kHz より上を削って灰の王城の色へ寄せる (ゼンゼロの音そのままにしない)
      const plan = PARRY_BASE_PLAN[rv]
      const trim = e.ctx.createGain()
      trim.gain.value = 10 ** (plan.trimDb / 20)
      trim.connect(e.comp)
      const te: Engine = { ...e, comp: trim as unknown as DynamicsCompressorNode }
      const w = Math.max(0.25, parryFixWeight(hit))
      // gh・gk (10-18): 高さ・明るさの候補。gb3 は PARRY_TONE_GB3 (= 何も変えない)
      const tone = plan.tone ?? PARRY_TONE_GB3
      playParryCold(te, t, w, true, undefined, { sweep: plan.cutSweep, pureLow: plan.noiseLow, bells: sfxRoots !== 'off', soul: plan.soulDb === undefined ? undefined : 10 ** (plan.soulDb / 20) }, tone)
      if (plan.noiseLow) playParryThud(te, t, w, 10 ** (tone.thudDb / 20))
      if (plan.pushDb !== null) playParryPush(te, t, w, 10 ** (plan.pushDb / 20))
      const ashCut = e.ctx.createBiquadFilter()
      ashCut.type = 'lowpass'
      ashCut.frequency.value = tone.ashHz
      ashCut.Q.value = 0.7
      ashCut.connect(trim)
      if (sfxRoots !== 'bare') playParryGakin({ ...te, comp: ashCut as unknown as DynamicsCompressorNode }, t, w, 10 ** (plan.layerDb / 20), tone.pitch)
      else playParryGa({ ...te, comp: ashCut as unknown as DynamicsCompressorNode }, t, w, 10 ** (plan.layerDb / 20), tone.pitch)
      const wl = planParryFixWeight(hit, rv)
      if (wl && sampleReady(wl.sample)) playSampleRated(wl.sample, wl.gain, wl.rate, PARRY_REMAKE_REVERB, te)
      // 余韻 (配布版＝humWc・開発版 ?parryTail= で比較・src/parry_remake.ts)。唸り (10-20) と板の響き (10-19) は同時に鳴らさない。
      //   崩した一打は humWc では余韻なし (鳴っている唸りも切る)
      const tl = parryTail()
      const hum = parryHumFor(tl, hit.broke)
      if (hum) { cutParryTail(e.ctx, t); playParryHum(te, trim, t, hum, w, parryHitPitch?.material ?? parryMaterialOf(hit.attackId, hit.weight).material) }
      else {
        stopParryHum(e.ctx, t)
        if (tl && !isParryHumTail(tl)) playParryTail(te, trim, t, PARRY_TAIL_PLAN[tl])
      }
      return
    }
    playParryCold(e, t, Math.max(0.25, parryFixWeight(hit)), true)
    const wl = planParryFixWeight(hit, rv)
    if (wl && sampleReady(wl.sample)) playSampleRated(wl.sample, wl.gain, wl.rate, PARRY_REMAKE_REVERB, e)
    return
  }
  if (!parryHitIsExec && parryHitPitch && playParryRemake(parryHitPitch, e)) return
  if (!parryHitIsExec && parryHitPitch && playParryPitch(parryHitPitch, e)) return
  const plan = planParrySound({ exec: parryHitIsExec, intensity, isJust })
  // 開発版 ?sfxRoots=plain|bare: 致命を弾いた時は調律した正弦・鐘を抜いた素材へ (無ければ今の素材)
  if (plan.kind === 'sample' && parryHitIsExec && sfxRoots !== 'off' && sampleReady('parry_exec_push_plain')
    && playSampleRated('parry_exec_push_plain', plan.gain, plan.rate, plan.reverbSend, e)) return
  if (plan.kind === 'sample' && playSampleRated(plan.sample, plan.gain, plan.rate, plan.reverbSend, e)) return
  if (parryColdEnabled) playParryCold(e, t, intensity, isJust)
  else playParry(e, t)
}

// ===== プレイヤーの音を主役にする (2026-09-29・src/sound_priority.ts) =====
// ボスの技の音は、出口 (comp / reverbSend / cathedral) をボス用のつまみへ差し替えた Engine を通して鳴らす。
//   合成音の関数は e.comp 等へつなぐだけなので、関数の中身を変えずにつまみを通せる。
// 同じ束の当たりの音を下げずに鳴らす間だけ true (playPending・keepsBossHitUp)。
let bypassBossDuck = false
function outFor(e: Engine, id: SfxId): Engine {
  if (bypassBossDuck || !isBossMoveSfx(id)) return e
  return { ...e, comp: e.bossDuck as unknown as DynamicsCompressorNode, reverbSend: e.bossDuckVerb,
    cathedral: e.bossDuckCathedral as unknown as ConvolverNode }
}
// ボスの技の音を一時的に下げる。下げている途中に次のプレイヤーの音が来たら、そこから下げ直す (戻りを延ばす)。
function duckBossMoves(e: Engine): void {
  const db = getBossDuckDepthDb()
  if (db === null) return
  const now = e.ctx.currentTime
  const low = 10 ** (db / 20)
  for (const p of [e.bossDuck.gain, e.bossDuckVerb.gain, e.bossDuckCathedral.gain]) {
    const cur = p.value
    p.cancelScheduledValues(now)
    p.setValueAtTime(cur, now)
    p.linearRampToValueAtTime(low, now + BOSS_DUCK.attackSec)
    p.setValueAtTime(low, now + BOSS_DUCK.attackSec + BOSS_DUCK.holdSec)
    p.linearRampToValueAtTime(1, now + BOSS_DUCK.attackSec + BOSS_DUCK.holdSec + BOSS_DUCK.releaseSec)
  }
}

// サンプルを「灰の王城の世界」へ置く再生 (2026-06-23 #12 世界観編集):
//   coldShelfDb<0 で高域の硬さ/ヒスを下げ「冷たい銀」へ / cathedral=大聖堂の石ホールへ送る。
//   = 乾いた生録音を parry 等と同じ空間に座らせる。
function playSampleWorld(
  name: string,
  vol: number,
  opts: {
    reverb?: number; cathedral?: number; coldShelfDb?: number
    fade?: { start: number; dur: number }       // start 秒から dur 秒かけて音量を引いて消す (余韻対策・奪われる方向)
    lpSweep?: { from: number; to: number; dur: number }  // ローパスを from→to へ絞る (くぐもって遠のく)
  },
): boolean {
  if (!engine) return false
  const s = sfxSamples[name]
  if (!s || !s.buf) return false
  const now = engine.ctx.currentTime
  const src = engine.ctx.createBufferSource()
  src.buffer = s.buf
  const g = engine.ctx.createGain()
  g.gain.value = vol
  let node: AudioNode = src
  if (opts.coldShelfDb && opts.coldShelfDb !== 0) {
    const shelf = engine.ctx.createBiquadFilter()
    shelf.type = 'highshelf'
    shelf.frequency.value = 6000
    shelf.gain.value = opts.coldShelfDb
    node.connect(shelf)
    node = shelf
  }
  if (opts.lpSweep) {
    const lp = engine.ctx.createBiquadFilter()
    lp.type = 'lowpass'
    lp.frequency.setValueAtTime(opts.lpSweep.from, now)
    lp.frequency.linearRampToValueAtTime(opts.lpSweep.to, now + opts.lpSweep.dur)
    node.connect(lp)
    node = lp
  }
  node.connect(g).connect(engine.comp)
  if (opts.fade) {
    g.gain.setValueAtTime(vol, now + opts.fade.start)
    g.gain.linearRampToValueAtTime(0.0001, now + opts.fade.start + opts.fade.dur)
  }
  if (opts.reverb && opts.reverb > 0) routeReverb(engine, g, opts.reverb)
  if (opts.cathedral && opts.cathedral > 0) routeCathedral(engine, g, opts.cathedral)
  src.start(now)
  return true
}

export function ensureAudio(): void {
  if (engine) { decodeSteel(); decodeSamples(); return }
  if (typeof window === 'undefined') return
  const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
  const ctx = new Ctx()

  const master = ctx.createGain()
  master.gain.value = 0.85

  const comp = ctx.createDynamicsCompressor()
  comp.threshold.value = -10
  comp.knee.value = 6
  comp.ratio.value = 6
  comp.attack.value = 0.002
  comp.release.value = 0.08

  const reverb = ctx.createConvolver()
  reverb.buffer = makeImpulseResponse(ctx, 0.55, 2.4)
  const reverbSend = ctx.createGain()
  reverbSend.gain.value = 1

  // マスタ最終段のブリックウォール・リミッタ (2026-06-09: 「プツ」割れ対策)。
  //   実素材の芯 (parry_steel = 0dBFS) と合成インパクトの和が一瞬 1.0 を越えると
  //   出力でデジタルクリップ = 「プツ」というクラックになる。comp(attack2ms)では拾えない
  //   <1ms の立ち上がりを、ここで 20:1 brickwall・1ms attack で頭打ちにして割れを消す。
  //   threshold は -1.5dB と浅めにして、ピーク以外の音色・音圧はほぼ素通し。
  const limiter = ctx.createDynamicsCompressor()
  limiter.threshold.value = -1.5
  limiter.knee.value = 0
  limiter.ratio.value = 20
  limiter.attack.value = 0.001
  limiter.release.value = 0.06

  // 致命3撃目専用: 大聖堂 IR 1.8s (通常 reverb 0.55s より長い石の空間)
  const cathedral = ctx.createConvolver()
  cathedral.buffer = makeImpulseResponse(ctx, 1.8, 1.8)
  const cathedralHPF = ctx.createBiquadFilter()
  cathedralHPF.type = 'highpass'; cathedralHPF.frequency.value = 280
  const cathedralLPF = ctx.createBiquadFilter()
  cathedralLPF.type = 'lowpass'; cathedralLPF.frequency.value = 5500
  const cathedralReturn = ctx.createGain()
  cathedralReturn.gain.value = 1.0
  cathedral.connect(cathedralHPF).connect(cathedralLPF).connect(cathedralReturn).connect(master)

  comp.connect(master)
  reverbSend.connect(reverb)
  reverb.connect(master)
  master.connect(limiter)
  limiter.connect(ctx.destination)

  const bossDuck = ctx.createGain()
  bossDuck.connect(comp)
  const bossDuckVerb = ctx.createGain()
  bossDuckVerb.connect(reverbSend)
  const bossDuckCathedral = ctx.createGain()
  bossDuckCathedral.connect(cathedral)

  engine = { ctx, master, comp, reverb, reverbSend, cathedral, cathedralReturn, limiter, bossDuck, bossDuckVerb, bossDuckCathedral }
  if (currentBgm) bgmChain(currentBgm)   // 先に鳴っていた曲 (タイトルなど) にも、 いまの遠さを掛ける
  decodeSteel()
  decodeSamples()
}

// dev専用の計器: 実出力 (limiter の後 = 実際に耳へ届く信号) を MediaStream として取り出す。
//   ★用途: SE を変えた時に「良くなったはず」と文章で報告せず、実際の音を録ってユーザーへ渡すため
//     (AGENTS.md §9「動きはGIFで見せる」の音版・メニュー操作音 2026-07-25 で確立した運用)。
//   ★合成し直した近似ではなく **本番と同じグラフの出力** を録る = 聞かせた音と実機の音がズレない。
//   本編の音は止めない (destination への既存接続はそのまま・タップを1本増やすだけ)。
export function __devCaptureStream(): MediaStream | null {
  ensureAudio()
  if (!engine) return null
  const dest = engine.ctx.createMediaStreamDestination()
  engine.limiter.connect(dest)
  return dest.stream
}
// dev専用の計器: ボスの技の音のつまみ (bossDuck) を通った後だけを取り出す (2026-09-29)。
//   弾きと重なった時にボスの音がどれだけ下がったかを、弾きの音に埋もれさせずに測るため (scripts/record_boss_duck.mjs)。
export function __devCaptureBossStream(): MediaStream | null {
  ensureAudio()
  if (!engine) return null
  const dest = engine.ctx.createMediaStreamDestination()
  engine.bossDuck.connect(dest)
  return dest.stream
}

// =====================================================================
// BGM (HTMLAudioElement で mp3 再生、Python合成のオリジナル楽曲)
// =====================================================================
// SE 系 (AudioContext) とは独立。各ボスごとに専用 mp3 をループ再生。
// ボス切替時にクロスフェード。

let currentBgm: HTMLAudioElement | null = null
let currentBgmBossId = ''
let bgmRate = 1   // BGM 再生速度 (=ピッチ連動=転調)。 setBgmRate で更新・新トラックへ引き継ぐ。
const BGM_TARGET_VOLUME = 0.15  // Suno のフル尺曲はマスタが大きい。SE/世界観の邪魔をしない控えめな敷き
const BGM_FADE_SEC = 0.6
const BGM_VICTORY_FADE_SEC = 3.7  // 致命シーケンス(実時間~3.7s)フルに合わせ、 VICTORY と同時に消えきる

export function bgmPathForBoss(bossId: string): string | null {
  switch (bossId) {
    case 'nameless-shade': return '/bgm/explore.mp3'  // 導入戦: 静かな探索アンビエント
    case 'dream-hound':    return '/bgm/king.mp3'     // 王: Suno「Cathedral Cello」
    case 'bride':          return '/bgm/bride.mp3'    // 花嫁: 壊れたオルゴール+葬送オルガン
    case 'vigil-knight':   return '/bgm/knight.mp3'      // 騎士: 哀愁の忠誠 (静かな圧)
    case 'the-executioner': return '/bgm/executioner.mp3' // 処刑人: 重い刻みの執行
    case 'crowned-king-awoken': return '/bgm/king-awoken.mp3' // 覚醒王: 王のテーマの暴走再演
    case 'inverted-jester': return '/bgm/jester.mp3'           // 道化: 狂った circus waltz
    case 'blade-dancer':    return '/bgm/dancer.mp3'           // 剣舞: 3拍子の剣戟ワルツ
    case 'hollow-actor':    return '/bgm/hollow.mp3'           // hollow-actor の曲 (前の版＝総集編の時に作った過去のテーマの引用集)
    case 'former-princess': return '/bgm/princess-duel.mp3'    // 先代の姫: 鏡映の決闘
    default: return null
  }
}

// 任意のパスの BGM をクロスフェード再生 (フェーズ駆動 BGM の共通入口)。
//   path=null は「BGM 無し」(既存をフェードアウト)。 同じ path の再呼び出しは no-op。
export function playBgmPath(path: string | null): void {
  if (currentBgmBossId === (path ?? '') && currentBgm) return  // 同じ曲は再開不要
  if (typeof Audio === 'undefined') return

  if (!path) {
    if (currentBgm) fadeAndStop(currentBgm, BGM_FADE_SEC)
    currentBgm = null
    currentBgmBossId = ''
    return
  }

  // 既存をフェードアウト
  if (currentBgm) {
    fadeAndStop(currentBgm, BGM_FADE_SEC)
  }

  // 新規 BGM をフェードイン
  const audio = new Audio(path)
  audio.loop = true
  audio.volume = 0
  audio.playbackRate = bgmRate   // 進行中の転調 (最終部屋など) を新トラックにも引き継ぐ
  // 遠く鳴らしたい時に WebAudio がまだ無い (= 初操作の前) なら鳴らさず待つ。
  //   要素だけで鳴らすと絞りもこもりも掛からず、 いきなり戦いの大きさで鳴る (2026-10-28 作者「画面をクリックする前に通常の音量でBGMがなっていて」)。
  //   初操作で ensureAudio → retryBgmStart が遠さを掛けてから鳴らす。
  if (engine || (bgmMix.level === 1 && !bgmMix.muffled)) {
    audio.play().catch(() => { /* autoplayポリシーで失敗、ユーザー初操作後に再試行される */ })
  }
  fadeIn(audio, BGM_TARGET_VOLUME, BGM_FADE_SEC)
  currentBgm = audio
  currentBgmBossId = path
  bgmChain(audio)   // いまの遠さで鳴らし始める (setBgmMix)
}

// 会話場面で BGM を「遠く」で鳴らす (2026-10-28 作者「いきなり効果音だけ鳴らすのはびっくりもするし…作り込みが甘く感じる」)。
//   曲の要素を WebAudio へ通し、 低い方だけ残す (扉の向こうで鳴っている聞こえ方) + 小さくする。
//   main が毎フレーム「いまの遠さ」を渡す。 戦いへ抜ける時は曲を消してから暗転し、 戦いで頭から鳴らし直す (main の advExiting)。
//   要素の volume (フェード・見切りの絞り) はこれまで通りで、 その後ろに掛かる。
type BgmMixChain = { g: GainNode; lp: BiquadFilterNode }
const bgmChains = new WeakMap<HTMLAudioElement, BgmMixChain>()
const BGM_OPEN_HZ = 20000
const BGM_MUFFLED_HZ = 650
let bgmMix = { level: 1, muffled: false }
function bgmChain(audio: HTMLAudioElement): BgmMixChain | null {
  const have = bgmChains.get(audio)
  if (have) return have
  const e = engine
  if (!e) return null
  try {
    const src = e.ctx.createMediaElementSource(audio)
    const lp = e.ctx.createBiquadFilter(), g = e.ctx.createGain()
    lp.type = 'lowpass'; lp.Q.value = 0.5
    lp.frequency.value = bgmMix.muffled ? BGM_MUFFLED_HZ : BGM_OPEN_HZ
    g.gain.value = bgmMix.level
    src.connect(lp).connect(g).connect(e.ctx.destination)
    const chain = { g, lp }
    bgmChains.set(audio, chain)
    return chain
  } catch { return null }
}
// level=曲の大きさの倍率 (1=戦いと同じ)・muffled=遠く (こもらせる)・sec=移りの長さ。 同じ値は何もしない。
export function setBgmMix(level: number, muffled: boolean, sec = 0.5): void {
  const changed = level !== bgmMix.level || muffled !== bgmMix.muffled
  bgmMix = { level, muffled }
  if (!currentBgm) return
  const fresh = !bgmChains.has(currentBgm)
  const chain = bgmChain(currentBgm)
  if (!chain || (!changed && !fresh) || !engine) return
  const t = engine.ctx.currentTime, k = Math.max(0.01, sec / 3)
  chain.g.gain.cancelScheduledValues(t); chain.g.gain.setTargetAtTime(level, t, k)
  chain.lp.frequency.cancelScheduledValues(t); chain.lp.frequency.setTargetAtTime(muffled ? BGM_MUFFLED_HZ : BGM_OPEN_HZ, t, k)
}

// BGM の再生速度 (=ピッチも連動 = 転調)。 1=原曲。 >1 で速く高く (クライマックスの煽り)。
//   フェーズ駆動で毎フレーム呼んで良い (同値は no-op)。 main の desiredBgmRate が唯一の決定元。
export function setBgmRate(rate: number): void {
  if (rate === bgmRate) return
  bgmRate = rate
  if (currentBgm) currentBgm.playbackRate = rate
}

export function playBgmForBoss(bossId: string): void {
  playBgmPath(bgmPathForBoss(bossId))
}

// フェードの刻み。 ~60Hz と細かくして音量段差の「プツプツ」(zipper noise) を無くす。
//   HTMLAudioElement の volume はカクッと変えると波形が途切れてクリック音が出るため、
//   1段あたりの変化量を極小にして滑らかに繋ぐ。
const FADE_STEP_MS = 16

function fadeIn(audio: HTMLAudioElement, target: number, durationSec: number): void {
  const steps = Math.max(1, Math.round((durationSec * 1000) / FADE_STEP_MS))
  let i = 0
  const id = setInterval(() => {
    i += 1
    audio.volume = Math.min(target, (i / steps) * target)
    if (i >= steps) clearInterval(id)
  }, FADE_STEP_MS)
}

function fadeAndStop(audio: HTMLAudioElement, durationSec: number): void {
  const startVol = audio.volume
  const steps = Math.max(1, Math.round((durationSec * 1000) / FADE_STEP_MS))
  let i = 0
  const id = setInterval(() => {
    i += 1
    audio.volume = Math.max(0, startVol * (1 - i / steps))
    if (i >= steps) {
      audio.pause()
      clearInterval(id)
    }
  }, FADE_STEP_MS)
}

// ボス撃破時など、 BGM をフェードアウトして停止する (次の boss.id 切替で再生し直せるよう状態もクリア)。
//   ⚠️ 必ず loop=false に切ってから fade する: mp3 (Suno 出力) は仕様上ループ境界に
//      サイレンスパディングが残り、 victory fade (3.7s) の途中で loop=true のまま境界を跨ぐと
//      「プツッ」と一瞬のクリックが混じる (#6 2026-06-24 原因)。 fade 中はもう次の周を回さない。
export function fadeOutBgm(durationSec: number = BGM_VICTORY_FADE_SEC): void {
  if (currentBgm) {
    currentBgm.loop = false
    fadeAndStop(currentBgm, durationSec)
  }
  currentBgm = null
  currentBgmBossId = ''
}

// ユーザー初操作後に呼ぶ再試行 (autoplayポリシー対策)
export function retryBgmStart(): void {
  if (currentBgm && currentBgm.paused) {
    bgmChain(currentBgm)   // 鳴らす前に遠さを掛ける (掛ける前の1コマも大きく鳴らさない)
    currentBgm.play().catch(() => { /* ignore */ })
  }
}

// 一発もの (勝利=終幕 / 敗北 等のスティンガー)。 ループせず鳴って消える。 BGM ループ系とは独立。
//   戦闘中の操作後に呼ばれるため autoplay ポリシーは基本クリア済。 BGM(0.15)より前に出すため音量は高め。
export function playSting(path: string, volume: number = 0.4): void {
  if (typeof Audio === 'undefined') return
  const audio = new Audio(path)
  audio.loop = false
  audio.volume = Math.max(0, Math.min(1, volume))
  audio.play().catch(() => { /* autoplay失敗時は無音 */ })
}

// 🌀 見切りの「時間スロー」演出 (2026-06-09・ベヨネッタ Witch Time モデル)。
//   ジャスト回避の瞬間、BGM を一瞬 "ふっ" と絞って世界が息を止める → 見切りWAV+光が
//   静寂を切り裂く。 満足感は "音単体" でなく "静寂とのコントラスト" で作る。
//   ※ Web Audio の音色作りでなく「ミックス(音量)の演出」= ピボットで許した補助の範囲。
let bgmDuckRaf = 0
function duckBgmForMikiri(): void {
  if (!currentBgm || currentBgm.paused) return
  const target = BGM_TARGET_VOLUME
  const floor = 0.03                 // ほぼ無音まで絞る
  currentBgm.volume = Math.min(currentBgm.volume, floor)
  if (bgmDuckRaf) cancelAnimationFrame(bgmDuckRaf)
  const holdMs = 120, releaseMs = 420
  const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now())
  const tick = () => {
    if (!currentBgm) { bgmDuckRaf = 0; return }
    const now = (typeof performance !== 'undefined' ? performance.now() : Date.now())
    const e = now - t0
    if (e < holdMs) { currentBgm.volume = floor }
    else {
      const k = Math.min(1, (e - holdMs) / releaseMs)
      const s = k * k * (3 - 2 * k)   // smoothstep でじわっと戻す
      currentBgm.volume = floor + (target - floor) * s
      if (k >= 1) { bgmDuckRaf = 0; return }
    }
    bgmDuckRaf = requestAnimationFrame(tick)
  }
  bgmDuckRaf = requestAnimationFrame(tick)
}

// パリィ音切替フラグ (P キーで warm ⇄ cold)
// 2026-06-10 ユーザー実機判定: cold(冷たい銀・鋼) を本番 default に確定。
//   warm(13層合成) は参考に残置(§2.1)。神聖儀式v2 はパリィには「軽すぎ」と判定→致命とどめ専用へ。
let parryColdEnabled = true
export function toggleParryCold(): boolean { parryColdEnabled = !parryColdEnabled; return parryColdEnabled }
export function playParryCombo(cold: boolean, intensity = 1, isJust = false): void {
  if (!engine) return
  const t = engine.ctx.currentTime
  if (cold) playParryCold(engine, t, intensity, isJust)
  else playParry(engine, t)
}

// =====================================================================
// SFX カタログ (Phase 4)
//
// ✅ fatal_ready — 実装済 (2026-07-27)。 致命が入れられる「間」の合図。
//   崩れた瞬間の1発 (stagger の §⑤) だけだと、 鳴った瞬間に別を見ていたら以後は無音になる。
//   窓は BOSS.staggerMs=1400ms の**状態**なので、 その間に数回だけ置いて「まだ取れる」を保つ。
// ★ TODO (将来): guard_break — 主人公ガード崩れ (現状 hit_player にバンドル)。
// ★ TODO (将来): reprise — 再演 (死亡後の常夜燈音)。設計メモのみで今回は追加しない。
// =====================================================================
export const SFX_CATALOG: Partial<Record<SfxId, SfxSpec>> = {
  parry: {
    bus: 'impact',
    playback: 'hybrid',
    // samples は parrySteelA/C として別管理。proceduralFn (playParryCold) が両方を内包する。
    volume: 0.6,
    pitchVariance: 0,
    reverbSend: 0.4,
    cooldownMs: 30,
    polyphony: 3,
    proceduralFn: (e, t, intensity, isJust) => playParryVoice(e, t, intensity, isJust),
    notes: '2026-10-09 から本番は柳の型 (public/sfx/parry_yanagi.wav)・致命を弾いた時は押し返しの二段 (parry_exec_push.wav)。選び方は playParryVoice → src/parry_sound.ts。読めていない時と開発版の比較 (旧の音) は冷たい銀 (playParryCold)。',
  },
  danger: {
    bus: 'telegraph',
    playback: 'procedural',
    volume: 0.5,
    pitchVariance: 0,
    reverbSend: 0.18,
    cooldownMs: 100,
    polyphony: 2,
    proceduralFn: (e, t) => playDanger(e, t),
    // TODO: 将来サンプル差し替え候補 →
    //   samples: ['danger_creak.wav']  // 息が詰まる+舞台が軋む冷たい警告音。電子アラーム・サイレン禁止。
    notes: '赤危険予兆専用。息が詰まる+舞台軋み。現状=合成 placeholder。将来 danger_creak.wav へ差し替え予定。',
  },
  stagger: {
    bus: 'impact',
    playback: 'procedural',
    volume: 0.8,
    pitchVariance: 0.05,
    reverbSend: 0.5,
    cooldownMs: 80,
    polyphony: 2,
    proceduralFn: (e, t) => playStaggerCrystal(e, t),
    // TODO: public/sfx/candidates/stagger_snap_a-d.mp3 との比較後に hybrid/sample 昇格検討
    notes: '体幹ブレイク。低く割れて重く崩れる+報酬感あり。candidates/stagger_snap との比較後に hybrid 昇格予定。',
  },
  fatal_ready: {
    bus: 'impact',
    playback: 'procedural',
    // 崩し音 (volume 0.8) よりはっきり下。 1戦で何十回も出るので、 主張しすぎると疲れる。
    volume: 0.34,
    pitchVariance: 0.02,
    reverbSend: 0.42,
    cooldownMs: 200,
    polyphony: 2,
    proceduralFn: (e, t) => playFatalReady(e, t),
    notes: '致命可能の「間」の合図。崩れ中に2回 (main.ts が 380ms / 860ms で鳴らす)。冷たい小ベル+短い吸い込み。',
  },
  hit_player: {
    bus: 'impact',
    playback: 'procedural',
    volume: 0.8,
    pitchVariance: 0,
    reverbSend: 0.28,
    cooldownMs: 40,
    polyphony: 2,
    proceduralFn: (e, t) => playHitPlayer(e, t),
    notes: '主人公被弾。焦る・詰まる・危ない。報酬感なし。guard_break は将来分離予定 (TODO)。',
  },
  execute: {
    bus: 'impact',
    playback: 'procedural',
    volume: 0.9,
    pitchVariance: 0,
    reverbSend: 0.48,
    cooldownMs: 0,
    polyphony: 1,
    proceduralFn: (e, t) => playExecute(e, t),
    notes: '致命開始の衝撃音。execute_hit1/2/3 は別エントリで個別発火。静かな合図は fatal_ready に分離予定 (TODO)。',
  },
  dodge: {
    bus: 'movement',
    playback: 'hybrid',
    samples: ['dodge'],
    volume: 0.75,
    pitchVariance: 0,
    reverbSend: 0.05,
    cooldownMs: 50,
    polyphony: 2,
    proceduralFn: (e, t) => playStep(e, t),
    notes: '回避 whoosh。採用 WAV=dodge.mp3。未ロード時は playStep (石床) でフォールバック。',
  },
  hit_boss: {
    bus: 'impact',
    playback: 'procedural',
    volume: 0.7,
    pitchVariance: 0.06,
    reverbSend: 0.22,
    cooldownMs: 40,
    polyphony: 3,
    proceduralFn: (e, t) => playHitBoss(e, t),
    notes: 'レイピアが当たる。冷たい FM 金属+体への重み+城の残響。',
  },
  hit_boss_blade_hide_shallow: {
    bus: 'impact',
    playback: 'hybrid',
    samples: ['hit_boss_contact'],
    volume: 0.15,
    pitchVariance: 0,
    reverbSend: 0.025,
    cooldownMs: 40,
    polyphony: 3,
    proceduralFn: (e, t) => playHitBladeHideFallback(e, t),
    notes: '王系への浅い刺突。専用生成した接触音から高域共鳴と長い尾を除き、短い物理接触だけを残す。通常命中では鎖・鐘・魔法音を鳴らさず、残響も最小。',
  },
  hit_boss_blade_hide_deep: {
    bus: 'impact',
    playback: 'hybrid',
    samples: ['hit_boss_contact'],
    volume: 0.15,
    pitchVariance: 0,
    reverbSend: 0.025,
    cooldownMs: 40,
    polyphony: 3,
    proceduralFn: (e, t) => playHitBladeHideDeepFallback(e, t),
    sampleLayerFn: (e, t) => playHitBladeHideDeepCore(e, t),
    notes: '王系の深い刺突。浅い命中と同じ短い生成素材を芯にし、70ms未満の小さな硬い「トッ」だけ加える。鎖・鐘・湿った肉音・長い低音は禁止。',
  },
  attack_swing_heavy: {
    bus: 'movement',
    playback: 'procedural',
    volume: 0.75,
    pitchVariance: 0.05,
    reverbSend: 0.08,
    cooldownMs: 60,
    polyphony: 2,
    proceduralFn: (e, t) => playSwingHeavy(e, t),
    notes: '連撃の締め (3段目) の踏み込み突き。通常の振りより低く長く、はっきり聞こえる風切り (2026-10-01「最後だけ重い」・09-29「大振りだけはっきり」)。',
  },
  hit_boss_heavy: {
    bus: 'impact',
    playback: 'procedural',
    volume: 0.9,
    pitchVariance: 0.04,
    reverbSend: 0.26,
    cooldownMs: 40,
    polyphony: 3,
    proceduralFn: (e, t) => playHitBossHeavy(e, t),
    notes: '連撃の締め (3段目) の命中。通常の命中 (hit_boss) と同じ音に低い芯を足し、少し大きく。',
  },
  attack_swing: {
    bus: 'movement',
    playback: 'procedural',
    volume: 0.5,
    pitchVariance: 0.08,
    reverbSend: 0.06,
    cooldownMs: 60,
    polyphony: 2,
    proceduralFn: (e, t) => playSwing(e, t),
    notes: 'レイピアが冷たい空気を切る。細く鋭いホイッスル。連打抑制のため cooldownMs=60。',
  },
  telegraph_growl: {
    bus: 'telegraph',
    playback: 'procedural',
    volume: 0.4,
    pitchVariance: 0,
    reverbSend: 0.22,
    cooldownMs: 120,
    polyphony: 1,
    proceduralFn: (e, t) => playTelegraphGrowl(e, t),
    notes: '物理攻撃予兆。舞台が軋む唸り。将来 creakWood/creakHeavy との差し替え比較候補。',
  },
  telegraph_whirr: {
    bus: 'telegraph',
    playback: 'procedural',
    volume: 0.4,
    pitchVariance: 0,
    reverbSend: 0.22,
    cooldownMs: 120,
    polyphony: 1,
    proceduralFn: (e, t) => playTelegraphWhirr(e, t),
    notes: '投擲/魔法予兆。冷たい結晶エネルギーが集まる。',
  },
  telegraph_ghost: {
    bus: 'telegraph',
    playback: 'procedural',
    volume: 0.4,
    pitchVariance: 0,
    reverbSend: 0.22,
    cooldownMs: 120,
    polyphony: 1,
    proceduralFn: (e, t) => playTelegraphGhost(e, t),
    notes: '亡霊(nameless-shade)限定の予兆。獣の唸りでなく冷たい霊気の吐息。チケットG3 (2026-07-05)。',
  },
  telegraph_bell: {
    bus: 'telegraph',
    playback: 'hybrid',
    samples: ['bride_flurry_bell'],
    volume: 0.32,         // ⬜暫定: 0.7→0.32 (ユーザー「うるさすぎる」2026-07-10)。素材が4連ゴン+焼込み残響で元々大きい=他telegraph(0.4)より下げる。実機で再調整。
    pitchVariance: 0,
    reverbSend: 0.12,     // ⬜暫定: 素材に残響を焼き込み済 = ゲーム側送りは薄く (うるささ低減で 0.18→0.12)。
    cooldownMs: 200,
    polyphony: 1,
    proceduralFn: (e, t) => playTelegraphGrowl(e, t),   // 未ロード時フォールバック (予告音が消えない=フェアネス)
    notes: '花嫁 裾乱れ(veilFlurry)限定の ため予告 = 教会の鐘4連(三全音の不協和)。素材=そざい/_inbox/2026-07-10-bride-flurry-bell-telegraph/selected。1.5s(ため1600msに収まる)。volume/reverbSend は⬜暫定(実機耳調整)。',
  },
  bride_veil_snap: {
    bus: 'telegraph',
    playback: 'procedural',
    volume: 0.28,
    pitchVariance: 0,
    reverbSend: 0.12,
    cooldownMs: 240,
    polyphony: 1,
    proceduralFn: (e, t) => playBrideVeilSnap(e, t),
    notes: '花嫁の350ms返し専用。張った絹が短く鳴る補助手掛かり。共通の物理予告より短く細く、パリィ音を覆わない。',
  },
  knight_combo_tension: {
    bus: 'telegraph',
    playback: 'procedural',
    volume: 0.24,
    pitchVariance: 0,
    reverbSend: 0.1,
    cooldownMs: 180,
    polyphony: 1,
    proceduralFn: (e, t) => playKnightComboTension(e, t),
    notes: '騎士の連撃2拍目以降。槍の口金と柄が短く締まる補助手掛かり。共通予告を残して重ね、接触前130ms以内に消える。',
  },
  knight_sweep_scrape: {
    bus: 'impact', playback: 'procedural', volume: 0.64, pitchVariance: 0,
    reverbSend: 0.22, cooldownMs: 100, polyphony: 2,
    proceduralFn: (e, t) => playKnightSweepScrape(e, t),
    notes: '騎士の薙ぎ接触。低い風切りと短い石床の打音。',
  },
  // 前の採用版 L (2026-09-29 昼 ユーザー「Lにする」・夕方に P の振り分けへ差し替え・ラボの prevL で聴ける)。騎士の薙ぎ・跳び下がり薙ぎの当たりの始まり。太い柄の低いブォンが主役・矛先の冷たい銀のシンは小さく。
  //   大きさ・残響は候補と同じ (volume 0.2 = 旧い手続き音と聴感の大きさが同じ)。素材が読めない時は旧い手続き音が鳴る。
  knight_sweep: {
    bus: 'impact', playback: 'hybrid', samples: ['knight_sweep'], volume: 0.2, pitchVariance: 0,
    reverbSend: 0.12, cooldownMs: 100, polyphony: 2,
    proceduralFn: (e, t) => playKnightSweepScrape(e, t),
    notes: '騎士の薙ぎ。設計の意図 = docs/departments/sound/2026-09-28-knight-sweep-sound-design.md §8',
  },
  // 騎士の薙ぎの音の候補 a〜o (2026-09-28/29・DEV の聞き比べ専用。src/knight_sweep_sound.ts)。
  //   素材は層を重ねて書き出した WAV (scripts/render_knight_sweep_sound.py → public/sfx/candidates/・git 外)。
  //   volume 0.2 = 今の版 (上の手続き音) と聴感の大きさ (K特性の近似) が同じになる値。残響も今の版と同じ量。p〜t は KNIGHT_SWEEP_GAIN_DB を上乗せ。
  //   未配置なら今の版が鳴る (フォールバック)。
  ...Object.fromEntries(KNIGHT_SWEEP_CANDIDATES.map(k => [`knight_sweep_${k}`, {
    bus: 'impact', playback: 'hybrid', samples: [`knight_sweep_${k}`], volume: 0.2 * 10 ** ((KNIGHT_SWEEP_GAIN_DB[k] ?? 0) / 20), pitchVariance: 0,
    reverbSend: 0.12, cooldownMs: 100, polyphony: 2,
    proceduralFn: (e: Engine, t: number) => playKnightSweepScrape(e, t),
    notes: `騎士の薙ぎの音の候補 ${k}。設計の意図 = docs/departments/sound/2026-09-28-knight-sweep-sound-design.md`,
  } satisfies SfxSpec])),
  // 騎士の薙ぎ・跳び下がり薙ぎ＝採用版 (09-29 夕方 ユーザー「今日の鳴らし分けに差し替えて」)。P を役ごとに高さ違いの音と音量 (L と同じ大きさ＋役の差) で鳴らす。
  ...Object.fromEntries((Object.keys(KNIGHT_SWEEP_PV) as KnightSweepRole[]).map(role => [`knight_sweep_pv_${role}`, {
    bus: 'impact', playback: 'hybrid', samples: [KNIGHT_SWEEP_PV[role].sample],
    volume: 0.2 * 10 ** ((KNIGHT_SWEEP_PV_MATCH_DB[role] + KNIGHT_SWEEP_PV[role].gainDb) / 20), pitchVariance: 0,
    reverbSend: 0.12, cooldownMs: 100, polyphony: 2,
    proceduralFn: (e: Engine, t: number) => playKnightSweepScrape(e, t),
    notes: `騎士の薙ぎ P の振り分け版・${KNIGHT_SWEEP_PV[role].label}`,
  } satisfies SfxSpec])),
  // 騎士の予告音の候補 (2026-09-29・DEV の聞き比べ専用。src/knight_telegraph_sound.ts)。共通の唸り (telegraph_growl) と同じ予告の通り道・同じ大きさ。
  //   未配置なら共通の唸りが鳴る (予告音が消えない＝公平さ)。
  ...Object.fromEntries(KNIGHT_TELL_SFX_IDS.map(id => [id, {
    bus: 'telegraph', playback: 'hybrid', samples: [id], volume: 0.4, pitchVariance: 0,
    reverbSend: 0.14, cooldownMs: 120, polyphony: 1,
    proceduralFn: (e: Engine, t: number) => playTelegraphGrowl(e, t),
    notes: `騎士の予告音の候補 ${id}。意図 = docs/departments/sound/2026-09-29-knight-telegraph-sound.md`,
  } satisfies SfxSpec])),
  // 戴冠獣の予備動作の声 (2026-10-13・既定 b = 配布版・DEV ?houndVoice=a|off・src/hound_voice_sound.ts)。共通の唸りと同じ予告の通り道。
  //   大きさは WAV に焼いた (騎士の薙ぎから段の差・scripts/render_hound_voice_sfx.py) ので、鳴らす大きさは薙ぎと同じ 0.2。読めていなければ共通の唸り。
  ...Object.fromEntries([...HOUND_VOICE_SFX_IDS, ...AWOKEN_VOICE_SFX_IDS].map(id => [id, {
    bus: 'telegraph', playback: 'hybrid', samples: [id], volume: 0.2, pitchVariance: 0,
    reverbSend: 0.14, cooldownMs: 120, polyphony: 1,
    proceduralFn: (e: Engine, t: number) => playTelegraphGrowl(e, t),
    notes: `戴冠獣・覚醒獣の声 ${id}。記録 = docs/research/2026-10-13-boss-sound-identity.md`,
  } satisfies SfxSpec])),
  // 騎士の固有のとどめ (突進) と弾かれた花嫁 (2026-10-12・src/exec_unique_sound.ts)。ボスの体と得物の音。
  //   大きさは WAV に焼いた (騎士の薙ぎから役の差・scripts/render_exec_unique_sfx.py) ので、鳴らす大きさは薙ぎと同じ 0.2。
  //   走りの一歩は 80ms ごとに来るので連打を止めない (cooldown 40ms・同時 3)。読めていない時は鳴らさない (前の音と同じ)。
  ...Object.fromEntries(EXEC_UNIQUE_SOUND_ROLES.map(role => [execUniqueSfxId(role), {
    bus: role.endsWith('walk') || role.endsWith('stride') || role.endsWith('kick') ? 'movement' : 'impact', playback: 'sample',
    samples: [`${execUniqueSfxId(role)}_b`, execUniqueSfxId(role)], volume: 0.2, pitchVariance: 0,
    reverbSend: role.startsWith('bride') ? 0.1 : 0.12, cooldownMs: 40, polyphony: 3,
    notes: `固有のとどめの音・${role}。記録 = docs/research/2026-10-12-exec-unique-sfx.md`,
  } satisfies SfxSpec])),
  // 請われぬ介錯の技の音 (2026-10-29・src/exec_sound.ts)。大きさは WAV に焼いた (騎士の薙ぎから段の差・scripts/render_exec_sfx.py) ので 0.2。
  //   予告は共通の唸りと同じ予告の通り道 (読めていなければ唸り・砲は機械音)。一歩は読めていなければ共通の足音。ほかは読めていなければ鳴らさない。
  ...Object.fromEntries(EXEC_SOUND_SFX_IDS.map(id => {
    const tell = id.includes('_tell_'), step = id.endsWith('_step')
    const body = step || id.endsWith('_morph') || id.endsWith('_skid')
    const spec: SfxSpec = {
      bus: tell ? 'telegraph' : body ? 'movement' : 'impact',
      playback: tell || step ? 'hybrid' : 'sample', samples: [id], volume: 0.2, pitchVariance: 0,
      reverbSend: 0.12, cooldownMs: step ? 40 : 120, polyphony: step || id.endsWith('_blast') ? 3 : 2,
      ...(tell ? { proceduralFn: (e: Engine, t: number) => (id.endsWith('_tell_cannon') ? playTelegraphWhirr(e, t) : playTelegraphGrowl(e, t)) } : {}),
      ...(step ? { proceduralFn: (e: Engine, t: number) => playStep(e, t) } : {}),
      notes: `請われぬ介錯の技の音 ${id}。記録 = docs/research/2026-10-29-executioner-sfx.md`,
    }
    return [id, spec]
  })),
  // 床技の予告音 a (2026-11-03・src/floor_tell_sound.ts)。大きさは WAV に焼いた (介錯 e の予告と同じ M400 −23dB) ので 0.2。
  //   読めていなければ今の whirr (予告音が消えない)。
  ...Object.fromEntries(FLOOR_TELL_SFX_IDS.map(id => [id, {
    bus: 'telegraph', playback: 'hybrid', samples: [id], volume: 0.2, pitchVariance: 0,
    reverbSend: 0.12, cooldownMs: 120, polyphony: 1,
    proceduralFn: (e: Engine, t: number) => playTelegraphWhirr(e, t),
    notes: `床技の予告音 ${id}。記録 = docs/research/2026-11-03-floor-tell-sfx-a.md`,
  } satisfies SfxSpec])),
  // 鶏・剣舞・軍配者たちの技の音 v1 (2026-10-31・src/boss_sfx_v1.ts)。大きさは WAV に焼いた (介錯の音と同じ物差し・scripts/render_boss_sfx_v1.py) ので 0.2。
  //   予告は予告の通り道・ほかは当たりの通り道。読めていなければ鳴らさない (比較版だけ)。
  ...Object.fromEntries(BOSS_SFX_V1_IDS.map(id => {
    const tell = id.includes('_tell_'), small = id.endsWith('_march') || id.endsWith('_feathers')
    const spec: SfxSpec = {
      bus: tell ? 'telegraph' : 'impact', playback: 'sample', samples: [id], volume: 0.2, pitchVariance: small ? 0.04 : 0,
      reverbSend: 0.12, cooldownMs: small ? 40 : 60, polyphony: small ? 4 : 2,
      notes: `鶏・剣舞・軍配者たちの技の音 v1 ${id}。記録 = docs/research/2026-10-31-boss-sfx-v1.md`,
    }
    return [id, spec]
  })),
  // 剣舞の技の音と拍手 v2 (2026-11-04・src/blade_sfx_v2.ts)。大きさは WAV に焼いた (騎士の薙ぎからの差・scripts/render_blade_sfx_v2.py) ので 0.2。
  //   予告 (暗器を抜く・とどめの手拍子) は予告の通り道・ほかは当たりの通り道。読めていなければ鳴らさない (比較版だけ)。
  ...Object.fromEntries(BLADE_SFX_V2_IDS.map(id => {
    const tell = id.includes('_tell_'), clap = id.endsWith('_clap')
    const spec: SfxSpec = {
      bus: tell ? 'telegraph' : 'impact', playback: 'sample', samples: [id], volume: 0.2, pitchVariance: 0,
      reverbSend: 0.12, cooldownMs: clap ? 120 : 60, polyphony: 2,
      notes: `剣舞の技の音と拍手 v2 ${id}。記録 = docs/research/2026-11-04-blade-sfx.md`,
    }
    return [id, spec]
  })),
  // ボスの致命固有の音と花嫁の抱擁の音 (2026-11-01・src/finisher_sound.ts)。大きさは WAV に焼いた (騎士の薙ぎからの差) ので 0.2。読めていなければ鳴らさない。
  ...Object.fromEntries(FINISHER_SFX_IDS.map(id => [id, {
    bus: id.includes('_hug') ? 'movement' : 'impact', playback: 'sample', samples: [id], volume: 0.2, pitchVariance: 0,
    reverbSend: id.includes('_hug') ? 0.08 : 0.14, cooldownMs: 200, polyphony: 1,
    notes: `致命固有の音 ${id}。記録 = docs/research/2026-11-01-sfx-finisher-all.md`,
  } satisfies SfxSpec])),
  // 会話場面 (ADV) の物音 (2026-10-27・src/adv_sound.ts)。大きさは WAV に焼いた (戦いの音より控えめ) ので 0.2。読めていない時は鳴らさない。
  //   いちばん強い揺れと一緒に鳴る音 (手首に食い込む・鎖が張る・戦いへ踏み込む) だけ当たりの通り道・ほかは体の動きの通り道。
  ...Object.fromEntries(ADV_SFX_IDS.map(id => [id, {
    bus: ADV_IMPACT_SFX.has(id) ? 'impact' : 'movement', playback: 'sample',
    samples: advSoundSamples(id), volume: 0.2, pitchVariance: 0,
    reverbSend: 0.1, cooldownMs: 100, polyphony: 1,
    notes: `会話場面の物音 ${id}。記録 = docs/research/2026-10-27-adv-sfx.md`,
  } satisfies SfxSpec])),
  // ===== 旧・常打ちの刑吏 専用 (2026-08-12退避) =====
  //   break/pick機構は廃止済み。音資産を消さず戻せるようカタログだけ保存し、実行経路からは呼ばない。
  exec_weapon_break: {
    bus: 'impact',
    playback: 'procedural',
    volume: 0.5,
    pitchVariance: 0.04,
    reverbSend: 0.3,
    cooldownMs: 400,
    polyphony: 1,
    proceduralFn: (e, t) => playExecWeaponBreak(e, t),
    notes: '武器が折れた瞬間。乾いた破断→金具が石床へ落ちる余韻。パリィ音の直後に鳴るので、パリィの銀色と被らない帯 (低め・木質) で作る。',
  },
  exec_weapon_pick: {
    bus: 'impact',
    playback: 'procedural',
    volume: 0.42,
    pitchVariance: 0.05,
    reverbSend: 0.34,
    cooldownMs: 400,
    polyphony: 1,
    proceduralFn: (e, t) => playExecWeaponPick(e, t),
    notes: 'ゴミ山から次の道具を引き出した瞬間 = 無防備が終わる合図。break と対になる上行 (break=落ちる/pick=持ち上がる)。',
  },
  ui_move: {
    bus: 'ui',
    playback: 'procedural',
    volume: 0.35,          // ※procedural は catalog volume を読まない = 実効音量は playUiMove の amp。表記は設計値。
    pitchVariance: 0,
    reverbSend: 0.12,
    cooldownMs: 45,        // 自動リピート間隔 120ms より短く = 押しっぱなしの送りは1つずつ鳴る。連打の潰れだけ防ぐ。
    polyphony: 2,
    proceduralFn: (e, t) => playUiMove(e, t),
    notes: 'メニューのカーソル移動。 押した数だけ鳴るので一番小さく短い。 立ち上がりを物質のノイズで作り、 大聖堂IRへ送って戦闘音と同じ石の広間に置く (2026-07-25 世界観リテイク)。 ⬜A/B/C 候補中 (setUiSoundSet)。',
  },
  ui_confirm: {
    bus: 'ui',
    playback: 'procedural',
    volume: 0.45,
    pitchVariance: 0,
    reverbSend: 0.3,
    cooldownMs: 80,
    polyphony: 1,
    proceduralFn: (e, t) => playUiConfirm(e, t),
    notes: 'メニューの決定・階層へ入る。 開く向き (A=息を吸って灯が点る / B=幕と呼び鈴 / C=櫛歯2音)。 ui_cancel と向きで対にする。 ⬜A/B/C 候補中 (setUiSoundSet)。',
  },
  ui_cancel: {
    bus: 'ui',
    playback: 'procedural',
    volume: 0.4,
    pitchVariance: 0,
    reverbSend: 0.22,
    cooldownMs: 80,
    polyphony: 1,
    proceduralFn: (e, t) => playUiCancel(e, t),
    notes: 'メニューの戻る・閉じる。 閉じる向き (A=灯を伏せる / B=幕が落ちる / C=蓋が閉まる)。 決定と取り違えないことが役目。 ⬜A/B/C 候補中 (setUiSoundSet)。',
  },
  ui_choice_appear: {
    bus: 'ui',
    playback: 'procedural',
    volume: 0.5,
    pitchVariance: 0,
    reverbSend: 0.34,
    cooldownMs: 400,       // 1場面に1回しか鳴らない設計なので長めでよい (二重発火の保険)。
    polyphony: 1,
    proceduralFn: (e, t) => playUiChoiceAppear(e, t),
    notes: 'ADVの選択肢が現れ始めた1 tick だけ鳴る (2026-07-26)。 常夜燈の家系のまま「灯が2つ並んで点る」= 問いが立った。 ui_confirm より低く長く、 決定と取り違えないこと。 文章を読まないプレイヤーには音だけが情報源なので、 UI音の中では一番はっきり出す。',
  },
  bride_triple_chime: {
    bus: 'telegraph',
    playback: 'procedural',
    volume: 0.22,
    pitchVariance: 0,
    reverbSend: 0.18,
    cooldownMs: 500,
    polyphony: 1,
    proceduralFn: (e, t) => playBrideTripleChime(e, t),
    notes: '花嫁のワープ3撃専用。再出現中にだけ鳴る短い上行3音。分岐の主手掛かりは構えで、音は補助に留める。',
  },
  rooster_crow_short: {
    bus: 'telegraph',
    playback: 'procedural',
    volume: 0.3,
    pitchVariance: 0,
    reverbSend: 0.12,
    cooldownMs: 400,
    polyphony: 1,
    proceduralFn: (e, t) => playRoosterCrowShort(e, t),
    notes: '仮 (10-30・本番は音の専用セッション)。鶏の鬨ずらし・短い鳴き。「コッ・コッ・コッ」を 450ms おきに3声 (src/rooster_toki.ts)。爪の3連の拍と同じ間で、聞けば弾く拍が分かる。',
  },
  rooster_crow_long: {
    bus: 'telegraph',
    playback: 'procedural',
    volume: 0.3,
    pitchVariance: 0,
    reverbSend: 0.14,
    cooldownMs: 400,
    polyphony: 1,
    proceduralFn: (e, t) => playRoosterCrowLong(e, t),
    notes: '仮 (10-30・本番は音の専用セッション)。鶏の鬨ずらし・長い鳴き。「コケコッコーー」を 1.25 秒で伸ばしてぷつりと止む (src/rooster_toki.ts)。止んだ所が見て押す合図。',
  },
  rooster_frenzy_cluck: {
    bus: 'telegraph',
    playback: 'procedural',
    volume: 0.3,
    pitchVariance: 0,
    reverbSend: 0.12,
    cooldownMs: 0,
    polyphony: 2,
    proceduralFn: (e, t) => playRoosterFrenzyCluck(e, t),
    notes: '鶏の焦り狂い (11-04 から配布版・src/rooster_frenzy.ts)。段0 (0.60 秒)・段1 (0.52 秒) の短い鳴きの「コッ」1声。声ごとに鳴らし、声の間 = 爪の拍。',
  },
  rooster_rush_tick: {
    bus: 'telegraph',
    playback: 'procedural',
    volume: 0.2,
    pitchVariance: 0,
    reverbSend: 0.08,
    cooldownMs: 300,
    polyphony: 1,
    proceduralFn: (e, t) => playRoosterRushTick(e, t),
    notes: '鶏の加速の比較だけ (DEV ?roosterRush・src/rooster_rush.ts)。遅れを取り戻そうと連を詰める頭で、時計の秒針のような乾いた2打「カッカッ」。なぜ速くなったかを知らせる補助で、予告の形は変えない。',
  },
}

// カタログ経由の再生。
//   - catalog に登録されていれば cooldown/polyphony を管理して発火 → true を返す (switch へ fallback しない)
//   - 未登録 / engine 未初期化 → false を返す (caller が switch fallback へ進む)
function playCatalogSfx(id: SfxId, intensity: number, isJust: boolean): boolean {
  const spec = SFX_CATALOG[id]
  if (!spec || !engine) return false

  // cooldown guard
  const nowMs = engine.ctx.currentTime * 1000
  if (spec.cooldownMs > 0) {
    const last = _sfxLastFireMs[id] ?? -Infinity
    if (nowMs - last < spec.cooldownMs) return true  // handled (意図的スキップ)
  }

  // polyphony guard
  const active = _sfxActiveCount[id] ?? 0
  if (active >= spec.polyphony) return true  // handled (意図的スキップ)

  // 発火確定
  _sfxLastFireMs[id] = nowMs
  _sfxActiveCount[id] = active + 1
  const releaseMs = Math.max(spec.cooldownMs + 200, 400)
  if (typeof setTimeout !== 'undefined') {
    setTimeout(() => {
      _sfxActiveCount[id] = Math.max(0, (_sfxActiveCount[id] ?? 1) - 1)
    }, releaseMs)
  }

  const t = engine.ctx.currentTime
  const out = outFor(engine, id)

  // hybrid: sfxSamples を先に試みる。成功時の追加層と、未ロード時fallbackを分離する。
  if (spec.samples && spec.samples.length > 0) {
    for (const name of spec.samples) {
      if (playSample(name, spec.volume, spec.reverbSend, out)) {
        spec.sampleLayerFn?.(out, t, intensity, isJust)
        return true
      }
    }
    // sample 未ロード → proceed to procedural
  }

  // procedural (または hybrid fallback)
  spec.proceduralFn?.(out, t, intensity, isJust)
  return true
}

export function playSfx(id: SfxId, intensity = 1, isJust = false): void {
  if (engine && isPlayerSfx(id)) duckBossMoves(engine)
  // 主人公の致命の新しい版 (DEV ?lunaFinisher=new・src/luna_finisher.ts): 致命の音をこの版の音へ差し替える (無ければ今の音)。
  if (finisherV2On() && playLunaFinisherSfx(id)) return
  if (playLunaAliceSfx(id)) return
  if (playCatalogSfx(id, intensity, isJust)) return
  if (!engine) return
  const t = engine.ctx.currentTime
  const engineMain = engine
  const out = outFor(engineMain, id)
  playSwitchSfx(id, out, t, intensity, isJust)
}

// 主人公の致命の新しい版の音 (2026-10-12・DEV ?lunaFinisher=new だけ読む・表と大きさの決まり = src/luna_finisher.ts LUNA_SFX)。
/** 新しい版の音を読む (開発版の ?lunaFinisher=new の時だけ main.ts が呼ぶ)。 */
export function preloadLunaFinisherSfx(): void {
  fetchSamples(lunaSfxAllSamples())  // 組の比較 (&lunaExecSfx・&lunaFinSfx) でも鳴るよう全部の組を読む
}
// 致命の入れ替えの比較の音 (DEV ?lunaExec=swap・src/luna_exec_swap.ts・書き出し scripts/render_lunaire_alice_sfx.py)。
//   高さを持つ材料・砂嵐なし・6kHz より上は削った。大きさはファイルの側でそろえてある (裂け目 = 今の刺す音・弾ける = それより重い)。
export const LUNA_ALICE_SFX: Partial<Record<SfxId, [string, string, number, number]>> = {
  luna_alice_cuts: ['luna_alice_cuts', '/sfx/luna_alice_cuts.wav?v=1', 1.0, 0.06],
  luna_alice_burst: ['luna_alice_burst', '/sfx/luna_alice_burst.wav?v=1', 1.0, 0.12],
  // 重みを足した版 (?lunaExec=swap3・書き出し scripts/render_lunaire_alice_sfx_v3.py)
  luna_alice_cuts3: ['luna_alice_cuts3', '/sfx/luna_alice_cuts3.wav?v=1', 1.0, 0.06],
  luna_alice_burst3: ['luna_alice_burst3', '/sfx/luna_alice_burst3.wav?v=1', 1.0, 0.12],
  luna_exec_weight: ['luna_exec_weight', '/sfx/luna_exec_weight.wav?v=1', 1.0, 0.06],
}
export function preloadLunaAliceSfx(): void {
  fetchSamples(Object.values(LUNA_ALICE_SFX).map(v => [v![0], v![1]] as [string, string]))
}
function playLunaAliceSfx(id: SfxId): boolean {
  const v = LUNA_ALICE_SFX[id]
  if (!v) return false
  if (engine) playSample(v[0], v[2], v[3], engine)
  return true
}
function playLunaFinisherSfx(id: SfxId): boolean {
  const spec = LUNA_SFX[id]
  if (!spec) return id === 'finisher_pull' || id === 'finisher_name'
  if (!engine) return true
  // 未ロードなら今の音へ (新しい版だけの音は鳴らさない)
  if (!playSample(spec.sample, 10 ** (spec.gainDb / 20), spec.reverb, outFor(engine, id))) return id === 'finisher_pull' || id === 'finisher_name'
  return true
}

function playSwitchSfx(id: SfxId, engine: Engine, t: number, intensity: number, isJust: boolean): void {
  switch (id) {
    case 'parry': return playParryVoice(engine, t, intensity, isJust)
    case 'hit_boss':        return playHitBoss(engine, t)
    case 'hit_player':      return playHitPlayer(engine, t)
    case 'attack_swing':    return playSwing(engine, t)
    case 'attack_swing_heavy': return playSwingHeavy(engine, t)
    case 'hit_boss_heavy':  return playHitBossHeavy(engine, t)
    case 'guard':           return playGuard(engine, t)
    case 'guard_player':    return playGuardPlayer(engine, t)
    case 'step':            return playStep(engine, t)
    case 'dodge':           { if (!playSample('dodge', 0.75, 0.05, engine)) playStep(engine, t); return }
    case 'stagger':         return playStaggerCrystal(engine, t)
    case 'fatal_ready':     return playFatalReady(engine, t)
    case 'execute':         return playExecute(engine, t)
    case 'execute_soul':    return playExecuteSoul(engine, t)
    case 'execute_hit1':    return playExecuteHit1(engine, t)
    case 'execute_hit2':    return playExecuteHit2(engine, t)
    case 'execute_hit3':    return playExecuteHit3(engine, t)
    case 'telegraph_start': return playTelegraphStart(engine, t)
    case 'telegraph_growl': return playTelegraphGrowl(engine, t)
    case 'telegraph_whirr': return playTelegraphWhirr(engine, t)
    case 'telegraph_ghost': return playTelegraphGhost(engine, t)
    case 'bride_veil_snap': return playBrideVeilSnap(engine, t)
    case 'bride_triple_chime': return playBrideTripleChime(engine, t)
    case 'rooster_rush_tick': return playRoosterRushTick(engine, t)
    case 'rooster_frenzy_cluck': return playRoosterFrenzyCluck(engine, t)
    case 'rooster_crow_short': return playRoosterCrowShort(engine, t)
    case 'rooster_crow_long': return playRoosterCrowLong(engine, t)
    case 'knight_combo_tension': return playKnightComboTension(engine, t)
    case 'exec_weapon_break': return playExecWeaponBreak(engine, t)
    case 'exec_weapon_pick':  return playExecWeaponPick(engine, t)
    case 'perfect_dodge':   return playPerfectDodge(engine, t)
    case 'counter_hit':     return playCounterHit(engine, t)
    case 'danger':          return playDanger(engine, t)
    case 'ui_move':         return playUiMove(engine, t)
    case 'ui_confirm':      return playUiConfirm(engine, t)
    case 'ui_cancel':       return playUiCancel(engine, t)
    case 'ui_choice_appear': return playUiChoiceAppear(engine, t)
  }
}

export function playPending(ids: SfxId[], parryIntensity = 1, parryIsJust = false, parryIsExec = false, parryPitch?: ParryPitchHit): void {
  // 同じ瞬間の束にプレイヤーの音があれば、束を鳴らす前に下げる (ボスの振りの出だしから引く)。
  if (engine && shouldDuckFor(ids)) duckBossMoves(engine)
  // 致命を弾いた時の音の候補は1本で完結 = 一緒に積まれたガードの音を外す (候補なし = 今のまま弾き＋ガード)。
  const hasParry = ids.includes('parry')
  const plan = hasParry ? planParrySound({ exec: parryIsExec, intensity: parryIntensity, isJust: parryIsJust }) : null
  // 候補の WAV が未ロードなら今の音 (弾き＋ガード) へ戻すので、ガードは外さない。
  const dropGuard = !!plan && plan.kind === 'sample' && plan.dropGuard && sampleReady(plan.sample)
  parryHitIsExec = hasParry && parryIsExec
  parryHitPitch = hasParry ? parryPitch : undefined
  try {
    for (const id of ids) {
      if (dropGuard && id === 'guard') continue
      bypassBossDuck = keepsBossHitUp(ids, id)
      playSfx(id, id === 'parry' ? parryIntensity : 1, id === 'parry' ? parryIsJust : false)
      bypassBossDuck = false
    }
  } finally {
    bypassBossDuck = false
    parryHitIsExec = false
    parryHitPitch = undefined
  }
}

/** dev 専用: 弾きの音を単体で鳴らす (聞き比べ・録音用)。候補は ?parrySound= 等と同じ planParrySound を通す。 */
export function __devPlayParry(opts: { exec: boolean; intensity: number; isJust: boolean; pitch?: ParryPitchHit }): void {
  ensureAudio()
  playPending(opts.exec ? ['parry', 'guard'] : ['parry'], opts.intensity, opts.isJust, opts.exec, opts.pitch)
}

/** DEV suri-ashi: restrained steel sole on stone, through the boss duck bus. */
export function playKnightSuriProcedural(kind: 'glide' | 'draw', durationMs: number): void {
  if (!engine) return
  const e = engine
  const ctx = e.ctx
  const t = ctx.currentTime
  const dur = kind === 'glide' ? Math.max(0.05, durationMs / 1000) : 0.035
  const sampleCount = Math.ceil(ctx.sampleRate * dur)
  const buffer = ctx.createBuffer(1, sampleCount, ctx.sampleRate)
  const data = buffer.getChannelData(0)
  for (let i = 0; i < sampleCount; i++) data[i] = Math.random() * 2 - 1
  const noise = ctx.createBufferSource()
  noise.buffer = buffer
  const filter = ctx.createBiquadFilter()
  filter.type = 'bandpass'
  filter.frequency.value = kind === 'glide' ? 1300 : 700
  filter.Q.value = 0.8
  const gain = ctx.createGain()
  const peak = kind === 'glide' ? 0.024 : 0.015
  gain.gain.setValueAtTime(0.0001, t)
  gain.gain.linearRampToValueAtTime(peak, t + Math.min(0.025, dur / 3))
  gain.gain.setValueAtTime(peak * 0.65, t + dur * 0.55)
  gain.gain.linearRampToValueAtTime(0.0001, t + dur)
  noise.connect(filter).connect(gain).connect(e.bossDuck)
  noise.start(t)
  noise.stop(t + dur)
}

// ===== dev 試聴用 (fxlab): 崩し・軋みの候補を鳴らし比べる =====
export function playStaggerCandidate(which: 'current' | 'crystal'): void {
  if (!engine) return
  const t = engine.ctx.currentTime
  if (which === 'crystal') playStaggerCrystal(engine, t)
  else playStagger(engine, t)
}
// ※亡霊 telegraph の候補比較 (合成A/B/C + 実録body D/E/F/G) は 2026-07-14 に A 採用で撤去。
//   本番再生は playSfx('telegraph_ghost')。 実録body路線に戻る時は
//   scripts/ghost_breath_render.py と そざい/_inbox/2026-07-14-ghost-telegraph/ から再生成できる。
export function playCreakCandidate(which: 'wood' | 'heavy' | 'chain' | 'current'): void {
  if (!engine) return
  const t = engine.ctx.currentTime
  switch (which) {
    case 'wood':    return playCreakWood(engine, t)
    case 'heavy':   return playCreakHeavy(engine, t)
    case 'chain':   return playCreakChain(engine, t)
    case 'current': return playTelegraphGrowl(engine, t)
  }
}
// ※見切り/回避の合成版(playMikiriCandidate/playDodgeCandidate)は 2026-06-09 撤去。
//   合成osc/FMは「安っぽい」と判定 → プリレンダーWAV主役へピボット(se-design-tracker §0)。
//   試聴は fxlab が public/sfx/candidates/*.mp3 を直接再生する。

// =====================================================================
// ★★★ パリィ音 (13レイヤ・SEKIROグレード超え) ★★★
// =====================================================================
function playParry(e: Engine, t: number): void {
  // --- 1. PRE-IMPACT CLICK (0-15ms) "カチッ" ---
  // 高周波の極短クリック。脳に「今だ」と教える瞬発
  envOsc(e, t, 'square', 5200, { amp: 0.55, attack: 0.0005, decay: 0.015, reverb: 0.05 })
  envOsc(e, t, 'square', 3800, { amp: 0.50, attack: 0.0005, decay: 0.020, reverb: 0.05 })

  // --- 2. METALLIC BELLS (5-300ms) "キーーン" ---
  // FMベル2セル (高音域 + 中音域) で複合金属音
  fmBell(e, t + 0.003, {
    carrierFreq: 1320, modFreq: 1650,         // ratio 1.25 = inharmonic
    modDepth: 1200, modDecay: 0.08,
    amp: 0.50, attack: 0.003, decay: 0.30, reverb: 0.35,
  })
  fmBell(e, t + 0.005, {
    carrierFreq: 760, modFreq: 950,           // ratio 1.25 = inharmonic
    modDepth: 800, modDecay: 0.12,
    amp: 0.48, attack: 0.004, decay: 0.30, reverb: 0.35,
  })
  // リング変調2セル (低音域 + 中音域) で金属の非調和厚み
  ringMod(e, t + 0.005, {
    freqA: 260, freqB: 173,  // sum=433, diff=87 (inharmonic)
    amp: 0.45, attack: 0.003, decay: 0.18, reverb: 0.30,
  })
  ringMod(e, t + 0.008, {
    freqA: 1100, freqB: 743, // sum=1843, diff=357 (inharmonic)
    amp: 0.35, attack: 0.003, decay: 0.22, reverb: 0.30,
  })

  // --- 3. SUB THUMP (0-200ms) "ドゥンッ" ---
  // 物理的な「重さ」。サブ2層
  envOsc(e, t, 'sine', 55, { amp: 0.85, attack: 0.002, decay: 0.20, reverb: 0.10 })
  envOsc(e, t + 0.002, 'sine', 90, { amp: 0.55, attack: 0.002, decay: 0.12, reverb: 0.05 })

  // --- 4. NOISE CRASH (0-150ms) "シャッ" ---
  // 中高域のワイドなクラッシュ。空気が裂ける感じ
  envNoise(e, t, 0.05, 0.50, { type: 'bandpass', frequency: 3500, Q: 1 }, 0.20)
  envNoise(e, t, 0.12, 0.35, { type: 'highpass', frequency: 800 }, 0.30)

  // --- 5. BELL TAILS (25-650ms) "ンーーー..." ---
  // ピッチが下がりながら消える鳴き。2本重ねて厚みを出す
  const tail1 = envOsc(e, t + 0.025, 'sine', 1320, {
    amp: 0.40, attack: 0.020, decay: 0.55, reverb: 0.70,
  })
  pitchBend(tail1.osc, t + 0.025, 1320, 1050, 0.50)
  const tail2 = envOsc(e, t + 0.050, 'sine', 660, {
    amp: 0.35, attack: 0.025, decay: 0.65, reverb: 0.75,
  })
  pitchBend(tail2.osc, t + 0.050, 660, 540, 0.60)

  // --- 6. SHIMMER (15-200ms) "キラッ" ---
  // 高い倍音の輝き
  envOsc(e, t + 0.015, 'triangle', 4500, {
    amp: 0.28, attack: 0.005, decay: 0.18, reverb: 0.35,
  })
}

// =====================================================================
// 🔔 パリィ音「冷たい銀のレイピア」確定版
//   軽い弾き → A(細い銀)主役で細く鋭い / 重い弾き → C(芯あり)が前に出て体重が乗る
// =====================================================================
// isJust=true: 完全に弾いた。高く澄んだ銀+明るいシマー。
// isJust=false: 受けた/いなした。やや低く鈍め。
//
// 🔵 2026-10-09: 本番の弾きの音は「柳の型」へ替わった (ユーザーが聞き比べて採用・src/parry_sound.ts)。この冷たい銀は
//   DEV ?parrySound=now の比較と、採用の WAV が読めていない時の代わりとして残す。下の 07-26 の記録はこの音についての物。
// 🔴 2026-07-26 ユーザー裁定: **この音は変えない。** 通常とジャストの書き分けを足す案は不採用。
//   発端は友達の実プレイFB「ガード : パリィ : ジャストパリィ = 1 : 2 : 2.5 に感じる」だったが、
//   そう言ったのは3人中1人だけで、一番上手い人を含む他2人は違和感なしだった。
//   経緯と却下した案の一覧 = docs/candidates/2026-07-26-parry-just-audio-split-verify-ticket.md
//
//   ★ただし調査で出た事実は残す価値があるので書いておく (将来また指摘が出た時の出発点):
//     ① 変更前 = 現在の音は、**通常パリィ(504ms)の方がジャスト(418ms)より長く鳴っている**。
//        原因は通常の playbackRate ×0.90 (下記 pitchMul)。ジャストは ×1.12 で速く鳴り終わる。
//        = 「完璧に弾いた」方が音として短い。直さないと決めた今も、これは事実。
//     ② §2「縫い留める鎖」/ §4「テイル」の decay・残響送り (reverbMul) をいくら削っても
//        **音は変わらない** (余韻帯 0.15-0.60s の mean が -27.9dB → -28.0dB)。
//        余韻の正体は鋼サンプル本体の尾 (>2kHz が支配的)。ここを触っても効かない。
//     ③ 効かせるなら鋼サンプルの gain を fade で落とす必要がある (実測で -39.0 → -44.4dB)。
//     ⚠️ 測る窓を間違えると②を「効いた」と誤判定する。**尾の後半 0.28-0.60s** で測ること。
//        計器 = scripts/record_parry_normal_levels.mjs (__devCaptureStream で実出力を録る)
// cut (gb1〜gb3 だけ・10-16): sweep = 音程が滑る層 (下がる slam・上がる soul) を外す / pureLow = 低い純音 (220Hz・72Hz) を外す。
//   bells (開発版 ?sfxRoots= だけ・10-17) = FM の鐘 (鎖 3040Hz・ジャストの 4400/2800Hz) を外す。
//   soul (gs だけ・10-18) = sweep で外していても「上がる尾」だけは戻し、この倍率を掛ける。
// tone (gh・gk だけ・10-18・src/parry_remake.ts ParryTone) = 芯の高さ・高い所の削り・鐘の強さ。無ければ今のまま。
function playParryCold(e: Engine, t: number, intensity = 1, isJust = false, voice?: ParryMaterialVoice,
  cut?: { sweep?: boolean; pureLow?: boolean; bells?: boolean; soul?: number }, tone?: ParryTone): void {
  const w = Math.max(0.25, Math.min(1, intensity))
  // 材質の声 (?parrySfx=f だけ・無ければ今のまま)
  const tp = tone?.pitch ?? 1
  const vRate = (voice?.steelRate ?? 1) * tp, vSteel = voice?.steelGain ?? 1, vBell = (voice?.bellMul ?? 1) * 10 ** ((tone?.bellDb ?? 0) / 20)
  const vTail = voice?.tailMul ?? 1, vCore = voice?.coreMul ?? 1

  // --- 1. 芯: C(芯あり)メイン + A(細い銀)を軽い弾きで混ぜる ---
  // ★可愛さ調整ノブ: PARRY_COLD_HISHELF_DB を 0(元の明るい)〜 -10(暗め) で動かす。
  const PARRY_COLD_HISHELF_DB = tone?.shelfDb ?? voice?.shelfDb ?? -6
  // ★全体音量ノブ(2026-06-10): 0.6 確定。
  const G = 0.6
  // ジャスト: サンプルを高く (+12%)、通常: 低く (-10%) → ピッチ差で直感的に判別できる。
  const pitchMul = isJust ? 1.12 : 0.90
  // ジャスト: リバーブ豊か(澄んで響く) / 通常: 控えめ(近く鈍い)
  const reverbMul = isJust ? 1.2 : 0.82
  const coldCut = e.ctx.createBiquadFilter()
  coldCut.type = 'highshelf'
  coldCut.frequency.value = 5200
  coldCut.gain.value = PARRY_COLD_HISHELF_DB
  coldCut.connect(e.comp)
  const bufC = parrySteelC
  const bufA = parrySteelA
  if (bufC) {
    const src = e.ctx.createBufferSource()
    src.buffer = bufC
    src.playbackRate.value = (0.75 - 0.08 * w) * pitchMul * vRate
    const g = e.ctx.createGain()
    g.gain.value = (0.28 + 0.42 * w) * G * vSteel
    src.connect(g).connect(coldCut)
    routeReverb(e, g, (0.18 + 0.22 * w) * reverbMul)
    src.start(t)
  }
  if (bufA) {
    const src = e.ctx.createBufferSource()
    src.buffer = bufA
    src.playbackRate.value = (0.88 - 0.04 * w) * pitchMul * vRate
    const g = e.ctx.createGain()
    g.gain.value = Math.max(0, 0.42 - 0.38 * w) * G * vSteel
    src.connect(g).connect(coldCut)
    routeReverb(e, g, 0.12 * reverbMul)
    src.start(t)
  }
  // スナップ (高域の刺さり・脳汁 / ジャスト: 鋭く / 通常: 少し弱め)
  const snapMul = isJust ? 1.0 : 0.75
  envNoise(e, t, 0.006, (0.34 + 0.28 * w) * G * snapMul, { type: 'highpass', frequency: 3000 }, 0.03)
  // 開発版 ?sfxRoots=plain|bare: 520Hz の三角波の「コッ」(高さのある短い鳴り) を細い帯の雑音へ
  if (sfxRoots === 'off') envOsc(e, t, 'triangle', 520 * tp, { amp: (0.20 + 0.22 * w) * G * snapMul, attack: 0.0004, decay: 0.014, reverb: 0.02 })
  else envNoise(e, t, 0.014, (0.20 + 0.22 * w) * G * snapMul, { type: 'bandpass', frequency: 700 * tp, Q: 1.0 }, 0.02)
  if (!bufC && !bufA) {
    envNoise(e, t, 0.014, 0.5 * (0.6 + 0.6 * w) * G * snapMul, { type: 'highpass', frequency: 4500 }, 0.06)
  }

  // --- 2. 縫い留める鎖 ---
  //   ⚠️ ここの decay/reverb を通常だけ削る案は実測で無効だったため入れていない (上のコメント参照)。
  if (vBell > 0 && !cut?.bells) fmBell(e, t + 0.004, {
    carrierFreq: 3040 * 1.004, modFreq: 3040 * 1.004 * 2.76, modDepth: 1400, modDecay: 0.06,
    amp: 0.09 * (0.7 + 0.5 * w) * G * vBell, attack: 0.002, decay: 0.30, reverb: 0.4,
  })

  // --- 3. 衝撃コア ---
  if (!cut?.pureLow) envOsc(e, t, 'sine', 220, { amp: (0.60 + 0.35 * w) * G * vCore, attack: 0.001, decay: 0.07 + 0.12 * w, reverb: 0 })
  envNoise(e, t, 0.06 + 0.08 * w, (0.55 + 0.38 * w) * G * vCore, { type: 'bandpass', frequency: 380 * tp, Q: 0.8 }, 0)
  if (!cut?.sweep) {
    const slam = envOsc(e, t, 'sine', 160, { amp: (0.45 + 0.50 * w) * G * vCore, attack: 0.001, decay: 0.10 + 0.18 * w, reverb: 0.03 })
    pitchBend(slam.osc, t, 160, 45, 0.08 + 0.10 * w)
  }
  if (!cut?.pureLow) envOsc(e, t, 'sine', 72, { amp: w * w * 0.70 * G * vCore, attack: 0.002, decay: 0.18 + 0.12 * w, reverb: 0.04 })

  // --- 4. テイル ---
  if (vTail > 0 && (!cut?.sweep || cut.soul !== undefined)) {
    const soul = envOsc(e, t + 0.028, 'sine', 1600, { amp: (0.05 + 0.06 * w) * G * vTail * (cut?.soul ?? 1), attack: 0.018, decay: 0.38, reverb: 0.55 })
    pitchBend(soul.osc, t + 0.028, 1600, 2400, 0.38)
  }

  // --- 5. ジャスト専用: 高域の澄んだシマーベル (+クリスタル感) ---
  //   🔴 2026-07-26 裁定により**ここは変更前の値のまま**。ジャストを強化して差を作る方向は撤回。
  //      もし将来また書き分けをやるなら、**通常側だけを引く**形で作ること (ジャストは基準点として動かさない)。
  //      効かせられるのは鋼サンプルの gain を fade で落とす経路だけ (上の③)。ここを触りたくなったら上を読む。
  if (isJust && vBell > 0 && !cut?.bells) {
    fmBell(e, t + 0.012, {
      carrierFreq: 4400, modFreq: 4400 * 1.5, modDepth: 900, modDecay: 0.04,
      amp: 0.055 * G * vBell, attack: 0.003, decay: 0.28, reverb: 0.60,
    })
    fmBell(e, t + 0.032, {
      carrierFreq: 2800, modFreq: 2800 * 2.1, modDepth: 560, modDecay: 0.06,
      amp: 0.040 * G * vBell, attack: 0.006, decay: 0.34, reverb: 0.55,
    })
  }
}

// ガキンの2層 (ga1〜ga3 だけ・10-16)。ゼンゼロの普通のパリィは出だしで 1.2〜16kHz が一度に跳ね、200ms 後も高い所が残る
//   (docs/research/2026-10-15-zzz-parry-gakin-layers.md)。e はそこが 30dB ほど弱く「ポィン」と柔らかい。
//   ガ = 1.2〜12kHz の広い打撃・数 ms で立ち、30ms ほどで消える。
//   キン = 倍音の並びが合わない金属の鳴りを数本 (2〜10kHz)・0.2〜0.3 秒で消える。きれいな和音にしない (10-14「演奏すぎる」)。
//   k = 層の強さ (ga3 = 1)。重さ w で少しだけ太く。p = 高さに掛ける (gh・gk だけ・10-18)。
function playParryGakin(e: Engine, t: number, w: number, k: number, p = 1): void {
  const G = 1.5 * k
  playParryGa(e, t, w, k, p)
  // キン: 合わない金属の鳴り。近い2本を少しずらしてうならせる
  const rate = (1.03 - 0.06 * w) * p
  for (const [f, a, d] of [[2310, 0.10, 0.30], [2347, 0.07, 0.26], [3630, 0.085, 0.24], [5170, 0.065, 0.21], [7420, 0.05, 0.18], [9860, 0.035, 0.15]] as const) {
    envOsc(e, t + 0.002, 'sine', f * rate, { amp: a * G, attack: 0.0015, decay: d, reverb: 0.12 })
  }
}

// ガだけ (開発版 ?sfxRoots=bare はキンを抜いてこれだけ鳴らす): 広い帯の打撃 (中心 3.5kHz の広い山 + 6kHz より上の刺さり)
function playParryGa(e: Engine, t: number, w: number, k: number, p = 1): void {
  const G = 1.5 * k
  envNoise(e, t, 0.032, (0.85 + 0.25 * w) * G, { type: 'bandpass', frequency: 3500 * p, Q: 0.32 }, 0.04)
  envNoise(e, t, 0.022, (0.55 + 0.15 * w) * G, { type: 'highpass', frequency: 6000 }, 0.03)
  envNoise(e, t + 0.001, 0.04, (0.45 + 0.2 * w) * G, { type: 'bandpass', frequency: 1700 * p, Q: 0.6 }, 0.02)
}

// 雑音のドン (gb2・gb3 だけ・10-16)。低い純音の代わりに、低い帯へ絞った雑音で「ド」を作る。音程を持たないので「ぽよん」と鳴らない。
//   ゼンゼロの普通のパリィは 35〜110Hz に広く山が立つ (1本の音程ではない)。
//   k = 強さに掛ける (gh2 だけ控える・10-18)。
function playParryThud(e: Engine, t: number, w: number, k = 1): void {
  const G = 0.6 * k
  envNoise(e, t, 0.16 + 0.08 * w, (9.0 + 4.5 * w) * G, { type: 'lowpass', frequency: 110, Q: 0.6 }, 0.02)
  envNoise(e, t, 0.09 + 0.05 * w, (4.0 + 2.2 * w) * G, { type: 'bandpass', frequency: 75, Q: 0.9 }, 0)
  envNoise(e, t, 0.05, (1.4 + 0.6 * w) * G, { type: 'bandpass', frequency: 180, Q: 0.8 }, 0)
}

// 余韻を足した比較版 (開発版 ?parryTail= だけ・10-19 作り直し・docs/research/2026-10-19-zzz-parry-tail-content.md)。
//   金属の板の響き = 弾いた音そのもの (trim の出口) を 2.4〜6.8kHz に切り、雑音の残響＋線8本の響きの元 (IR) へ通して master へ出す。
//   正弦を直接鳴らさない (10-18 の ring は「鐘の音を足したよう」で不採用)。唸り (plateLow) = 35〜75Hz の雑音を長く残す。
//   続けて弾いた時は前の余韻の出口を 40ms で 0 にして外す (重ねない)。
function playParryTail(e: Engine, dry: AudioNode, t: number, plan: ParryTailPlan): void {
  const ctx = e.ctx
  cutParryTail(ctx, t)
  const out = ctx.createGain()
  out.gain.value = 1
  out.connect(e.master)
  const hp = ctx.createBiquadFilter()
  hp.type = 'highpass'; hp.frequency.value = 2400; hp.Q.value = Math.SQRT1_2
  const lp = ctx.createBiquadFilter()
  lp.type = 'lowpass'; lp.frequency.value = 6800; lp.Q.value = Math.SQRT1_2
  const conv = ctx.createConvolver()
  conv.buffer = parryPlateIr(ctx, plan.irSec, plan.scale)
  const send = ctx.createGain()
  send.gain.value = plan.sendGain
  dry.connect(hp)
  hp.connect(lp).connect(conv).connect(send).connect(out)
  let rumble: AudioBufferSourceNode | null = null
  if (plan.rumble !== null) {
    rumble = ctx.createBufferSource()
    rumble.buffer = parryRumbleBuffer(ctx)
    const r1 = ctx.createBiquadFilter(), r2 = ctx.createBiquadFilter()
    r1.type = 'highpass'; r1.frequency.value = 35; r1.Q.value = Math.SQRT1_2
    r2.type = 'lowpass'; r2.frequency.value = 75; r2.Q.value = Math.SQRT1_2
    const rg = ctx.createGain()
    rg.gain.value = plan.rumble
    rumble.connect(r1).connect(r2).connect(rg).connect(out)
    rumble.start(t)
  }
  const tail: ParryTailVoice = {
    out,
    stop: () => {
      try { dry.disconnect(hp) } catch { /* 既に外れている */ }
      try { rumble?.stop() } catch { /* 既に止まっている */ }
      try { out.disconnect() } catch { /* 既に外れている */ }
    },
  }
  lastParryTail = tail
  // 響きが消えたら外す
  const life = Math.max(plan.irSec, plan.rumble !== null ? PARRY_RUMBLE_SEC : 0) + 0.3
  setTimeout(() => { if (lastParryTail === tail) lastParryTail = null; tail.stop() }, (t - ctx.currentTime + life) * 1000)
}
type ParryTailVoice = { out: GainNode; stop: () => void }
let lastParryTail: ParryTailVoice | null = null
function cutParryTail(ctx: AudioContext, t: number): void {
  const prev = lastParryTail
  if (!prev) return
  lastParryTail = null
  const at = Math.max(t, ctx.currentTime)
  prev.out.gain.cancelScheduledValues(at)
  prev.out.gain.setValueAtTime(prev.out.gain.value, at)
  prev.out.gain.linearRampToValueAtTime(0, at + 0.04)
  setTimeout(prev.stop, (at - ctx.currentTime + 0.06) * 1000)
}
// 響きの元 (左右2本)。雑音の残響 (消えるまで 2.6 秒・立ち上がり 4ms) に、3.2〜6.2kHz の不揃いな線8本を足す。
//   線は低いほど長く残る (ゼンゼロの余韻の線の減り方の並び)。左右で 6 割を共有し (ゼンゼロの左右の相関 0.63)、線の強さと位相は左右で別々。
//   ConvolverNode の normalize (既定) で量をそろえる。長さと減り方の倍率ごとに1つ作って使い回す。
const PARRY_PLATE_MODES: readonly (readonly [number, number, number])[] = [[3260, 1.0, 3.2], [3470, 0.8, 2.6], [3710, 0.9, 2.2], [4130, 0.6, 1.8], [4390, 0.7, 1.6], [4760, 0.45, 1.3], [5480, 0.4, 1.0], [6230, 0.35, 0.9]]
const PARRY_PLATE_WASH_T60 = 2.6
const PARRY_PLATE_MODE_GAIN = 10 ** (8 / 20) * 0.02
const PARRY_PLATE_COMMON = 0.6
const parryPlateIrs = new Map<string, AudioBuffer>()
function parryPlateIr(ctx: AudioContext, sec: number, scale: number): AudioBuffer {
  const sr = ctx.sampleRate
  const key = `${sr}:${sec}:${scale}`
  const got = parryPlateIrs.get(key)
  if (got) return got
  const n = Math.floor(sec * sr)
  const buf = ctx.createBuffer(2, n, sr)
  const share = new Float32Array(n)
  for (let i = 0; i < n; i += 1) share[i] = Math.random() * 2 - 1
  const cs = Math.sqrt(PARRY_PLATE_COMMON), ci = Math.sqrt(1 - PARRY_PLATE_COMMON)
  for (let c = 0; c < 2; c += 1) {
    const d = buf.getChannelData(c)
    const modes = PARRY_PLATE_MODES.map(([f, a, t60]) => ({ w: 2 * Math.PI * f / sr, g: PARRY_PLATE_MODE_GAIN * a * 10 ** ((Math.random() * 6 - 3) / 20), ph: Math.random() * 2 * Math.PI, k: -6.91 / (t60 * scale * sr) }))
    const kw = -6.91 / (PARRY_PLATE_WASH_T60 * scale * sr)
    for (let i = 0; i < n; i += 1) {
      const att = 1 - Math.exp(-i / (0.004 * sr))
      let y = (cs * share[i] + ci * (Math.random() * 2 - 1)) * Math.exp(kw * i)
      for (const m of modes) y += m.g * Math.sin(m.w * i + m.ph) * Math.exp(m.k * i)
      d[i] = y * att
    }
  }
  parryPlateIrs.set(key, buf)
  return buf
}
// 唸りの元 (左右同じ・1本を使い回す)。立ち上がり 30ms・消えるまで 3 秒の雑音。帯域は鳴らす時に 35〜75Hz へ切る。
const PARRY_RUMBLE_SEC = 3.0
const parryRumbles = new Map<number, AudioBuffer>()
function parryRumbleBuffer(ctx: AudioContext): AudioBuffer {
  const sr = ctx.sampleRate
  const got = parryRumbles.get(sr)
  if (got) return got
  const n = Math.floor(PARRY_RUMBLE_SEC * sr)
  const buf = ctx.createBuffer(1, n, sr)
  const d = buf.getChannelData(0)
  for (let i = 0; i < n; i += 1) {
    const x = i / sr
    d[i] = (Math.random() * 2 - 1) * (1 - Math.exp(-x / 0.03)) * Math.exp(-6.91 * x / PARRY_RUMBLE_SEC)
  }
  parryRumbles.set(sr, buf)
  return buf
}

// 唸りが主役の余韻 (開発版 ?parryTail=hum|humLine|humSway だけ・10-20・docs/research/2026-10-20-parry-tail-hum.md)。
//   唸り = 雑音を 48Hz の山へ2回絞った物 (38〜60Hz・音程を持たない・ゼンゼロの余韻の芯)。50ms で立ち上がり、parryHumT60 で消える。
//   唸りは鳴らしっぱなしの1本 (ParryHumBus) を使い回す。続けて弾いた時 (1.2 秒以内) は重ねも鳴らし直しもせず、その1本を
//     n 打目の量 (1 打目の 0.5 倍 → 0.35 倍) へ 0.1 秒で寄せるだけ。連打のあいだ唸りは小さく続き、最後の一打のあと消えていく。
//     10-19 は一打ごとに前の余韻を 40ms で切って新しく鳴らし直したので、連打すると余韻がずっと満タンで鳴り続けた (「まだうるさい」)。
//   humLine = 弾いた音そのもの (trim の出口) を 2.4〜6.8kHz に切って、細い線2本だけの響きの元へ通す (雑音の残響は入れない)。
//   humSway = 唸りの大きさを ゆっくり (2.6Hz) 揺らす。出口は master (板の響きと同じ・押しつぶしを通さない)。
//   humCore・humW・humM (10-21)：弾いた瞬間に低い芯 (playParryHumCore) を鳴らし、唸りは 15ms で山へ上がって芯から続く。
//     humW・humM の揺れは一打ごとに決め直す (parryHumSwayOf)。揺れない一打では揺れを 0.15 秒でほどく。
function playParryHum(e: Engine, dry: AudioNode, t: number, kind: ParryHumTail, w: number, material: ParryMaterial): void {
  const ctx = e.ctx
  const plan = PARRY_HUM_PLAN[kind]
  let bus = parryHumBus
  if (bus && (bus.kind !== kind || bus.ctx !== ctx)) { stopParryHum(ctx, t); bus = null }
  const n = bus ? parryHumChainIndex(bus.lastAt, bus.n, t) : 0
  if (!bus || n === 0) {
    if (bus) stopParryHum(ctx, t)
    bus = makeParryHumBus(e, kind)
    parryHumBus = bus
  }
  const tau = parryHumT60(plan, w) / 6.91
  const env = bus.env.gain
  if (bus.swayGain && bus.swayDepth && bus.lfo) {
    // 揺れの深さと速さを、この一打の物へ寄せる (揺れない一打は深さ 0 = ただの唸り)
    const sw = parryHumSwayOf(plan, w, material)
    const d = sw?.depth ?? 0, mk = parryHumSwayMakeup(d)
    const k = n === 0 ? 0.005 : 0.05
    bus.swayGain.gain.setTargetAtTime((1 - d / 2) * mk, t, k)
    bus.swayDepth.gain.setTargetAtTime((d / 2) * mk, t, k)
    if (sw) bus.lfo.frequency.setTargetAtTime(sw.hz, t, k)
  }
  if (plan.core !== null) playParryHumCore(e, t, parryHumCoreT60(w, plan), parryHumCoreLevel(plan, w, n))
  if (n === 0) {
    const peak = parryHumLevel(plan, w, 0)
    const at = t + plan.attackSec
    env.cancelScheduledValues(t)
    env.setValueAtTime(0, t)
    env.linearRampToValueAtTime(peak, at)
    env.setTargetAtTime(0, at, tau)
    bus.lvl = peak; bus.at = at; bus.tau = tau
  } else {
    // 唸りを足し直さない：鳴っている1本を、n 打目の量 (1 打目より小さい) へ 0.1 秒で寄せ、そこからまた消えていく
    const now = parryHumAt(bus, t)
    const to = parryHumLevel(plan, w, n)
    const at = t + PARRY_HUM_CHAIN.rampSec
    env.cancelScheduledValues(t)
    env.setValueAtTime(now, t)
    env.linearRampToValueAtTime(to, at)
    env.setTargetAtTime(0, at, tau)
    bus.lvl = to; bus.at = at; bus.tau = tau
  }
  bus.lastAt = t; bus.n = n
  if (bus.conv) {
    // 細い線：一打ごとに送り、n 打目は減らす。前の線は切らない
    const hp = ctx.createBiquadFilter()
    hp.type = 'highpass'; hp.frequency.value = 2400; hp.Q.value = Math.SQRT1_2
    const lp = ctx.createBiquadFilter()
    lp.type = 'lowpass'; lp.frequency.value = 6800; lp.Q.value = Math.SQRT1_2
    const send = ctx.createGain()
    send.gain.value = parryHumLineLevel(plan, w, n)
    dry.connect(hp)
    hp.connect(lp).connect(send).connect(bus.conv)
    setTimeout(() => { try { dry.disconnect(hp) } catch { /* 既に外れている */ } try { send.disconnect() } catch { /* 既に外れている */ } }, (t - ctx.currentTime + 1.0) * 1000)
  }
  // 鳴り終わったら止める (次の一打が来ていたら延びる)
  const life = bus.at - ctx.currentTime + bus.tau * 6.91 + 0.3
  const mine = bus
  if (mine.timer) clearTimeout(mine.timer)
  mine.timer = setTimeout(() => { if (parryHumBus === mine) parryHumBus = null; mine.stop() }, Math.max(life, PARRY_HUM_LINE_SEC + 0.3) * 1000)
}
type ParryHumBus = {
  kind: ParryHumTail; ctx: AudioContext; env: GainNode; out: GainNode; conv: ConvolverNode | null
  swayGain: GainNode | null; swayDepth: GainNode | null; lfo: OscillatorNode | null
  lastAt: number | null; n: number; lvl: number; at: number; tau: number
  timer: ReturnType<typeof setTimeout> | null; stop: () => void
}
let parryHumBus: ParryHumBus | null = null
function parryHumAt(bus: ParryHumBus, t: number): number {
  return t <= bus.at ? bus.lvl : bus.lvl * Math.exp(-(t - bus.at) / bus.tau)
}
function makeParryHumBus(e: Engine, kind: ParryHumTail): ParryHumBus {
  const ctx = e.ctx
  const plan = PARRY_HUM_PLAN[kind]
  const out = ctx.createGain()
  out.gain.value = 1
  out.connect(e.master)
  const src = ctx.createBufferSource()
  src.buffer = parryHumNoise(ctx)
  src.loop = true
  const b1 = ctx.createBiquadFilter(), b2 = ctx.createBiquadFilter()
  b1.type = 'bandpass'; b1.frequency.value = PARRY_HUM_HZ; b1.Q.value = PARRY_HUM_Q
  b2.type = 'bandpass'; b2.frequency.value = PARRY_HUM_HZ; b2.Q.value = PARRY_HUM_Q
  const env = ctx.createGain()
  env.gain.value = 0
  src.connect(b1).connect(b2).connect(env)
  let lfo: OscillatorNode | null = null
  let swayGain: GainNode | null = null, swayDepth: GainNode | null = null
  if (plan.rule === 'weight' || plan.rule === 'material') {
    // 技で揺れを変える案：揺れの層を常に通し、深さは一打ごとに playParryHum が決める (始めは揺れなし)
    swayGain = ctx.createGain(); swayGain.gain.value = 1
    lfo = ctx.createOscillator(); lfo.frequency.value = 3
    swayDepth = ctx.createGain(); swayDepth.gain.value = 0
    lfo.connect(swayDepth).connect(swayGain.gain)
    env.connect(swayGain).connect(out)
    lfo.start()
  } else if (plan.swayHz !== null) {
    // 揺れ：大きさを (1 - d/2) を中心に ±d/2 で揺らす
    const sway = ctx.createGain()
    sway.gain.value = 1 - plan.swayDepth / 2
    lfo = ctx.createOscillator()
    lfo.frequency.value = plan.swayHz
    const depth = ctx.createGain()
    depth.gain.value = plan.swayDepth / 2
    lfo.connect(depth).connect(sway.gain)
    env.connect(sway).connect(out)
    lfo.start()
  } else env.connect(out)
  let conv: ConvolverNode | null = null
  if (plan.lineGain !== null) {
    conv = ctx.createConvolver()
    conv.normalize = false
    conv.buffer = parryHumLineIr(ctx)
    conv.connect(out)
  }
  src.start(0, Math.random() * 2)
  const bus: ParryHumBus = {
    kind, ctx, env, out, conv, swayGain, swayDepth, lfo, lastAt: null, n: 0, lvl: 0, at: 0, tau: 1, timer: null,
    stop: () => {
      if (bus.timer) clearTimeout(bus.timer)
      try { src.stop() } catch { /* 既に止まっている */ }
      try { lfo?.stop() } catch { /* 既に止まっている */ }
      try { out.disconnect() } catch { /* 既に外れている */ }
    },
  }
  return bus
}
/** 唸りを 40ms で消して外す (板の響きへ切り替えた時・余韻を外した時・別の唸りへ替えた時)。 */
function stopParryHum(ctx: AudioContext, t: number): void {
  const bus = parryHumBus
  if (!bus) return
  parryHumBus = null
  if (bus.ctx !== ctx) { bus.stop(); return }
  const at = Math.max(t, ctx.currentTime)
  bus.out.gain.cancelScheduledValues(at)
  bus.out.gain.setValueAtTime(bus.out.gain.value, at)
  bus.out.gain.linearRampToValueAtTime(0, at + 0.04)
  if (bus.timer) clearTimeout(bus.timer)
  bus.timer = setTimeout(bus.stop, (at - ctx.currentTime + 0.06) * 1000)
}
// 低い芯 (humCore・humW・humM)：雑音を 62Hz の山へ2回絞った物 (45〜90Hz・音程を持たない)。6ms で立ち上がり、重いほど長く残る。
//   量は重さの段ごとに上がる (parryHumCoreLevel)。唸りと同じく master へ出す (押しつぶしで弾いた音を揺らさない)。
function playParryHumCore(e: Engine, t: number, t60: number, level: number): void {
  if (level <= 0) return
  const ctx = e.ctx
  const src = ctx.createBufferSource()
  src.buffer = parryHumNoise(ctx)
  const b1 = ctx.createBiquadFilter(), b2 = ctx.createBiquadFilter()
  b1.type = b2.type = 'bandpass'
  b1.frequency.value = b2.frequency.value = PARRY_HUM_CORE_HZ
  b1.Q.value = b2.Q.value = PARRY_HUM_CORE_Q
  const g = ctx.createGain()
  g.gain.setValueAtTime(0, t)
  g.gain.linearRampToValueAtTime(level * PARRY_HUM_CORE_GAIN, t + 0.006)
  g.gain.setTargetAtTime(0, t + 0.006, t60 / 6.91)
  src.connect(b1).connect(b2).connect(g).connect(e.master)
  src.start(t, Math.random() * 1.5)
  src.stop(t + t60 + 0.1)
}
const PARRY_HUM_CORE_HZ = 62
const PARRY_HUM_CORE_Q = 1.4
const PARRY_HUM_CORE_GAIN = 14
const PARRY_HUM_HZ = 48
const PARRY_HUM_Q = 3
// 唸りの元の雑音 (2 秒・左右同じ・繰り返して鳴らす)
const parryHumNoises = new Map<number, AudioBuffer>()
function parryHumNoise(ctx: AudioContext): AudioBuffer {
  const sr = ctx.sampleRate
  const got = parryHumNoises.get(sr)
  if (got) return got
  const buf = ctx.createBuffer(1, Math.floor(2 * sr), sr)
  const d = buf.getChannelData(0)
  for (let i = 0; i < d.length; i += 1) d[i] = Math.random() * 2 - 1
  parryHumNoises.set(sr, buf)
  return buf
}
// 細い線2本だけの響きの元 (humLine)。ゼンゼロの余韻で一番長く残る 3.2kHz 近くと、ほとんど減らない 4.9kHz 近くの線。
//   雑音の残響 (板の響きの「反響」) は入れない。線の長さは板の響き (3.2 秒) より短い。左右で位相だけ変える。
//   normalize を切る。線ごとの山は「線の高さの音を通した時に PARRY_HUM_LINE_GAIN × a 倍」になるよう線の長さで割る。
const PARRY_HUM_LINES: readonly (readonly [number, number, number])[] = [[3260, 1.0, 1.4], [4890, 0.55, 1.8]]
const PARRY_HUM_LINE_SEC = 1.8
const PARRY_HUM_LINE_GAIN = 16
const parryHumLineIrs = new Map<number, AudioBuffer>()
function parryHumLineIr(ctx: AudioContext): AudioBuffer {
  const sr = ctx.sampleRate
  const got = parryHumLineIrs.get(sr)
  if (got) return got
  const n = Math.floor(PARRY_HUM_LINE_SEC * sr)
  const buf = ctx.createBuffer(2, n, sr)
  for (let c = 0; c < 2; c += 1) {
    const d = buf.getChannelData(c)
    const lines = PARRY_HUM_LINES.map(([f, a, t60]) => ({ w: 2 * Math.PI * f / sr, g: PARRY_HUM_LINE_GAIN * a * 2 / (t60 / 6.91 * sr), ph: c * Math.PI * 0.37 + a, k: -6.91 / (t60 * sr) }))
    for (let i = 0; i < n; i += 1) {
      const att = 1 - Math.exp(-i / (0.004 * sr))
      let y = 0
      for (const l of lines) y += l.g * Math.sin(l.w * i + l.ph) * Math.exp(l.k * i)
      d[i] = y * att
    }
  }
  parryHumLineIrs.set(sr, buf)
  return buf
}

// 弾いた瞬間の低い押し (gb4 系だけ・10-17)。95Hz より下の雑音の塊が 35ms 遅れてふくらみ、半秒ほどで消える。
//   音程を持たないので「ぽよん」と鳴らない。耳で決める4問で「押しの強い版」が選ばれた形 (docs/research/2026-10-17-zzz-parry-ear4-candidates.md)。
function playParryPush(e: Engine, t: number, w: number, k: number): void {
  const sr = e.ctx.sampleRate, delay = 0.035, att = 0.03, t60 = 0.5
  const len = Math.floor(sr * t60)
  const buf = e.ctx.createBuffer(1, len, sr)
  const data = buf.getChannelData(0)
  for (let i = 0; i < len; i += 1) {
    const x = i / sr
    data[i] = (Math.random() * 2 - 1) * (1 - Math.exp(-x / att)) * Math.exp(-x * 6.91 / t60)
  }
  const src = e.ctx.createBufferSource()
  src.buffer = buf
  // 4次のバターワース (12dB/oct を2段)
  const f1 = e.ctx.createBiquadFilter(), f2 = e.ctx.createBiquadFilter()
  f1.type = f2.type = 'lowpass'
  f1.frequency.value = f2.frequency.value = 95
  f1.Q.value = 0.54; f2.Q.value = 1.31
  const g = e.ctx.createGain()
  g.gain.value = (PARRY_PUSH_GAIN + 0.5 * PARRY_PUSH_GAIN * w) * k
  src.connect(f1).connect(f2).connect(g).connect(e.comp)
  src.start(t + delay)
}
// 押しの基準の量 (gb4 の pushDb=0)。ゲームの出力を録って決める。
const PARRY_PUSH_GAIN = 7.2

// 直した版 f の材質だけの層 (10-15・src/parry_material_voice.ts)。合成だけで鳴らす・どれも 0.4 秒以内に消える。
function playParryMaterialLayer(e: Engine, t: number, v: ParryMaterialVoice, w: number): void {
  const G = 0.6 * v.layerGain
  switch (v.layer) {
    case 'wood': // 柄・蹴り = 木と体の鈍い「ゴッ」
      envNoise(e, t, 0.035, (0.45 + 0.3 * w) * G, { type: 'bandpass', frequency: 850, Q: 1.3 }, 0.02)
      envOsc(e, t, 'triangle', 340, { amp: (0.30 + 0.2 * w) * G, attack: 0.0006, decay: 0.035, reverb: 0.02 })
      envOsc(e, t, 'sine', 140, { amp: (0.45 + 0.3 * w) * G, attack: 0.001, decay: 0.08, reverb: 0 })
      break
    case 'ironRing': { // 枷・鎖・車輪 = 合わない低い鉄の鳴り + 鎖の粒
      for (const [f, a] of [[412, 0.11], [587, 0.08], [893, 0.05]] as const) {
        envOsc(e, t + 0.002, 'sine', f * (1.04 - 0.08 * w), { amp: a * G, attack: 0.001, decay: 0.32, reverb: 0.12 })
      }
      for (let i = 0; i < 4; i += 1) {
        envNoise(e, t + 0.018 + i * 0.027 + Math.random() * 0.006, 0.008, (0.16 - i * 0.03) * G, { type: 'bandpass', frequency: 4200 + i * 300, Q: 2.5 }, 0.05)
      }
      break
    }
    case 'silk': // 薄衣 = 布の擦れ + 細い銀
      envNoise(e, t + 0.003, 0.09, 0.14 * G, { type: 'bandpass', frequency: 2600, Q: 0.7 }, 0.06)
      fmBell(e, t + 0.006, { carrierFreq: 5400, modFreq: 5400 * 1.41, modDepth: 600, modDecay: 0.03,
        amp: 0.045 * G, attack: 0.002, decay: 0.20, reverb: 0.35 })
      break
    case 'air': { // 砲の弾・衝撃波 = 物は鳴らず空気の圧「ボフッ」
      envNoise(e, t, 0.13, (0.55 + 0.3 * w) * G, { type: 'lowpass', frequency: 320 }, 0.04)
      const p = envOsc(e, t, 'sine', 90, { amp: (0.5 + 0.3 * w) * G, attack: 0.004, decay: 0.16, reverb: 0.03 })
      pitchBend(p.osc, t, 90, 38, 0.14)
      envNoise(e, t + 0.01, 0.06, 0.12 * G, { type: 'highpass', frequency: 1800 }, 0.04)
      break
    }
    case 'gold': // 金の爪と牙 = やわらかく短い金 + 爪の擦れ
      fmBell(e, t + 0.002, { carrierFreq: 1760, modFreq: 1760, modDepth: 500, modDecay: 0.03,
        amp: 0.07 * G, attack: 0.002, decay: 0.13, reverb: 0.15 })
      envNoise(e, t + 0.004, 0.045, 0.16 * G, { type: 'bandpass', frequency: 3200, Q: 2 }, 0.04)
      break
    case 'broad': // 幅広の黒い刃 = 低く長めの鋼
      fmBell(e, t + 0.003, { carrierFreq: 1150 * (1.04 - 0.08 * w), modFreq: 1150 * 2.76, modDepth: 700, modDecay: 0.08,
        amp: 0.08 * G, attack: 0.002, decay: 0.40, reverb: 0.25 })
      break
    case 'tip': // 槍の穂先 = 細く高い先の鳴り
      fmBell(e, t + 0.002, { carrierFreq: 3900, modFreq: 3900 * 3.1, modDepth: 800, modDecay: 0.03,
        amp: 0.05 * G, attack: 0.001, decay: 0.18, reverb: 0.2 })
      break
    case 'none':
      break
  }
}

// =====================================================================
// その他のSE
// =====================================================================

// レイピアが当たる: 灰の王城=冷たい銀の刃が鎧を噛む音 (2026-06-20 redesign v3: 世界観優先)
//   v3: 「冷たい銀」のため fmBell 復活 (純粋な silver bell パレット)。
//   構造: 60Hzサブ (鎧の体重) + 100Hz body (鎧のクラング) + friction + 銀のベル + 高域 click + わずかな影
function playHitBoss(e: Engine, t: number): void {
  if (sfxRoots !== 'off') { playHitBossPlain(e, t); return }
  // 60Hzサブ: 鎧の体重 (decay 延長)
  envOsc(e, t, 'sine', 60, { amp: 0.65, attack: 0.001, decay: 0.12, reverb: 0.04 })
  // body: 100Hz 鎧のクラング (ブースト・decay 延長)
  envOsc(e, t, 'sine', 100, { amp: 0.88, attack: 0.001, decay: 0.14, reverb: 0.08 })
  // thud: 180Hz 「ズシン」の中低域 body (重さの核)
  envOsc(e, t, 'sine', 180, { amp: 0.58, attack: 0.001, decay: 0.13, reverb: 0.06 })
  // mid friction: 銀の刃が鎧の表面を切り裂く感触 (dur 延長)
  envNoise(e, t, 0.070, 0.62, { type: 'bandpass', frequency: 500, Q: 0.9 }, 0.10)
  // 冷たい銀の主鐘 (amp 控えめ=body 優位に)
  fmBell(e, t + 0.001, {
    carrierFreq: 2400, modFreq: 2400 * 2.76, modDepth: 1100, modDecay: 0.04,
    amp: 0.26, attack: 0.0008, decay: 0.11, reverb: 0.18,
  })
  // 高域の銀の影 (控えめ)
  fmBell(e, t + 0.004, {
    carrierFreq: 4800, modFreq: 4800 * 1.41, modDepth: 700, modDecay: 0.03,
    amp: 0.13, attack: 0.001, decay: 0.07, reverb: 0.15,
  })
  // 即時 click (presence・控えめ)
  envNoise(e, t, 0.016, 0.42, { type: 'highpass', frequency: 3000 }, 0.04)
}

// 命中の「楽器の層を抜いた版」(開発版 ?sfxRoots= だけ・10-17)。60/100/180Hz の正弦 (高さの決まった「ぽん」) を
//   低い帯の雑音の塊に、2400/4800Hz の FM の鐘を細い帯の雑音に替える。擦れ (500Hz) とクリックは同じ。
function playHitBossPlain(e: Engine, t: number): void {
  envNoise(e, t, 0.13, 3.32, { type: 'lowpass', frequency: 160 }, 0.05)
  envNoise(e, t, 0.11, 1.42, { type: 'bandpass', frequency: 180, Q: 0.8 }, 0.06)
  envNoise(e, t, 0.070, 0.29, { type: 'bandpass', frequency: 500, Q: 0.9 }, 0.10)
  envNoise(e, t + 0.001, 0.05, 0.13, { type: 'bandpass', frequency: 2400, Q: 1.5 }, 0.15)
  envNoise(e, t, 0.016, 0.20, { type: 'highpass', frequency: 3000 }, 0.04)
}

// 連撃の締めの命中: 通常の命中に、踏み込みの体重が乗った低い芯を足す。
function playHitBossHeavy(e: Engine, t: number): void {
  playHitBoss(e, t)
  if (sfxRoots !== 'off') envNoise(e, t, 0.18, 0.9, { type: 'lowpass', frequency: 90 }, 0.05)
  else envOsc(e, t, 'sine', 48, { amp: 0.55, attack: 0.002, decay: 0.18, reverb: 0.05 })
  envNoise(e, t + 0.004, 0.05, 0.30, { type: 'lowpass', frequency: 320 }, 0.06)
}

// 実素材がまだdecodeされていない最初の発音だけを救う、乾いた短い代替音。
// 通常経路の音色はHキーで選んだ実素材候補が担う。
function playHitBladeHideFallback(e: Engine, t: number): void {
  envNoise(e, t, 0.009, 0.30, { type: 'highpass', frequency: 2600 }, 0)
  envNoise(e, t + 0.003, 0.024, 0.32, { type: 'bandpass', frequency: 1500, Q: 1.2 }, 0)
}

// 深い命中だけに足す硬い芯。大剣の重量や骨折音ではなく、厚い抵抗の奥へ届いた短い「トッ」。
function playHitBladeHideDeepCore(e: Engine, t: number): void {
  const body = envOsc(e, t + 0.004, 'triangle', 190, { amp: 0.07, attack: 0.001, decay: 0.060, reverb: 0 })
  pitchBend(body.osc, t + 0.004, 205, 165, 0.050)
}

function playHitBladeHideDeepFallback(e: Engine, t: number): void {
  playHitBladeHideFallback(e, t)
  playHitBladeHideDeepCore(e, t)
}

// カウンター: 灰の王城=銀の刃が深く噛んだ報酬 (2026-06-20 redesign v3: 世界観優先)
//   v3: hit_boss と同じ「冷たい銀のベル」パレットを継承しつつ、より深く・より美しく。
//   構造: 55Hzサブ + 80Hz body + friction + 銀の主鐘 (decay長め・わずか cathedral) + 銀の高域影 + 高域 click
//   見せ場性: cathedralAmt 微量で「呪われた舞台に銀の音が一瞬残響する」体感
function playCounterHit(e: Engine, t: number): void {
  // 55Hzサブ: 決定打の体重 (ブースト・decay 延長)
  envOsc(e, t, 'sine', 55, { amp: 0.70, attack: 0.001, decay: 0.18, reverb: 0.06 })
  // body: hit_boss より厚い決定打 (ブースト・decay 延長)
  envOsc(e, t, 'sine', 80, { amp: 1.00, attack: 0.001, decay: 0.20, reverb: 0.10 })
  // thud: 160Hz 「ガッ」の中低域 body (決定打の核)
  envOsc(e, t, 'sine', 160, { amp: 0.62, attack: 0.001, decay: 0.16, reverb: 0.08 })
  // mid friction: 刺さる手応え (dur 延長・ブースト)
  envNoise(e, t, 0.090, 0.76, { type: 'bandpass', frequency: 600, Q: 0.8 }, 0.12)
  // 冷たい銀の主鐘 (控えめ=body 優位・微量 cathedral 維持)
  fmBell(e, t + 0.001, {
    carrierFreq: 2400, modFreq: 2400 * 2.76, modDepth: 1200, modDecay: 0.05,
    amp: 0.34, attack: 0.001, decay: 0.18, reverb: 0.22, cathedralAmt: 0.10,
  })
  // 銀の高域影 (控えめ)
  fmBell(e, t + 0.004, {
    carrierFreq: 4800, modFreq: 4800 * 1.41, modDepth: 800, modDecay: 0.03,
    amp: 0.18, attack: 0.001, decay: 0.10, reverb: 0.18,
  })
  // 高域click (presence・控えめ)
  envNoise(e, t, 0.022, 0.50, { type: 'highpass', frequency: 3000 }, 0.06)
}

// 見切り (時が止まる): ① BGMを一瞬しぼって世界が息を止める(Witch Time) ② 採用WAVが主役
//   ③ 静寂を切り裂く「一筋の光」を薄く重ねる。
//   2026-06-20 redesign: shimmer の高音感をやや抑制 (2600→2200Hz・decay 0.20→0.12)。
//   WAV (mikiri.mp3) はユーザー指定で触らない=fallback 合成も最小変更。
function playPerfectDodge(e: Engine, t: number): void {
  duckBgmForMikiri()  // ① 世界がふっと静まる
  if (playSample('mikiri', 0.65, 0.1)) {
    // ③ 静寂を切り裂く一筋の光 (やや低めに・短く=高音感抑制)
    envOsc(e, t + 0.02, 'triangle', 2200, { amp: 0.14, attack: 0.004, decay: 0.12, reverb: 0.45 })
    return
  }
  envNoise(e, t, 0.10, 0.20, { type: 'bandpass', frequency: 2400, Q: 2.0 }, 0.35)
  envOsc(e, t, 'sine', 82, { amp: 0.36, attack: 0.004, decay: 0.20, reverb: 0.18 })
  fmBell(e, t + 0.012, {
    carrierFreq: 950, modFreq: 950 * 2.76, modDepth: 620, modDecay: 0.10,
    amp: 0.26, attack: 0.006, decay: 0.42, reverb: 0.62,
  })
  const sh = envOsc(e, t + 0.008, 'sine', 1300, { amp: 0.26, attack: 0.022, decay: 0.48, reverb: 0.60 })
  pitchBend(sh.osc, t + 0.008, 1200, 1900, 0.42)
}

// 被弾: 灰の王城=磁器人形 LUNAIRE が打たれる音 (2026-06-20 redesign v3: 世界観優先)
//   v3: 「磁器が割れる + 鎖が震える」=LUNAIRE が doll であることを音で表現。
//   構造: 55Hzサブ + 90Hz body (人形の木芯) + 古布 + 磁器の痛みベル + 鎖の震え + 衝撃の頭
function playHitPlayer(e: Engine, t: number): void {
  // === 鈍い被弾: 低中域メイン・高域カット・近接インターバルで脈打つ鈍痛 ===
  // 40Hz sine subsonic (深い weight・sawtooth でないので滑らか)
  envOsc(e, t, 'sine', 40, { amp: 0.70, attack: 0.001, decay: 0.32, reverb: 0.16 })
  // 75Hz body triangle (倍音あり・pitch bend 沈む=鈍痛が深まる)
  const body = envOsc(e, t, 'triangle', 75, { amp: 1.00, attack: 0.001, decay: 0.30, reverb: 0.20 })
  pitchBend(body.osc, t, 75, 52, 0.26)
  // 95Hz triangle ビーティング相手 (75との近接インターバル=ヒューンと脈打つ鈍痛)
  envOsc(e, t, 'triangle', 95, { amp: 0.60, attack: 0.001, decay: 0.26, reverb: 0.18 })
  // 150Hz mid body (厚み)
  envOsc(e, t, 'sine', 150, { amp: 0.52, attack: 0.001, decay: 0.22, reverb: 0.12 })
  // 250Hz body 芯 (鈍い肉感)
  envOsc(e, t, 'sine', 250, { amp: 0.42, attack: 0.001, decay: 0.18, reverb: 0.10 })
  // 低域 200Hz 不協和パートナー (250との minor 3rd 近接=低域の不快な共鳴=鈍痛)
  envOsc(e, t, 'sine', 200, { amp: 0.40, attack: 0.001, decay: 0.16, reverb: 0.10 })
  // lowpass 600Hz noise burst (ザラ感ゼロの dull thud=鈍い塊感・dur 長く・amp 限界)
  envNoise(e, t, 0.18, 0.95, { type: 'lowpass', frequency: 600, Q: 0.5 }, 0.18)
  // 350Hz bandpass crunch (中域の鈍い当たり)
  envNoise(e, t, 0.10, 0.55, { type: 'bandpass', frequency: 350, Q: 0.6 }, 0.14)
  // 衝撃の頭 (低めの click=タイミング合図のみ・高域はカット)
  envNoise(e, t, 0.018, 0.36, { type: 'bandpass', frequency: 700, Q: 0.8 }, 0.08)
}

// レイピアが冷たい空気を切る: 細く鋭いホイッスル
function playSwing(e: Engine, t: number): void {
  envNoise(e, t, 0.070, 0.24, { type: 'bandpass', frequency: 3400, Q: 2.8 }, 0.04)
  const sw = envOsc(e, t, 'sine', 700, { amp: 0.12, attack: 0.002, decay: 0.065, reverb: 0.05 })
  pitchBend(sw.osc, t, 560, 1700, 0.065)
}

// 連撃の締め: 同じレイピアの風切りを、低い帯域の空気の押しと長めの尾で太くする。
function playSwingHeavy(e: Engine, t: number): void {
  envNoise(e, t, 0.110, 0.32, { type: 'bandpass', frequency: 2200, Q: 1.6 }, 0.06)
  envNoise(e, t, 0.090, 0.20, { type: 'bandpass', frequency: 700, Q: 1.0 }, 0.04)
  const sw = envOsc(e, t, 'sine', 520, { amp: 0.16, attack: 0.003, decay: 0.10, reverb: 0.07 })
  pitchBend(sw.osc, t, 420, 1400, 0.10)
}

// 受け (ガード): 刃が鎧/武器で弾かれる「カッ/キン」。 ③⑤ 命中 (hit_boss=低音ドスッ) と明確に分ける。
//   設計: 低音 body は出さない (= 通ってない)。 短く硬い金属リング。 パリィ (大きく澄んだ弾き) より
//   小さく鈍く・残響短め = 「受け止められた (動じない)」手応え。 ※音色の最終調整は後日 (ユーザー「音は後で」)。
function playGuard(e: Engine, t: number): void {
  // 1. 硬い接触のクリック (鋼が触れる瞬間)
  envOsc(e, t, 'square', 4200, { amp: 0.26, attack: 0.0004, decay: 0.012, reverb: 0.03 })
  // 2. 非調和な金属リング ×2 (短く鈍め = 鎧で止められた)。開発版 ?sfxRoots= では高さを持たない雑音2本に替える
  if (sfxRoots !== 'off') {
    envNoise(e, t + 0.002, 0.08, 0.75, { type: 'bandpass', frequency: 1500, Q: 1.2 }, 0.16)
    envNoise(e, t + 0.004, 0.10, 0.67, { type: 'bandpass', frequency: 900, Q: 1.0 }, 0.14)
    envNoise(e, t, 0.03, 0.41, { type: 'bandpass', frequency: 2600, Q: 1.4 }, 0.05)
    return
  }
  fmBell(e, t + 0.002, {
    carrierFreq: 1500, modFreq: 1500 * 1.27, modDepth: 760, modDecay: 0.05,
    amp: 0.30, attack: 0.002, decay: 0.12, reverb: 0.16,
  })
  fmBell(e, t + 0.004, {
    carrierFreq: 900, modFreq: 900 * 1.27, modDepth: 520, modDecay: 0.06,
    amp: 0.24, attack: 0.003, decay: 0.14, reverb: 0.14,
  })
  // 3. 刃が逸れる短い擦過ノイズ (glance off)
  envNoise(e, t, 0.03, 0.20, { type: 'bandpass', frequency: 2600, Q: 1.4 }, 0.05)
}

// 主人公のガード成立 (パリィ長押し): パリィ音 (playParryCold) を「受け止め」向けに編集した派生音。
//   ユーザー要望 (2026-07-07): パリィの澄んだ弾き (鳴り切る) を、ドスッと受け止めた鈍い衝撃へ =
//   同じ鋼サンプル (parrySteelC/A) を低く・くぐもらせて金属の鳴き/シマー/スナップを削り、 低域の衝撃を厚くする。
//   「弾いた (鳴らす)」でなく「受けて、止めた (詰める)」。 ※音量/音色の最終調整は後日 (サウンド部)。
function playGuardPlayer(e: Engine, t: number): void {
  const G = 0.78   // 全体音量ノブ (2026-07-07 ユーザー「少し小さい」→ 0.6→0.78 ≈+30%。⬜暫定・本調整は後日)
  // 鋼サンプルを parry (playbackRate 0.75〜) よりさらに低く=重く + 高域を lowpass で削って
  //   金属の高い鳴りを殺す (くぐもった「受け止め」=ドスッと詰まる。 highshelf でなく lowpass で鳴かせない)。
  const dullCut = e.ctx.createBiquadFilter()
  dullCut.type = 'lowpass'
  dullCut.frequency.value = 1400
  dullCut.Q.value = 0.6
  dullCut.connect(e.comp)
  if (parrySteelC) {
    const src = e.ctx.createBufferSource()
    src.buffer = parrySteelC
    src.playbackRate.value = 0.58                 // 重く沈める
    const g = e.ctx.createGain()
    g.gain.value = 0.46 * G
    src.connect(g).connect(dullCut)
    routeReverb(e, g, 0.10)                        // 近く短い残響 (鳴きを残さない)
    src.start(t)
  }
  if (parrySteelA) {
    const src = e.ctx.createBufferSource()
    src.buffer = parrySteelA
    src.playbackRate.value = 0.70
    const g = e.ctx.createGain()
    g.gain.value = 0.13 * G                        // 細い銀は薄く (主役でない)
    src.connect(g).connect(dullCut)
    routeReverb(e, g, 0.06)
    src.start(t)
  }
  // サンプル未ロード時のフォールバック接触 (くぐもった当たり・鋭いスナップは出さない)
  if (!parrySteelC && !parrySteelA) {
    envNoise(e, t, 0.05, 0.30 * G, { type: 'lowpass', frequency: 520, Q: 0.7 }, 0.05)
  }
  // --- 衝撃コア「ドスッ」: playParryCold の重量層 (§3) を厚めに、金属の鳴き (§1-2 スナップ/鎖) は足さない ---
  envOsc(e, t, 'sine', 200, { amp: 0.68 * G, attack: 0.001, decay: 0.10, reverb: 0 })
  envNoise(e, t, 0.10, 0.58 * G, { type: 'bandpass', frequency: 300, Q: 0.8 }, 0)
  const slam = envOsc(e, t, 'sine', 150, { amp: 0.82 * G, attack: 0.001, decay: 0.20, reverb: 0.03 })
  pitchBend(slam.osc, t, 150, 44, 0.14)            // ドスッと沈むピッチベンド (受け止めた重み)
  envOsc(e, t, 'sine', 66, { amp: 0.64 * G, attack: 0.002, decay: 0.24, reverb: 0.04 })  // 腹に来るサブ
}

// 石床の足音: 乾いた重みと城の残響
function playStep(e: Engine, t: number): void {
  envNoise(e, t, 0.06, 0.28, { type: 'bandpass', frequency: 280, Q: 1.2 }, 0.10)
  envOsc(e, t, 'sine', 88, { amp: 0.22, attack: 0.001, decay: 0.08, reverb: 0.12 })
}

// 体幹崩壊: 超常エネルギーが放出される劇的な瞬間
function playStagger(e: Engine, t: number): void {
  envOsc(e, t, 'sine', 62, { amp: 0.72, attack: 0.004, decay: 0.54, reverb: 0.38 })
  envNoise(e, t, 0.46, 0.42, { type: 'bandpass', frequency: 880, Q: 0.7 }, 0.52)
  const fall = envOsc(e, t, 'sine', 400, { amp: 0.46, attack: 0.008, decay: 0.58, reverb: 0.62 })
  pitchBend(fall.osc, t, 400, 62, 0.46)
  fmBell(e, t + 0.018, {
    carrierFreq: 680, modFreq: 680 * 2.76, modDepth: 1250, modDecay: 0.16,
    amp: 0.36, attack: 0.005, decay: 0.58, reverb: 0.66,
  })
  fmBell(e, t + 0.055, {
    carrierFreq: 1420, modFreq: 1420 * 3.5, modDepth: 920, modDecay: 0.11,
    amp: 0.28, attack: 0.006, decay: 0.48, reverb: 0.70,
  })
}

// 体幹崩し: 灰の王城=古い舞台の梁が折れて崩れる音 (2026-06-20 redesign v3: 鈍低中域メイン)
//   旧版「9個のガラス片散乱」=汎用クリスタル magic + 高音感が出ていた。 撤回。
//   新版: ① 低中域の「ボキッ」(梁が折れる) ② 沈み込む崩落 (体幹が割れる) ③ 銀の鎖の弛み (2 個まで) ④ 致命可能 subtle 合図
function playStaggerCrystal(e: Engine, t: number): void {
  if (sfxRoots !== 'off') { playStaggerPlain(e, t); return }
  // === ① 折れる刹那: 低中域の鋭いクラック (高音ガラスでなく梁が折れる) ===
  envOsc(e, t, 'triangle', 240, { amp: 0.62, attack: 0.0005, decay: 0.030, reverb: 0.06 })
  envNoise(e, t, 0.025, 0.85, { type: 'lowpass', frequency: 800, Q: 0.5 }, 0.08)
  envNoise(e, t, 0.020, 0.55, { type: 'bandpass', frequency: 350, Q: 0.8 }, 0.06)

  // === ② 崩れる重み: 低音の沈み込み (pitch bend down = 「ガクン」と崩れる体幹) ===
  envOsc(e, t, 'sine', 50, { amp: 0.85, attack: 0.001, decay: 0.40, reverb: 0.18 })
  const fall1 = envOsc(e, t, 'triangle', 80, { amp: 1.00, attack: 0.001, decay: 0.42, reverb: 0.22 })
  pitchBend(fall1.osc, t, 80, 48, 0.36)
  envOsc(e, t, 'sine', 110, { amp: 0.55, attack: 0.001, decay: 0.30, reverb: 0.14 })
  // 220 + 310 不協和ペア (不安定な基盤=「もう立てない」)
  envOsc(e, t + 0.020, 'sine', 220, { amp: 0.42, attack: 0.002, decay: 0.28, reverb: 0.16 })
  envOsc(e, t + 0.020, 'sine', 310, { amp: 0.32, attack: 0.002, decay: 0.24, reverb: 0.14 })

  // === ③ 鎖が弛む (一斉に鎖がゆるむ鈍い金属) ===
  envNoise(e, t + 0.030, 0.18, 0.36, { type: 'bandpass', frequency: 1200, Q: 1.4 }, 0.20)

  // === ④ 銀のヒビ (2個まで・hit_bossと同パレットで連続性) ===
  fmBell(e, t + 0.008, {
    carrierFreq: 1800, modFreq: 1800 * 2.76, modDepth: 1100, modDecay: 0.06,
    amp: 0.32, attack: 0.001, decay: 0.20, reverb: 0.24,
  })
  fmBell(e, t + 0.040, {
    carrierFreq: 2400, modFreq: 2400 * 1.41, modDepth: 900, modDecay: 0.05,
    amp: 0.22, attack: 0.002, decay: 0.16, reverb: 0.22,
  })

  // === ⑤ 致命可能 subtle 合図 (極控えめ cathedral で「呪われた舞台の余韻」) ===
  fmBell(e, t + 0.080, {
    carrierFreq: 3200, modFreq: 3200 * 1.41, modDepth: 600, modDecay: 0.04,
    amp: 0.10, attack: 0.005, decay: 0.18, reverb: 0.20, cathedralAmt: 0.18,
  })
}

// 崩しの「楽器の層を抜いた版」(開発版 ?sfxRoots= だけ・10-17)。① 折れる刹那と ③ 鎖の弛みはそのまま。
//   ② の低い正弦・滑る三角波 (50/80→48/110/220/310Hz) と ④⑤ の FM の鐘を抜き、② の重みは低い帯の雑音の塊で作る (音程を持たない)。
function playStaggerPlain(e: Engine, t: number): void {
  envOsc(e, t, 'triangle', 240, { amp: 0.62, attack: 0.0005, decay: 0.030, reverb: 0.06 })
  envNoise(e, t, 0.025, 0.85, { type: 'lowpass', frequency: 800, Q: 0.5 }, 0.08)
  envNoise(e, t, 0.020, 0.55, { type: 'bandpass', frequency: 350, Q: 0.8 }, 0.06)
  envNoise(e, t, 0.40, 6.0, { type: 'lowpass', frequency: 120, Q: 0.6 }, 0.16)
  envNoise(e, t, 0.30, 3.0, { type: 'bandpass', frequency: 70, Q: 0.9 }, 0.10)
  envNoise(e, t + 0.020, 0.26, 1.2, { type: 'bandpass', frequency: 220, Q: 0.8 }, 0.12)
  envNoise(e, t + 0.030, 0.18, 0.36, { type: 'bandpass', frequency: 1200, Q: 1.4 }, 0.20)
  // ④ 銀のヒビの代わり: 高さを持たない短いきしみ (細い帯の雑音)
  envNoise(e, t + 0.008, 0.05, 0.30, { type: 'bandpass', frequency: 2400, Q: 1.2 }, 0.20)
}

// 致命が入れられる「間」の合図 (2026-07-27)。
//   設計: 崩し音の §⑤ (fmBell 3200Hz・amp 0.10) と**同じ家系**にして、 新しい音を覚えさせない。
//   違いは「1回きり」か「間に置かれるか」だけ = 崩れている間ずっと同じ鐘が薄く点く。
//   ⚠️ 鳴らし続けない。 パリィは1戦で何十回も出るので、 持続音にすると必ず疲れる。
function playFatalReady(e: Engine, t: number): void {
  if (sfxRoots !== 'off') {
    // 開発版 ?sfxRoots=: FM の鐘をやめ、高さを持たない短い合図 (細い帯の雑音2本) にする
    envNoise(e, t, 0.07, 1.39, { type: 'bandpass', frequency: 3200, Q: 2.0 }, 0.24)
    envNoise(e, t + 0.012, 0.09, 1.02, { type: 'bandpass', frequency: 1600, Q: 1.6 }, 0.22)
    envNoise(e, t + 0.02, 0.16, 0.25, { type: 'bandpass', frequency: 2600, Q: 2.2 }, 0.24)
    return
  }
  // ① 冷たい小ベル (崩し音 §⑤ と同じ carrier/mod 比)
  fmBell(e, t, {
    carrierFreq: 3200, modFreq: 3200 * 1.41, modDepth: 620, modDecay: 0.04,
    amp: 0.30, attack: 0.004, decay: 0.22, reverb: 0.24, cathedralAmt: 0.22,
  })
  // ② 一段下のオクターブで芯を足す (小さい画面でも埋もれない)
  fmBell(e, t + 0.012, {
    carrierFreq: 1600, modFreq: 1600 * 1.41, modDepth: 380, modDecay: 0.05,
    amp: 0.16, attack: 0.005, decay: 0.26, reverb: 0.22, cathedralAmt: 0.18,
  })
  // ③ 短い吸い込み (常夜燈へ吸われる予感。 音量は下げ、 気配だけ)
  envNoise(e, t + 0.02, 0.16, 0.10, { type: 'bandpass', frequency: 2600, Q: 2.2 }, 0.24)
}

// 舞台が軋む音・候補 (2026-06-09)。telegraph_growl の差し替え用。
//   「ぶうーん」(純粋な唸り) でも「ゴゴゴゴ」(地鳴り) でもなく "きしみ" を出す。
//   きしみの核 = 低い荷重の唸り + 不規則に上ずる stick-slip スクイーク (摩擦の鳴き)。
function creakSqueak(e: Engine, t: number, base: number, amp: number, type: OscillatorType = 'triangle'): void {
  const sq = envOsc(e, t, type, base, { amp, attack: 0.012, decay: 0.16 + Math.random() * 0.06, reverb: 0.28 })
  pitchBend(sq.osc, t, base, base * (1.3 + Math.random() * 0.25), 0.16)
}
// 候補1: 木が軋む・ゆっくり (たわむ梁。低い唸り + まばらな鳴き)
function playCreakWood(e: Engine, t: number): void {
  const body = envOsc(e, t, 'sine', 78, { amp: 0.26, attack: 0.05, decay: 0.62, reverb: 0.28 })
  pitchBend(body.osc, t, 78, 104, 0.32)
  envOsc(e, t, 'sine', 46, { amp: 0.30, attack: 0.04, decay: 0.5, reverb: 0.12 })
  creakSqueak(e, t + 0.02, 360, 0.09)
  creakSqueak(e, t + 0.17, 480, 0.075)
  creakSqueak(e, t + 0.33, 300, 0.08)
  envNoise(e, t, 0.5, 0.05, { type: 'bandpass', frequency: 520, Q: 1.2 }, 0.18)
}
// 候補2: 重い梁・低く不穏 (もっと低く遅く、鳴きは少なく圧を強く)
function playCreakHeavy(e: Engine, t: number): void {
  const body = envOsc(e, t, 'sine', 58, { amp: 0.34, attack: 0.06, decay: 0.78, reverb: 0.24 })
  pitchBend(body.osc, t, 58, 80, 0.45)
  envOsc(e, t, 'sine', 38, { amp: 0.40, attack: 0.05, decay: 0.7, reverb: 0.10 })
  creakSqueak(e, t + 0.06, 250, 0.075)
  creakSqueak(e, t + 0.38, 330, 0.065)
  envNoise(e, t, 0.6, 0.045, { type: 'lowpass', frequency: 600 }, 0.2)
}
// 候補3: 張る鎖・金属の軋み (縫い留める鎖の主題。鳴きを高く金属質に)
function playCreakChain(e: Engine, t: number): void {
  const body = envOsc(e, t, 'sine', 88, { amp: 0.18, attack: 0.04, decay: 0.5, reverb: 0.3 })
  pitchBend(body.osc, t, 88, 120, 0.3)
  for (const [f, dt] of [[760, 0.0], [980, 0.14], [1240, 0.27], [880, 0.4]] as [number, number][]) {
    const sq = e.ctx.createOscillator(); sq.type = 'triangle'; sq.frequency.value = f
    const g = e.ctx.createGain()
    g.gain.setValueAtTime(0, t + dt)
    g.gain.linearRampToValueAtTime(0.06, t + dt + 0.01)
    g.gain.exponentialRampToValueAtTime(0.0001, t + dt + 0.2)
    sq.connect(g).connect(e.comp); routeReverb(e, g, 0.4)
    sq.frequency.setValueAtTime(f, t + dt)
    sq.frequency.linearRampToValueAtTime(f * 1.35, t + dt + 0.18)
    sq.start(t + dt); sq.stop(t + dt + 0.25)
  }
}

// ボス撃破の最後の一撃 + 魂引き抜きの瞬間 (2026-06-20 redesign v7: v0 復元+微強化)
//   v6 の上昇シマー (1800→2700Hz) + C5/C6 chant が「ぴょいん」感の元になっていたため撤去。
//   v7: 元 (v0) の構造を素直に維持。 46Hz sub + 580Hz friction + 360/720Hz bell + HPF noise。
//   微強化のみ: cathedralAmt をやや上げて「呪われた舞台の銀」感を補強。
// =====================================================================
// 致命3連撃 (2026-06-23 #12): ElevenLabs 生楽器テイク(本体) を「灰の王城の音」へ染める。
//   採用テイク: hit1=短#2 / hit2=短#3 / hit3=長#1 t12 (ユーザー耳で確定 2026-06-23)。
// 確定方向 (ユーザー選択 2026-06-23 #12・実機A/A+B比較): A=大聖堂の空間+冷EQ + B=銀の結晶。
//   各撃 = サンプル(本体) を大聖堂へ座らせ高域を冷やす + ズドンの芯(サブ低域) + B結晶(下記 execColorLayers)。
//   cathedral量を hit1<hit2<hit3。サンプル未ロード時は旧・純合成版 (*Synth) へフォールバック。
// 致命の世界観カラー: A=空間(大聖堂+冷EQ)は常時。B=銀の結晶も採用 (実機 A/A+B 比較でユーザー確定 2026-06-23)。
//   ※ fxlab 単発では B が「浮く」と判定したが、実戦(BGM+他SE)の中だと馴染むと再評価=採用。C 不穏は不採用。
//   dev トグル __setExecWorld('A'/'AB'/'ABC') で実機再評価も可能。
let execColB = true   // B 銀の結晶 = 採用 (確定)
let execColC = false  // C 不穏な弦・金属 = 不採用
export function setExecWorldColors(crystal: boolean, unsettling: boolean): void {
  execColB = crystal
  execColC = unsettling
}

// A の上に乗せる色レイヤー (hit1/2 は短いので控えめ)。既定 off=何もしない。
function execColorLayers(e: Engine, t: number, hit: 1 | 2 | 3): void {
  const big = hit === 3
  if (execColB && sfxRoots === 'off') {
    const sc = big ? 1 : 0.6
    fmBell(e, t + 0.008, { carrierFreq: 2300, modFreq: 2300 * 2.76, modDepth: 1200, modDecay: 0.12, amp: 0.16 * sc, attack: 0.004, decay: big ? 0.60 : 0.32, reverb: 0.25, cathedralAmt: 0.40 })
    if (big) fmBell(e, t + 0.020, { carrierFreq: 3480, modFreq: 3480 * 2.76, modDepth: 800, modDecay: 0.09, amp: 0.09, attack: 0.004, decay: 0.45, reverb: 0.25, cathedralAmt: 0.35 })
  }
  if (execColC) {
    ringMod(e, t + 0.005, { freqA: 196, freqB: 233, amp: big ? 0.13 : 0.08, attack: 0.006, decay: big ? 0.70 : 0.40, reverb: 0.30 })
    envNoise(e, t + 0.030, big ? 0.30 : 0.18, big ? 0.10 : 0.07, { type: 'bandpass', frequency: 1400, Q: 1.6 }, 0.25)
  }
}

// 致命のズドンの芯。開発版 ?sfxRoots= では音程が滑る正弦 (歪ませたキック) をやめ、低い帯の雑音の塊にする (音程を持たない)。
function execThump(e: Engine, t: number, freq: number,
  opts: { amp: number; attack: number; decay: number; drive: number; pitchTo?: number; reverb?: number }): void {
  if (sfxRoots === 'off') { saturatedThump(e, t, freq, opts); return }
  envNoise(e, t, opts.decay * 1.2, opts.amp * 11, { type: 'lowpass', frequency: freq * 1.6, Q: 0.6 }, opts.reverb ?? 0)
  envNoise(e, t, opts.decay * 0.7, opts.amp * 5, { type: 'bandpass', frequency: freq, Q: 0.9 }, 0)
}

function playExecuteHit1(e: Engine, t: number): void {
  if (!playSampleWorld('exec_hit1', 0.72, { reverb: 0.10, cathedral: 0.42, coldShelfDb: -5 })) { playExecuteHit1Synth(e, t); return }
  execThump(e, t, 88, { amp: 0.45, attack: 0.001, decay: 0.16, drive: 5, pitchTo: 60, reverb: 0.05 })
  execColorLayers(e, t, 1)
}

function playExecuteHit2(e: Engine, t: number): void {
  if (!playSampleWorld('exec_hit2', 0.85, { reverb: 0.10, cathedral: 0.48, coldShelfDb: -5 })) { playExecuteHit2Synth(e, t); return }
  execThump(e, t, 78, { amp: 0.52, attack: 0.001, decay: 0.18, drive: 5, pitchTo: 55, reverb: 0.05 })
  execColorLayers(e, t, 2)
}

function playExecuteHit3(e: Engine, t: number): void {
  // 2026-06-24: 魂③=無音化に伴い hit3 の残響を伸ばし"部屋が呑む"の役を hit3 の尾に任せる (cathedral 0.55→0.78)
  if (!playSampleWorld('exec_hit3', 1.0, { reverb: 0.10, cathedral: 0.78, coldShelfDb: -5 })) { playExecuteHit3Synth(e, t); return }
  execThump(e, t, 55, { amp: 0.60, attack: 0.001, decay: 0.28, drive: 6, pitchTo: 42, reverb: 0.06 })
  execThump(e, t, 150, { amp: 0.40, attack: 0.001, decay: 0.10, drive: 3, reverb: 0.05 })
  execColorLayers(e, t, 3)
}

// (#12 確定 2026-06-23: hit3 の世界観編集 audition 関数 playExecuteHit3World / ...Audition は
//  A+B 確定により撤去。本番は playExecuteHit1/2/3 + execColorLayers が担う。)

// 系統② トドメの一撃 (撃破の決め一発・1戦1回): hit3 を主軸に「一段デカく重く」した最大版。
//   hit3 と同じ ElevenLabs テイク(exec_hit3) を本体に、より深く長いズドン + 大きめの大聖堂 + B結晶。
//   = 3連撃で訓練された快感の最大化 (設計doc §3「別世界へワープしない・同じ血筋の最大版」)。
//   サンプル未ロード時は旧・純合成版 (playExecuteSynth) へフォールバック。
function playExecute(e: Engine, t: number): void {
  if (!playSampleWorld('exec_hit3', 1.05, { reverb: 0.12, cathedral: 0.72, coldShelfDb: -5 })) { playExecuteSynth(e, t); return }
  // ズドンの芯 = hit3 より一段深く長く (撃破の決定打)
  execThump(e, t, 46,  { amp: 0.85, attack: 0.001, decay: 0.45, drive: 7, pitchTo: 34, reverb: 0.10 })
  execThump(e, t, 110, { amp: 0.55, attack: 0.001, decay: 0.18, drive: 5, pitchTo: 66, reverb: 0.06 })
  execThump(e, t, 150, { amp: 0.42, attack: 0.001, decay: 0.10, drive: 3, reverb: 0.05 })
  // B 銀の結晶 (hit3 と同じ血筋・決定打なので少し華やかに)。開発版 ?sfxRoots= では抜く
  if (sfxRoots !== 'off') return
  fmBell(e, t + 0.008, { carrierFreq: 2300, modFreq: 2300 * 2.76, modDepth: 1200, modDecay: 0.12, amp: 0.18, attack: 0.004, decay: 0.65, reverb: 0.28, cathedralAmt: 0.45 })
  fmBell(e, t + 0.020, { carrierFreq: 3480, modFreq: 3480 * 2.76, modDepth: 800, modDecay: 0.09, amp: 0.11, attack: 0.004, decay: 0.48, reverb: 0.28, cathedralAmt: 0.40 })
}

// 系統③ 魂 (execute_soul) — 魂が抜け常夜燈に奪われる署名音。
//   設計 (2026-06-24 spec 再確認で振り直し): 致命=解放は嘘 / 紫の魂=救いでなく自分を縛る鎖。
//   ⇒ 浄化・弔いを"演じない"。儀式しない。プレイヤーが「あれ、何か聞こえた?」と感じる程度の
//      偶発的・無声音的なテクスチャ (息/エア)。spec の「奪取の瞬間音」を音にする。
//   旧テイク (弦+声 #4) は儀式感が出るので比較用に残す (old_strings)。
//   新テイク E系 (溶ける吐息) / F系 (空気が通った) を candidates から複数本ロード済。
//   実機/fxlab で __setSoulTake('e1'|'e2'|'e3'|'f1'|'f2'|'old_strings') で切替。
type SoulTake = 'off' | 'old_strings' | 'e1' | 'e2' | 'e3' | 'f1' | 'f2'
// 既定='off' (2026-06-24 ユーザー裁定): hit3 の大聖堂残響だけで"部屋が魂を飲み込む"を表現。
//   spec 「致命=解放は嘘 / 儀式しない」に直結=何も鳴らさない方が嘘が成立する。
//   E/F テイクは fxlab/実機 __setSoulTake で比較用に残す (再有効化可能)。
let soulTake: SoulTake = 'off'
export function setSoulTake(v: SoulTake): void { soulTake = v }

// 魂の音量 (0=無音 / 0.95=既定 / >1=実機ブースト)。実機で余韻バランスを探る用 __setSoulVoice。
let soulVoiceLevel = 0.95
export function setSoulVoice(v: number): void { soulVoiceLevel = Math.max(0, Math.min(1.5, v)) }

function playExecuteSoul(_e: Engine, _t: number): void {
  if (soulTake === 'off') return  // 既定=無音 (hit3 残響だけで"部屋が呑む")
  if (soulTake === 'old_strings') {
    // 旧弦+声 (儀式感あり・比較用) — 大聖堂・フェードで余韻整える。
    playSampleWorld('exec_soul', soulVoiceLevel * 0.85, {
      reverb: 0.05, cathedral: 0.22, coldShelfDb: -4,
      fade: { start: 1.3, dur: 1.3 },
    })
    return
  }
  // 新テイク (偶発系) — 大聖堂は控えめ(=儀式化しない)・余韻はゆっくり引いて消える。
  const name = `exec_soul_${soulTake}`
  playSampleWorld(name, soulVoiceLevel, {
    reverb: 0.05, cathedral: 0.18, coldShelfDb: -3,
    fade: { start: 2.0, dur: 1.0 },
  })
}

// 旧・純合成 execute (トドメのサンプル未ロード時フォールバック + 魂 placeholder)。
function playExecuteSynth(e: Engine, t: number): void {
  // 46Hz sub (体幹を貫く)
  envOsc(e, t, 'sine', 46, { amp: 0.88, attack: 0.003, decay: 0.38, reverb: 0.18 })
  // 中域 friction (580Hz bandpass)
  envNoise(e, t, 0.22, 0.62, { type: 'bandpass', frequency: 580, Q: 0.7 }, 0.48)
  // 銀の主鐘 360Hz (cathedralAmt で「呪われた舞台」の響き)
  fmBell(e, t + 0.045, {
    carrierFreq: 360, modFreq: 360 * 2.76, modDepth: 1450, modDecay: 0.28,
    amp: 0.46, attack: 0.008, decay: 0.68, reverb: 0.72, cathedralAmt: 0.20,
  })
  // オクターブ上 720Hz
  fmBell(e, t + 0.12, {
    carrierFreq: 720, modFreq: 720 * 3.5, modDepth: 1050, modDecay: 0.20,
    amp: 0.30, attack: 0.008, decay: 0.58, reverb: 0.74, cathedralAmt: 0.18,
  })
  // 高域 transient (1.9kHz HPF=元のまま)
  envNoise(e, t, 0.08, 0.36, { type: 'highpass', frequency: 1900 }, 0.32)
}

// 致命1撃目 (突き): 「点で刺す」=貫通最優先 (Codex 2026-06-10「刺さる」設計)
//   2026-06-20 v3: 「おもちゃみたい」=薄い body が原因。 sub + 厚い body + mid thud で重さ追加。
//   「ぴょいん」回避=末尾 shimmer は 1760Hz 固定 sine (pitch bend 無し)。
function playExecuteHit1Synth(e: Engine, t: number): void {
  // === 重み層: sub + body + thud (突きの「決まった」核) ===
  envOsc(e, t, 'sine', 50, { amp: 0.65, attack: 0.001, decay: 0.20, reverb: 0.10 })   // sub
  envOsc(e, t, 'sine', 90, { amp: 0.75, attack: 0.001, decay: 0.18, reverb: 0.10 })   // body
  envOsc(e, t, 'sine', 180, { amp: 0.42, attack: 0.001, decay: 0.16, reverb: 0.08 })  // thud
  // mid friction: 刺さる手応え
  envNoise(e, t, 0.060, 0.55, { type: 'bandpass', frequency: 500, Q: 0.8 }, 0.10)
  // === 突きの「点で刺す」レイヤー (元の設計の核を維持) ===
  // ① 刃が入った瞬間の高域トランジェント (HP noise ~3ms)
  envNoise(e, t, 0.003, 0.50, { type: 'highpass', frequency: 6000 }, 0.02)
  // ② stab ping: 点で貫通する芯 (3.9kHz・強化)
  const stab = envOsc(e, t, 'sine', 3900, { amp: 0.42, attack: 0.0006, decay: 0.022, reverb: 0.06 })
  pitchBend(stab.osc, t, 4100, 3700, 0.022)
  // ③ 擦弦の刺し (2400Hz)
  envNoise(e, t, 0.055, 0.36, { type: 'bandpass', frequency: 2400, Q: 2.0 }, 0.10)
  // ④ 銀の高域 ping (A6=1760Hz 固定 sine・cathedral 風 reverb=hit2と octave 同期)
  envOsc(e, t + 0.008, 'sine', 1760, { amp: 0.18, attack: 0.002, decay: 0.20, reverb: 0.42 })
}

// 致命2撃目 (薙ぎ): 「横に裂く」=点でなく面 (Codex 2026-06-10「刺さる」設計)
//   2026-06-20 v3: 「おもちゃみたい」=薄い body が原因。 sub + 厚い body + thud で重さ追加。
//   「ぴっぽっぱ」回避=A5+A6 (880+1760) perfect octave + inharmonic 2.76 ratio で metallic crackle。
function playExecuteHit2Synth(e: Engine, t: number): void {
  // === 重み層: sub + body + thud (薙ぎの「決まった」核) ===
  envOsc(e, t, 'sine', 60, { amp: 0.68, attack: 0.001, decay: 0.22, reverb: 0.10 })   // sub
  envOsc(e, t, 'sine', 100, { amp: 0.78, attack: 0.001, decay: 0.20, reverb: 0.10 })  // body
  envOsc(e, t, 'sine', 200, { amp: 0.45, attack: 0.001, decay: 0.18, reverb: 0.08 })  // thud
  // 150Hz body 維持 (元の設計の残し)
  envOsc(e, t, 'sine', 150, { amp: 0.40, attack: 0.001, decay: 0.20, reverb: 0.04 })
  // === 薙ぎの「横に裂く」レイヤー (元の設計の核を維持) ===
  // ① noise sweep: 1.6k→3.0k をずらして上昇感
  envNoise(e, t, 0.075, 0.36, { type: 'bandpass', frequency: 1600, Q: 0.9 }, 0.12)
  envNoise(e, t + 0.022, 0.090, 0.32, { type: 'bandpass', frequency: 3000, Q: 0.9 }, 0.14)
  // ② scrape: 擦弦の弓圧感 (1500Hz)
  envNoise(e, t, 0.110, 0.24, { type: 'bandpass', frequency: 1500, Q: 1.5 }, 0.10)
  // ③ 銀の鐘 A5 (880Hz・modFreq×2.76 inharmonic・decay 強化=「決まった」余韻)
  fmBell(e, t, {
    carrierFreq: 880, modFreq: 880 * 2.76, modDepth: 1100, modDecay: 0.10,
    amp: 0.28, attack: 0.003, decay: 0.40, reverb: 0.22, cathedralAmt: 0.28,
  })
  // ④ A6 (1760Hz・hit1の④と完全同期=連続性・decay 強化)
  fmBell(e, t + 0.018, {
    carrierFreq: 1760, modFreq: 1760 * 2.76, modDepth: 800, modDecay: 0.07,
    amp: 0.18, attack: 0.004, decay: 0.34, reverb: 0.20, cathedralAmt: 0.22,
  })
  // ⑤ swing (横の動き)
  const sw = envOsc(e, t + 0.016, 'triangle', 480, { amp: 0.14, attack: 0.010, decay: 0.30, reverb: 0.12 })
  pitchBend(sw.osc, t + 0.016, 600, 300, 0.24)
}

// 致命3撃目 (とどめ): 神聖・儀式パッケージ (parry_lab playExecuteHit3 確定版)
//   2026-06-20: ユーザー「方向性は良いがもう少し重み」フィードバック → sub/倍音 amp ブースト + 30Hz 超低域追加。
//   既存構造 (儀式の鐘 C/E♭/G + cathedral chant + 魂シマー) は変更なし。
function playExecuteHit3Synth(e: Engine, t: number): void {
  const C2 = 65.41, G1 = 49.0, C3 = 130.81, G2 = 98.0
  const EB3 = 155.56, G3 = 196.0, AB3 = 207.65
  const C5 = 523.25, EB5 = 622.25, G5 = 784.0
  const C7 = 2093.0

  // ※ v5: 30Hz subsonic (decay 0.42 + reverb 0.08) と sub amp 0.92 へのブーストが「ぼぉん」(遅い低域共鳴) の元だったため撤回。
  //    重みは 30-80Hz の長尺ではなく、 200Hz の「ガツン」短打で出し直す (下の thud)。
  // deep sub C2→G1 + 補助倍音 (元の amp に戻す)
  const sub = envOsc(e, t, 'sine', C2, { amp: 0.72, attack: 0.002, decay: 0.40, reverb: 0 })
  pitchBend(sub.osc, t, C2, G1, 0.32)
  envOsc(e, t, 'sine', C3, { amp: 0.14, attack: 0.002, decay: 0.42, reverb: 0 })
  envOsc(e, t, 'sine', G2, { amp: 0.12, attack: 0.002, decay: 0.30, reverb: 0 })
  // 200Hz「ガツン」短打 (重みを長尺低域でなく中低域 punchy で出す=「ぼぉん」回避)
  envOsc(e, t, 'sine', 200, { amp: 0.55, attack: 0.001, decay: 0.13, reverb: 0.06 })

  // 低いクラック + 儀式クラック + 高域クリック (致命一撃の punch=ブースト)
  envNoise(e, t, 0.065, 0.72, { type: 'bandpass', frequency: 190, Q: 1.0 }, 0)
  envNoise(e, t, 0.040, 0.50, { type: 'bandpass', frequency: 1800, Q: 1.4 }, 0.06)
  envNoise(e, t, 0.004, 0.45, { type: 'highpass', frequency: 4500 }, 0)

  // 儀式の鐘 C/E♭/G (cathedral reverb・decay 強化=余韻延長)
  fmBell(e, t + 0.004, { carrierFreq: C5, modFreq: C5 * 2.0, modDepth: 1100, modDecay: 0.10, amp: 0.42, attack: 0.003, decay: 0.85, reverb: 0, cathedralAmt: 0.55 })
  fmBell(e, t + 0.006, { carrierFreq: EB5, modFreq: EB5 * 2.0, modDepth: 950, modDecay: 0.09, amp: 0.24, attack: 0.003, decay: 0.80, reverb: 0, cathedralAmt: 0.50 })
  fmBell(e, t + 0.008, { carrierFreq: G5, modFreq: G5 * 2.0, modDepth: 850, modDecay: 0.08, amp: 0.15, attack: 0.003, decay: 0.75, reverb: 0, cathedralAmt: 0.48 })

  // 短調聖歌スウェル C3/E♭3/G3/A♭3
  playChantSwellCath(e, t, [C3, EB3, G3, AB3])

  // 魂シマー C7 固定 (v4: 上昇 sweep Eb6→Bb6→C7 = 漫画チックな「ぼいん」を撤回・FLAT で deathblow の決定的なリング)
  const shim = envOsc(e, t + 0.10, 'sine', C7, { amp: 0.085, attack: 0.06, decay: 1.10, reverb: 0 })
  routeCathedral(e, shim.gain, 0.70)

  // ※ 2026-06-20 v3: 50Hz+100Hz 余韻ハム (attack 0.20 + decay 1.50) は「ぼいぃん」の元だったため撤回。
  //    余韻は既存の鐘 (decay 0.85/0.80/0.75) + 魂シマー C7 (decay 1.10) で十分自然にフェードする。

  // 「魂を抜いた後の静寂」演出は SE の master duck では実機で不採用 (2026-06-10 実機検証)。
  //   戦闘の流れの中だと「残響がスーッ→キュッと引っ込む→また出てくる」二段の尾が不自然
  //   (BGM が別系統 = master を通らないため、SE の残響だけ引っ込んで BGM は残る)。
  //   静寂を出すなら SE 単体でなく致命撃破演出シーケンス側で BGM fade 込みで設計する (演出部レーン)。
}

// (#12 確定 2026-06-23: hit3 合成3案 A/B/C (playExecuteHit3CandA/B/C) と dispatcher は
//  ElevenLabsテイク+A+B 採用により撤去。旧・純合成版 playExecuteHit3Synth はフォールバックとして残置。)

// 聖歌スウェル (致命3撃目専用・playExecuteHit3Synth フォールバックで使用)
function playChantSwellCath(e: Engine, t: number, freqs: number[]): void {
  const lpf = e.ctx.createBiquadFilter()
  lpf.type = 'lowpass'; lpf.frequency.value = 1150
  const bus = e.ctx.createGain()
  bus.gain.setValueAtTime(0, t)
  bus.gain.linearRampToValueAtTime(1.0, t + 0.10)
  bus.gain.setValueAtTime(1.0, t + 0.35)
  bus.gain.exponentialRampToValueAtTime(0.0001, t + 0.80)
  bus.connect(lpf).connect(e.comp)
  routeCathedral(e, bus, 0.55)

  const detuneMap = [-5, +4, -3, +6]
  freqs.forEach((freq, i) => {
    const tri = e.ctx.createOscillator()
    tri.type = 'triangle'; tri.frequency.value = freq; tri.detune.value = detuneMap[i] ?? 0
    const tg = e.ctx.createGain(); tg.gain.value = 0.085
    tri.connect(tg).connect(bus)
    tri.start(t); tri.stop(t + 0.85)
  })
}

// 攻撃予兆のかすかな共鳴
function playTelegraphStart(e: Engine, t: number): void {
  fmBell(e, t, {
    carrierFreq: 230, modFreq: 230 * 2.1, modDepth: 140, modDecay: 0.05,
    amp: 0.13, attack: 0.006, decay: 0.16, reverb: 0.36,
  })
}

// 物理攻撃の予兆: 舞台が軋む幽霊的な唸り
function playTelegraphGrowl(e: Engine, t: number): void {
  const rumble = envOsc(e, t, 'sine', 115, { amp: 0.22, attack: 0.025, decay: 0.30, reverb: 0.22 })
  pitchBend(rumble.osc, t, 115, 75, 0.26)
  envOsc(e, t, 'sine', 50, { amp: 0.30, attack: 0.020, decay: 0.25, reverb: 0.14 })
  envNoise(e, t, 0.22, 0.12, { type: 'bandpass', frequency: 380, Q: 1.5 }, 0.16)
}

// 投擲/魔法の予兆: 冷たい結晶エネルギーが集まる
function playTelegraphWhirr(e: Engine, t: number): void {
  const crystal = envOsc(e, t, 'triangle', 980, { amp: 0.18, attack: 0.035, decay: 0.44, reverb: 0.42 })
  pitchBend(crystal.osc, t, 680, 1580, 0.38)
  fmBell(e, t + 0.08, {
    carrierFreq: 2300, modFreq: 2300 * 3.5, modDepth: 820, modDecay: 0.12,
    amp: 0.14, attack: 0.010, decay: 0.32, reverb: 0.52,
  })
  envNoise(e, t, 0.30, 0.08, { type: 'highpass', frequency: 3200 }, 0.22)
}

// 花嫁の速い返し: 低い唸りを増やさず、張った絹の短い擦過音+銀の芯だけを置く。
// 350ms予告の前半で終わり、接触時のパリィ音と競合しない。
function playBrideVeilSnap(e: Engine, t: number): void {
  envNoise(e, t, 0.11, 0.13, { type: 'bandpass', frequency: 2200, Q: 2.8 }, 0.08)
  const thread = envOsc(e, t + 0.006, 'triangle', 1380, {
    amp: 0.075, attack: 0.002, decay: 0.105, reverb: 0.16,
  })
  pitchBend(thread.osc, t + 0.006, 1720, 1120, 0.09)
}

// 騎士の連撃継続: 低い共通唸りを置き換えず、槍の金具が締まる細い擦過音を上へ重ねる。
// 騎士の薙ぎ接触の今の版 (knight_sweep_scrape)。低い風切りと短い石床の打音。候補 a〜e の未配置時にも鳴る。
function playKnightSweepScrape(e: Engine, t: number): void {
  envNoise(e, t, 0.26, 0.52, { type: 'lowpass', frequency: 480, Q: 0.7 }, 0.11)
  envOsc(e, t + 0.045, 'sine', 72, { amp: 0.28, attack: 0.001, decay: 0.075, reverb: 0.13 })
}

// 最短400msの薙ぎでも前半130ms以内に消え、接触時のパリィ音/dangerを覆わない。
function playKnightComboTension(e: Engine, t: number): void {
  envNoise(e, t, 0.085, 0.11, { type: 'bandpass', frequency: 1450, Q: 3.2 }, 0.07)
  const shaft = envOsc(e, t + 0.004, 'triangle', 760, {
    amp: 0.055, attack: 0.002, decay: 0.105, reverb: 0.1,
  })
  pitchBend(shaft.osc, t + 0.004, 760, 1040, 0.085)
  fmBell(e, t + 0.014, {
    carrierFreq: 1180, modFreq: 2797, modDepth: 120, modDecay: 0.04,
    amp: 0.045, attack: 0.002, decay: 0.115, reverb: 0.14,
  })
}

// 常打ちの刑吏: 武器が折れた (2026-07-29)。
//   パリィ音 (冷たい銀・高域) の直後に必ず鳴るので、**帯を明け渡す**: 破断は木質の低中域だけで作り、
//   金属は「落ちる金具」の短い2点にとどめる。上行させない = 折れる = 下へ落ちる向き。
function playExecWeaponBreak(e: Engine, t: number): void {
  // ①破断そのもの: 太い木が裂ける乾いた一撃 (低めの bandpass ノイズ + 急峻な減衰)
  envNoise(e, t, 0.012, 0.16, { type: 'bandpass', frequency: 420, Q: 1.6 }, 0.22)
  const snap = envOsc(e, t, 'triangle', 190, { amp: 0.13, attack: 0.001, decay: 0.13, reverb: 0.25 })
  pitchBend(snap.osc, t, 190, 96, 0.11)   // 下行 = 落ちる向き
  // ②裂け目が走る: 少し遅れて中域が短く裂ける
  envNoise(e, t + 0.03, 0.02, 0.1, { type: 'bandpass', frequency: 980, Q: 2.4 }, 0.09)
  // ③金具が石床へ: 高い硬質が2つ、間隔を不揃いにして「転がった」を出す
  fmBell(e, t + 0.11, { carrierFreq: 1560, modFreq: 3720, modDepth: 210, modDecay: 0.03,
    amp: 0.05, attack: 0.001, decay: 0.17, reverb: 0.36 })
  fmBell(e, t + 0.19, { carrierFreq: 1180, modFreq: 2760, modDepth: 160, modDecay: 0.03,
    amp: 0.036, attack: 0.001, decay: 0.22, reverb: 0.4 })
}

// 常打ちの刑吏: ゴミ山から次の道具を引き出した (2026-07-29)。
//   break の対。**上行**させて「持ち上がる/無防備が終わる」を伝える。
//   先に山が崩れる (乾いた瓦礫) → 重い物が引き出される、の順で、拾い終わりの一点を明確にする。
function playExecWeaponPick(e: Engine, t: number): void {
  // ①山が崩れる: 細かい金属片の散り (短く・広め)
  envNoise(e, t, 0.008, 0.22, { type: 'bandpass', frequency: 2600, Q: 0.9 }, 0.07)
  envNoise(e, t + 0.06, 0.01, 0.15, { type: 'bandpass', frequency: 1900, Q: 1.2 }, 0.05)
  // ②重い物が引き出される: 低い擦過 + 上行
  const drag = envOsc(e, t + 0.13, 'sawtooth', 88, { amp: 0.07, attack: 0.02, decay: 0.3, reverb: 0.3 })
  pitchBend(drag.osc, t + 0.13, 88, 148, 0.26)   // 上行 = 持ち上がる向き
  envNoise(e, t + 0.13, 0.05, 0.26, { type: 'bandpass', frequency: 620, Q: 1.1 }, 0.08)
  // ③構え直した一点 (ここで無防備が終わる)
  fmBell(e, t + 0.34, { carrierFreq: 880, modFreq: 1760, modDepth: 140, modDecay: 0.05,
    amp: 0.06, attack: 0.003, decay: 0.26, reverb: 0.42 })
}

// 三重の返礼: 数を数えさせる長い旋律ではなく、ひとまとまりの上行3音。
// 姿の広い三叉シルエットが主情報なので、各音は小さく70ms間隔で収める。
// 鶏の加速の比較 (DEV ?roosterRush): 時計の秒針のような乾いた2打。高い木の打撃 + ごく短い雑音。
// 鶏の鳴き声の仮 (10-30)。ノコギリ波を喉の響き (帯域) に通し、音の高さを声の抑揚で動かす。
//   points = [秒, 高さHz, 大きさ] を折れ線で結ぶ。最後の点で声が切れる。
function roosterVoice(e: Engine, t: number, points: readonly (readonly [number, number, number])[], formantHz: number): void {
  const osc = e.ctx.createOscillator()
  osc.type = 'sawtooth'
  const bp = e.ctx.createBiquadFilter()
  bp.type = 'bandpass'
  bp.frequency.value = formantHz
  bp.Q.value = 2.2
  const g = e.ctx.createGain()
  g.gain.setValueAtTime(0, t)
  osc.frequency.setValueAtTime(points[0]![1], t)
  for (const [dt, hz, amp] of points) {
    osc.frequency.linearRampToValueAtTime(hz, t + dt)
    g.gain.linearRampToValueAtTime(amp, t + dt)
  }
  osc.connect(bp).connect(g).connect(e.comp)
  routeReverb(e, g, 0.12)
  const end = points[points.length - 1]![0]
  osc.start(t)
  osc.stop(t + end + 0.05)
}

function playRoosterCrowShort(e: Engine, t: number): void {
  for (const ms of TOKI_SHORT_CLUCK_AT_MS) {
    const s = t + ms / 1000, d = TOKI_SHORT_CLUCK_MS / 1000
    roosterVoice(e, s, [[0, 520, 0], [0.012, 640, 0.32], [d * 0.55, 560, 0.24], [d, 430, 0]], 1300)
    envNoise(e, s, 0.03, 0.05, { type: 'bandpass', frequency: 2400, Q: 1.5 }, 0.04)
  }
}

function playRoosterFrenzyCluck(e: Engine, t: number): void {
  const d = TOKI_SHORT_CLUCK_MS / 1000
  roosterVoice(e, t, [[0, 520, 0], [0.012, 640, 0.32], [d * 0.55, 560, 0.24], [d, 430, 0]], 1300)
  envNoise(e, t, 0.03, 0.05, { type: 'bandpass', frequency: 2400, Q: 1.5 }, 0.04)
}

function playRoosterCrowLong(e: Engine, t: number): void {
  const cut = TOKI_LONG_CUT_MS / 1000
  // コ・ケ・コッ の短い3声のあと、高く伸ばして止む。
  const syl: [number, number, number][] = [[0, 0.11, 560], [0.15, 0.1, 640], [0.3, 0.09, 600]]
  for (const [s0, d, hz] of syl) roosterVoice(e, t + s0, [[0, hz * 0.85, 0], [0.012, hz, 0.3], [d, hz * 0.8, 0]], 1300)
  roosterVoice(e, t + 0.45, [[0, 600, 0], [0.04, 820, 0.34], [0.3, 900, 0.32], [cut - 0.45 - 0.08, 860, 0.3], [cut - 0.45 - 0.01, 820, 0.26], [cut - 0.45, 760, 0]], 1500)
}

function playRoosterRushTick(e: Engine, t: number): void {
  ;[0, 0.085].forEach((dt, i) => {
    fmBell(e, t + dt, {
      carrierFreq: i === 0 ? 1850 : 2100, modFreq: 3100, modDepth: 900, modDecay: 0.012,
      amp: 0.06, attack: 0.001, decay: 0.035, reverb: 0.06,
    })
  })
}

function playBrideTripleChime(e: Engine, t: number): void {
  const freqs = [930, 1170, 1475]
  freqs.forEach((freq, i) => {
    fmBell(e, t + i * 0.07, {
      carrierFreq: freq, modFreq: freq * 2.37, modDepth: 170, modDecay: 0.05,
      amp: 0.055, attack: 0.003, decay: 0.16, reverb: 0.24,
    })
  })
}

// 亡霊 (nameless-shade) 限定の予兆: 冷たい霊気の吐息/靄が満ちる (チケットG3)。
//   獣の唸り(growl)でも結晶の集束音(whirr)でもなく「霊がそこに在る」気配だけを鳴らす。
//   **低音の唸り成分 (ノコギリ波/低周波ランブル) は意図的に一切入れない = 獣性を排除。**
//   全体は danger/parry より必ず控えめ (呼吸のような予兆に留める)。
//
//   2026-07-14 作り直し (✅ユーザー採用=候補A「囁き寄り」)。
//   旧版 (3層: 生ホワイトノイズ bandpass 1本 + sine デチューン2本 / 0.35秒) は body・倍音・
//   時間の呼吸がなく薄かった = 安っぽさの正体。 実録サンプル路線 (Freesound CC0 の息を body に
//   敷く D/E/F/G) も並行して作り比較したが、 合成のAを採用 (実録の再挑戦用に
//   scripts/ghost_breath_render.py と そざい/_inbox/2026-07-14-ghost-telegraph/ を保管)。
//
//   構造 (尺 0.45秒 / すべて「満ちて引く」非対称包絡):
//     ① 吐息の body: ピンクノイズ(1/f) を bandpass 900→400Hz へスイープ (生ホワイトの「シャーッ」を回避)
//     ② 囁きのフォルマント F1/F2/F3 (500/1200/2400Hz の狭 bandpass・わずかに動いて母音が変わる)
//     ③ 抜けの空気 (highpass 3000→4200Hz)
//   ①②とも LFO で振幅をゆらす (息の震え) = 「誰かが息だけで喋っている」気配。
function playTelegraphGhost(e: Engine, t: number): void {
  const dur = 0.45
  ghostBreath(e, t, { dur, peak: 0.160, attack: 0.12, release: 0.30, freqFrom: 900, freqTo: 400, Q: 1.2, reverb: 0.32, flutterHz: 7.0, flutterDepth: 0.28, pinkAmt: 0.85 })
  ghostFormants(e, t, dur, 1.0)
  ghostBreath(e, t + 0.05, { dur: dur - 0.05, peak: 0.030, attack: 0.20, release: 0.24, filter: 'highpass', freqFrom: 3000, freqTo: 4200, Q: 0.7, reverb: 0.50, pinkAmt: 0.2 })
}

// =====================================================================
// 亡霊 telegraph の部品 (2026-07-14 作り直しで導入 / playTelegraphGhost 専用)
//   狙い: 生ホワイトノイズは「シャーッ」と安い → ピンクノイズ(1/f)で息の body を作り、
//        帯域を重ねて倍音を持たせ、attack/release を非対称にして「満ちて引く」呼吸にする。
// =====================================================================

// 1/f ピンクノイズ (Paul Kellet 近似)。 生ホワイトより低中域に body があり「息・空気」に聞こえる。
function ghostNoiseBuffer(e: Engine, dur: number, pinkAmt: number): AudioBuffer {
  const sr = e.ctx.sampleRate
  const len = Math.max(1, Math.floor(sr * dur))
  const buf = e.ctx.createBuffer(1, len, sr)
  const d = buf.getChannelData(0)
  let b0 = 0, b1 = 0, b2 = 0
  for (let i = 0; i < len; i += 1) {
    const w = Math.random() * 2 - 1
    b0 = 0.99765 * b0 + w * 0.0990460
    b1 = 0.96300 * b1 + w * 0.2965164
    b2 = 0.57000 * b2 + w * 1.0526913
    const pink = (b0 + b1 + b2 + w * 0.1848) * 0.6
    d[i] = pink * pinkAmt + w * (1 - pinkAmt)
  }
  return buf
}

type GhostBreathOpts = {
  dur: number
  peak: number
  attack: number          // 満ちる時間 (長め)
  release: number         // 引く時間 (attack と非対称)
  filter?: BiquadFilterType
  freqFrom: number
  freqTo?: number         // 指定時は dur かけてスイープ
  Q?: number
  reverb?: number
  flutterHz?: number      // 振幅ゆらぎ (息の震え)
  flutterDepth?: number   // peak に対する比率
  pinkAmt?: number
}

// ノイズ1層 = 「満ちて引く」息。 帯域・ゆらぎを変えて重ねると body と倍音になる。
function ghostBreath(e: Engine, t: number, o: GhostBreathOpts): void {
  const src = e.ctx.createBufferSource()
  src.buffer = ghostNoiseBuffer(e, o.dur, o.pinkAmt ?? 0.7)

  const f = e.ctx.createBiquadFilter()
  f.type = o.filter ?? 'bandpass'
  f.Q.value = o.Q ?? 2
  f.frequency.setValueAtTime(o.freqFrom, t)
  if (o.freqTo !== undefined) f.frequency.linearRampToValueAtTime(o.freqTo, t + o.dur)

  const g = e.ctx.createGain()
  g.gain.setValueAtTime(0.0001, t)
  g.gain.linearRampToValueAtTime(o.peak, t + o.attack)
  g.gain.exponentialRampToValueAtTime(0.0001, t + o.attack + o.release)

  if (o.flutterHz) {
    const lfo = e.ctx.createOscillator()
    lfo.type = 'sine'
    lfo.frequency.value = o.flutterHz
    const lg = e.ctx.createGain()
    lg.gain.value = (o.flutterDepth ?? 0.25) * o.peak
    lfo.connect(lg).connect(g.gain)
    lfo.start(t); lfo.stop(t + o.dur + 0.05)
  }

  src.connect(f).connect(g).connect(e.comp)
  routeReverb(e, g, o.reverb ?? 0.30)
  src.start(t)
  src.stop(t + o.dur + 0.02)
}

// 囁き声のフォルマント帯 (F1/F2/F3 を狭 bandpass で通す = 有声音でない「息の言葉」)。
//   周波数をわずかに動かして母音が変わるように見せる (人の気配は出るが声にはならない)。
function ghostFormants(e: Engine, t: number, dur: number, scale: number): void {
  ghostBreath(e, t, { dur, peak: 0.090 * scale, attack: 0.10, release: 0.26, freqFrom: 500, freqTo: 430, Q: 9, reverb: 0.36, flutterHz: 6.5, flutterDepth: 0.30, pinkAmt: 0.8 })
  ghostBreath(e, t, { dur, peak: 0.060 * scale, attack: 0.14, release: 0.24, freqFrom: 1200, freqTo: 1500, Q: 10, reverb: 0.42, flutterHz: 5.0, flutterDepth: 0.25, pinkAmt: 0.6 })
  ghostBreath(e, t, { dur, peak: 0.035 * scale, attack: 0.16, release: 0.22, freqFrom: 2400, freqTo: 2280, Q: 12, reverb: 0.48, flutterHz: 8.0, flutterDepth: 0.20, pinkAmt: 0.4 })
}


// 裁定C: 灰の王城=「神の声」が呪いを告げる音 (2026-06-20 redesign v3: 世界観優先)
//   v3: 単なる警告音でなく「舞台の意志が動いた=幕の向こうから誰かが見ている」の体感。
//   構造: 80Hz dread sub + tritone bells (呪いの音程) + sawtooth 沈み + 神の声の幻聴 (高域ゴースト)
function playDanger(e: Engine, t: number): void {
  // 40Hz subsonic: 底に沈む地響き (大スピーカーでズシンと響く)
  envOsc(e, t, 'sine', 40, { amp: 0.50, attack: 0.004, decay: 0.30, reverb: 0.04 })
  // 低い圧 (迫る) — 80Hz から 50Hz へ「沈み込む」恐怖 (ブースト・decay 延長)
  const sub = envOsc(e, t, 'sine', 80, { amp: 0.78, attack: 0.003, decay: 0.28, reverb: 0.08 })
  pitchBend(sub.osc, t, 80, 50, 0.24)
  // thud: 140Hz 「来る」の中低域 body
  envOsc(e, t, 'sine', 140, { amp: 0.45, attack: 0.003, decay: 0.20, reverb: 0.08 })
  // tritone bells (1.41比=悪魔の音程=呪いの音響シグナル)
  fmBell(e, t, { carrierFreq: 380, modFreq: 380 * 1.41, modDepth: 850, modDecay: 0.06, amp: 0.40, attack: 0.002, decay: 0.20, reverb: 0.14 })
  fmBell(e, t + 0.006, { carrierFreq: 538, modFreq: 538 * 1.41, modDepth: 720, modDecay: 0.05, amp: 0.30, attack: 0.002, decay: 0.18, reverb: 0.12 })
  // sawtooth 下降 (不気味な迫り)
  const low = envOsc(e, t, 'sawtooth', 180, { amp: 0.32, attack: 0.004, decay: 0.20, reverb: 0.10 })
  pitchBend(low.osc, t, 180, 100, 0.18)
  // 緊張ノイズ層 (舞台の床/梁が軋む不安定さ)
  envNoise(e, t, 0.10, 0.28, { type: 'bandpass', frequency: 280, Q: 1.4 }, 0.12)
  // 神の声の幻聴 (控えめ)
  const ghost = envOsc(e, t + 0.020, 'sine', 1800, { amp: 0.10, attack: 0.030, decay: 0.18, reverb: 0.40 })
  pitchBend(ghost.osc, t + 0.020, 1800, 2400, 0.15)
}

// スタミナ切れ音 (playStaminaEmpty) は 2026-07-27 に撤去。
//   スタミナ/息切れの概念ごと廃止したため鳴らす契機が消えた。実装は git 履歴にある。

// =====================================================================
// メニュー操作音 (2026-07-25 第3段 / 票 docs/candidates/2026-07-25-menu-ui-sound-ticket.md)
//
// 2026-07-25 作り直し (ユーザー判定「通常のメニューとしてはいい音。 世界観を出してほしい」)。
//   旧版が「よくできたUI音」に留まった原因は2つ:
//     ① 素材が電子的 = ほぼ純粋なサイン波の FM ベルだけで、 物が鳴った摩擦・軋み・空気が無い
//     ② 部屋が無い = 短い reverb にしか送っておらず、 戦闘音が居る大聖堂 (cathedral IR 1.8s)
//        と別の空間で鳴っていた。 UIだけ「ゲームの外」に浮く
//   対策: (a) 立ち上がりを物質のノイズで作る (b) cathedral へ送って戦闘音と同じ石の広間に置く。
//
// ✅ 2026-07-25 確定 = **A 常夜燈** (ユーザー裁定「Aで」)。 冷たいガラスの灯体に触れる家系。
//   決め手: 常夜燈は全画面に居る本作の署名オブジェクトで、 タイトルも「常夜燈の稽古」もこの灯の
//   名を持つ。 「メニュー = 灯に触れる」が一番説明の要らない見立てになる。
//   不採用の B 舞台 (板の軋み・幕・呼び鈴) / C 骨董オルゴール (櫛歯+爪音) は、 実出力の録音
//   `そざい/_inbox/2026-07-25-menu-ui-sound/ui_set_{a,b,c}.mp3` と票 §3-0 に残して実装は畳んだ
//   (確定後に切替機構を残さない = 亡霊 telegraph で A 採用時に候補を撤去したのと同じ作法)。
//     ui_move    = 単発で一番小さい (押した数だけ鳴るため)
//     ui_confirm = 開く向き (息を吸って灯が点る)
//     ui_cancel  = 閉じる向き (灯を伏せる)
//   ⬜ amp/decay は耳判定で調整しうる (catalog の volume は procedural では読まれない
//      = 音量を変える時はここの amp を触る)。
// =====================================================================

function playUiMove(e: Engine, t: number): void { uiLampMove(e, t) }
function playUiConfirm(e: Engine, t: number): void { uiLampConfirm(e, t) }
function playUiCancel(e: Engine, t: number): void { uiLampCancel(e, t) }
function playUiChoiceAppear(e: Engine, t: number): void { uiLampChoiceAppear(e, t) }

// ---- 確定: 常夜燈 (冷たいガラスの灯体) ----
//   カーソル= 硝子の縁に触れる。 決定= 息を吸うように空気が集まって灯が点る。 戻る= 灯を伏せる。
function uiLampMove(e: Engine, t: number): void {
  // 触れた瞬間の摩擦 (これが「物」の側面。 無いと途端に電子音へ戻る)
  uiAir(e, t, { dur: 0.045, amp: 0.020, attack: 0.002, type: 'bandpass', from: 3400, to: 5600, Q: 0.9, reverb: 0.06 })
  uiGlass(e, t + 0.004, { freq: 1180, amp: 0.032, decay: 0.11, reverb: 0.10, cathedral: 0.07 })
}
function uiLampConfirm(e: Engine, t: number): void {
  // ① 空気が吸い寄せられる (逆再生的な swell = 「これから点る」の予告)
  uiAir(e, t, { dur: 0.16, amp: 0.030, attack: 0.125, type: 'bandpass', from: 620, to: 2300, Q: 0.8, reverb: 0.14, cathedral: 0.22 })
  // ② 灯が点る = 硝子が鳴って石の広間へ広がる
  uiGlass(e, t + 0.105, { freq: 1180, amp: 0.044, decay: 0.42, reverb: 0.24, cathedral: 0.34 })
  uiGlass(e, t + 0.150, { freq: 1770, amp: 0.030, decay: 0.60, reverb: 0.28, cathedral: 0.40 })
}
function uiLampCancel(e: Engine, t: number): void {
  // 灯を伏せる = 空気が下へ引かれ、 硝子は短く止められる
  uiAir(e, t, { dur: 0.20, amp: 0.026, attack: 0.012, type: 'bandpass', from: 1900, to: 320, Q: 0.7, reverb: 0.12, cathedral: 0.14 })
  uiGlass(e, t + 0.010, { freq: 790, amp: 0.034, decay: 0.15, reverb: 0.10, cathedral: 0.10 })
  uiWood(e, t + 0.030, { freq: 165, amp: 0.030, decay: 0.13, reverb: 0.08, cathedral: 0.10 })
}

// 選択肢が現れた = 灯が「2つ」並んで点る (問いが立った)。 2026-07-26 追加。
//   ui_confirm と取り違えないための作り分け:
//     ・決定は 1180→1770Hz の上行 1音ずつ。 こちらは **同時に 2つ** 点る (五度 = 問いの並列)
//     ・決定より低く始め、 減衰は長い (「決まった」ではなく「開いた・待っている」)
//     ・空気の swell を決定 (0.125s) より長く取り、 石の広間へ深く送る = 場面が変わった重さ
//   ⚠️ 文章を読まないプレイヤーには音しか届かない (友達=たいちの像・2026-07-26 聞き取り) ので、
//     UI音の中では一番はっきり出す。 ⬜ 実機の耳判定で amp を調整しうる。
function uiLampChoiceAppear(e: Engine, t: number): void {
  // ① 空気が集まる (決定より長い swell = 「これから問いが開く」)
  uiAir(e, t, { dur: 0.30, amp: 0.034, attack: 0.220, type: 'bandpass', from: 420, to: 1600, Q: 0.8, reverb: 0.18, cathedral: 0.30 })
  // ② 灯が2つ、 ほぼ同時に点る (五度で並ぶ = 二択の並列。 上行しないので決定に聞こえない)
  uiGlass(e, t + 0.190, { freq: 880, amp: 0.046, decay: 0.85, reverb: 0.30, cathedral: 0.46 })
  uiGlass(e, t + 0.205, { freq: 1320, amp: 0.038, decay: 0.95, reverb: 0.32, cathedral: 0.48 })
}

// ---- UI 共通部品 ----

// 帯域を動かせる空気/摩擦のノイズ。 envNoise (減衰固定) と違い attack を持てるので、
//   「吸い込んで開く」swell も「引いて閉じる」流れも作れる。
function uiAir(
  e: Engine, t: number,
  o: {
    dur: number; amp: number; attack: number
    type: BiquadFilterType; from: number; to?: number; Q?: number
    reverb?: number; cathedral?: number
  },
): void {
  const sr = e.ctx.sampleRate
  const len = Math.max(1, Math.floor(sr * o.dur))
  const buf = e.ctx.createBuffer(1, len, sr)
  const d = buf.getChannelData(0)
  for (let i = 0; i < len; i += 1) d[i] = Math.random() * 2 - 1
  const src = e.ctx.createBufferSource()
  src.buffer = buf

  const f = e.ctx.createBiquadFilter()
  f.type = o.type
  f.frequency.setValueAtTime(o.from, t)
  if (o.to != null) f.frequency.exponentialRampToValueAtTime(Math.max(20, o.to), t + o.dur)
  if (o.Q != null) f.Q.value = o.Q

  const g = e.ctx.createGain()
  g.gain.setValueAtTime(0.0001, t)
  g.gain.exponentialRampToValueAtTime(Math.max(0.0002, o.amp), t + Math.max(0.002, o.attack))
  g.gain.exponentialRampToValueAtTime(0.0001, t + o.dur)

  src.connect(f).connect(g).connect(e.comp)
  routeReverb(e, g, o.reverb ?? 0)
  routeCathedral(e, g, o.cathedral ?? 0)
  src.start(t)
  src.stop(t + o.dur + 0.02)
}

// 木/板が当たる音。 落ちるピッチ + 短い中域ノイズ = 「硬いが響かない物」。
function uiWood(e: Engine, t: number, o: { freq: number; amp: number; decay: number; reverb?: number; cathedral?: number }): void {
  const k = envOsc(e, t, 'triangle', o.freq, { amp: o.amp, attack: 0.001, decay: o.decay, reverb: o.reverb ?? 0 })
  pitchBend(k.osc, t, o.freq * 1.55, o.freq * 0.88, o.decay * 0.8)
  if (o.cathedral) routeCathedral(e, k.gain, o.cathedral)
  uiAir(e, t, { dur: Math.min(0.03, o.decay), amp: o.amp * 0.45, attack: 0.001, type: 'bandpass', from: 1900, to: 850, Q: 1.1, reverb: o.reverb ?? 0 })
}

// 冷たい硝子。 わずかにずれた第2共鳴を重ねて「作った音」でなく「物が鳴った」揺らぎを出す。
function uiGlass(e: Engine, t: number, o: { freq: number; amp: number; decay: number; reverb: number; cathedral?: number }): void {
  fmBell(e, t, {
    carrierFreq: o.freq, modFreq: o.freq * 3.17, modDepth: o.freq * 0.12, modDecay: 0.02,
    amp: o.amp, attack: 0.001, decay: o.decay, reverb: o.reverb, cathedralAmt: o.cathedral,
  })
  fmBell(e, t + 0.004, {
    carrierFreq: o.freq * 2.02, modFreq: o.freq * 5.1, modDepth: o.freq * 0.06, modDecay: 0.02,
    amp: o.amp * 0.34, attack: 0.002, decay: o.decay * 0.68, reverb: o.reverb * 0.8,
    cathedralAmt: (o.cathedral ?? 0) * 0.6,
  })
}

// =====================================================================
// 低レベルヘルパ
// =====================================================================

type EnvOpts = {
  amp: number
  attack: number
  decay: number
  reverb: number
}
type FilterSpec = { type: BiquadFilterType; frequency: number; Q?: number }

function envOsc(
  e: Engine,
  t: number,
  type: OscillatorType,
  freq: number,
  opts: EnvOpts,
): { osc: OscillatorNode; gain: GainNode } {
  const osc = e.ctx.createOscillator()
  osc.type = type
  osc.frequency.value = freq

  const g = e.ctx.createGain()
  g.gain.setValueAtTime(0, t)
  g.gain.linearRampToValueAtTime(opts.amp, t + opts.attack)
  g.gain.exponentialRampToValueAtTime(0.0001, t + opts.decay)

  osc.connect(g).connect(e.comp)
  routeReverb(e, g, opts.reverb)

  osc.start(t)
  osc.stop(t + opts.decay + 0.05)
  return { osc, gain: g }
}

function envNoise(
  e: Engine,
  t: number,
  dur: number,
  amp: number,
  filter: FilterSpec,
  reverb: number,
): void {
  const sampleRate = e.ctx.sampleRate
  const len = Math.max(1, Math.floor(sampleRate * dur))
  const buf = e.ctx.createBuffer(1, len, sampleRate)
  const data = buf.getChannelData(0)
  for (let i = 0; i < len; i += 1) {
    data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 2)
  }
  const src = e.ctx.createBufferSource()
  src.buffer = buf

  const f = e.ctx.createBiquadFilter()
  f.type = filter.type
  f.frequency.value = filter.frequency
  if (filter.Q != null) f.Q.value = filter.Q

  const g = e.ctx.createGain()
  g.gain.value = amp

  src.connect(f).connect(g).connect(e.comp)
  routeReverb(e, g, reverb)

  src.start(t)
}

function fmBell(
  e: Engine,
  t: number,
  o: {
    carrierFreq: number
    modFreq: number
    modDepth: number
    modDecay: number
    amp: number
    attack: number
    decay: number
    reverb: number
    cathedralAmt?: number
  },
): void {
  const carrier = e.ctx.createOscillator()
  carrier.type = 'sine'
  carrier.frequency.value = o.carrierFreq

  const modulator = e.ctx.createOscillator()
  modulator.type = 'sine'
  modulator.frequency.value = o.modFreq

  const modGain = e.ctx.createGain()
  modGain.gain.setValueAtTime(o.modDepth, t)
  modGain.gain.exponentialRampToValueAtTime(1, t + o.modDecay)

  modulator.connect(modGain)
  modGain.connect(carrier.frequency)

  const amp = e.ctx.createGain()
  amp.gain.setValueAtTime(0, t)
  amp.gain.linearRampToValueAtTime(o.amp, t + o.attack)
  amp.gain.exponentialRampToValueAtTime(0.0001, t + o.decay)

  carrier.connect(amp).connect(e.comp)
  routeReverb(e, amp, o.reverb)
  if (o.cathedralAmt && o.cathedralAmt > 0) routeCathedral(e, amp, o.cathedralAmt)

  carrier.start(t)
  carrier.stop(t + o.decay + 0.05)
  modulator.start(t)
  modulator.stop(t + o.decay + 0.05)
}

function ringMod(
  e: Engine,
  t: number,
  o: {
    freqA: number
    freqB: number
    amp: number
    attack: number
    decay: number
    reverb: number
  },
): void {
  const oscA = e.ctx.createOscillator()
  oscA.type = 'sine'
  oscA.frequency.value = o.freqA

  const oscB = e.ctx.createOscillator()
  oscB.type = 'sine'
  oscB.frequency.value = o.freqB

  const ring = e.ctx.createGain()
  ring.gain.value = 0
  oscB.connect(ring.gain)
  oscA.connect(ring)

  const amp = e.ctx.createGain()
  amp.gain.setValueAtTime(0, t)
  amp.gain.linearRampToValueAtTime(o.amp, t + o.attack)
  amp.gain.exponentialRampToValueAtTime(0.0001, t + o.decay)

  ring.connect(amp).connect(e.comp)
  routeReverb(e, amp, o.reverb)

  oscA.start(t); oscA.stop(t + o.decay + 0.05)
  oscB.start(t); oscB.stop(t + o.decay + 0.05)
}

function routeReverb(e: Engine, source: GainNode, level: number): void {
  if (level <= 0) return
  const send = e.ctx.createGain()
  send.gain.value = level * 0.6
  source.connect(send)
  send.connect(e.reverbSend)
}

function routeCathedral(e: Engine, source: GainNode, level: number): void {
  if (level <= 0) return
  const send = e.ctx.createGain()
  send.gain.value = level * 0.6
  source.connect(send)
  send.connect(e.cathedral)
}

function pitchBend(osc: OscillatorNode, t: number, from: number, to: number, dur: number): void {
  osc.frequency.setValueAtTime(from, t)
  osc.frequency.exponentialRampToValueAtTime(Math.max(1, to), t + dur)
}

// =====================================================================
// サチュレーション (歪み) — 致命の「物理的な重さ」を作る部品 (2026-06-23 #12)
//   背景: 既存の致命音は全てクリーン sine + FMベル = 純粋すぎて「電子音/おもちゃ」に聞こえる。
//   実物の打撃 (太鼓/ティンパニ/ピアノ低弦スラム) は歪み (倍音のガッツ) が重さの正体。
//   tanh ソフトクリップで sine に偶数次・奇数次倍音を足し、低音に「肉」を付ける。
// =====================================================================
const _satCurveCache: Record<number, Float32Array> = {}
function makeSaturationCurve(drive: number): Float32Array {
  const key = Math.round(drive * 10)
  const cached = _satCurveCache[key]
  if (cached) return cached
  const n = 1024
  const curve = new Float32Array(n)
  for (let i = 0; i < n; i += 1) {
    const x = (i / (n - 1)) * 2 - 1
    curve[i] = Math.tanh(drive * x)
  }
  _satCurveCache[key] = curve
  return curve
}

// 歪んだ低音スラム: sine → waveshaper で倍音を足す = 物理的な重さ (ベルに頼らない「ズドン」)。
//   drive 大=ガッツ強 / pitchTo 指定でアタック直後にピッチを落とす (打撃の「ドスッ」)。
function saturatedThump(
  e: Engine,
  t: number,
  freq: number,
  opts: { amp: number; attack: number; decay: number; drive: number; pitchTo?: number; reverb?: number },
): void {
  const osc = e.ctx.createOscillator()
  osc.type = 'sine'
  osc.frequency.value = freq
  if (opts.pitchTo != null) pitchBend(osc, t, freq, opts.pitchTo, opts.decay * 0.55)

  const shaper = e.ctx.createWaveShaper()
  shaper.curve = makeSaturationCurve(opts.drive)
  shaper.oversample = '4x'

  const g = e.ctx.createGain()
  g.gain.setValueAtTime(0, t)
  g.gain.linearRampToValueAtTime(opts.amp, t + opts.attack)
  g.gain.exponentialRampToValueAtTime(0.0001, t + opts.decay)

  osc.connect(shaper).connect(g).connect(e.comp)
  routeReverb(e, g, opts.reverb ?? 0)

  osc.start(t)
  osc.stop(t + opts.decay + 0.05)
}

function makeImpulseResponse(ctx: AudioContext, durSec: number, decay: number): AudioBuffer {
  const sampleRate = ctx.sampleRate
  const len = Math.floor(sampleRate * durSec)
  const buf = ctx.createBuffer(2, len, sampleRate)
  for (let ch = 0; ch < 2; ch += 1) {
    const data = buf.getChannelData(ch)
    for (let i = 0; i < len; i += 1) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay)
    }
  }
  return buf
}
